import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CheckCircle,
  List,
  Table,
  Table2,
  Download,
  Edit2,
  X,
  RefreshCw,
  Sparkles,
  Scissors,
  Copy,
  Clipboard,
  Plus,
  Trash2,
  ArrowUp,
  ArrowLeft,
  Link,
  Undo2,
  ArrowUpDown,
  Calculator,
  Eraser,
  CaseSensitive,
  Send,
  Check,
  Settings,
  Hash,
  Type,
  Ban,
  Search,
  Replace,
  FileText,
  FileJson,
  Grid3x3,
  GitCompare,
} from "lucide-react";
import { renderHighlightedText } from "../utils/highlightDiff";
import { generateCharDiffHtml } from "../utils/charDiff";
import { getSourceNickname } from "../utils/modelNickname";
import VehicleMasterManagement from "./VehicleMasterManagement";
import AIChatPanel from "./AIChatPanel";
import CrossValidationModal from "./CrossValidationModal";
import * as XLSX from "xlsx";
import GridTextEditor from "./GridTextEditor";
import HarnessWorkflowGuide from "./HarnessWorkflowGuide";
import TrainingDataViewer from "./TrainingDataViewer";
import VLMFineTuningPanel from "./VLMFineTuningPanel";
import FieldSourceBadge from "./FieldSourceBadge";
import InvoiceLineEvidence from "./InvoiceLineEvidence";
import * as pdfjsLib from "pdfjs-dist";
import { setupColumnAutoFit, autoFitAllColumns } from "../utils/auiGridAutoFit";
import { setupFillHandle, destroyFillHandle } from "../utils/auiGridFillHandle";
import { isLoginHSProduct } from "../config/product";
import {
  GEMINI_FLASH_MODEL,
  normalizeGeminiGenerationModel,
} from "../utils/geminiModel";

// loginHS 일반 사용자에게 개발용 VLM 학습저장/파인튜닝 버튼 숨김 — config/product.js SSoT
const IS_LOGINHS_PRODUCT = isLoginHSProduct();

// ============================================================
// 📐 계층적 컬럼 너비 설정 시스템 (BatchAUIGrid에서 가져옴)
// ============================================================

// ============================================================
// Phase 1.5b G3: grid camelCase row.key → server snake_case key mapping
// FieldSourceBadge looks up rawValue/dbAugmented from server response
// (_raw, _enrichment.db_augmented), which use snake_case keys
// (e.g., plate_no, owner_id_no). Grid rows use camelCase (plateNumber,
// ownerIdNumber). This map keeps a deterministic mapping with a generic
// camelCase→snake_case fallback for unknown keys.
// ============================================================
const GRID_KEY_TO_SERVER_KEY = {
  // 차량 식별
  plateNumber: "plate_no",
  vehicleRegistrationNumber: "plate_no",
  vin: "vin",
  model: "car_name",
  vehicleName: "car_name",
  modelYear: "year",
  maker: "maker",
  // 차주 정보
  ownerName: "owner_name",
  name: "owner_name",
  ownerIdNumber: "owner_id_no",
  residentNumber: "owner_id_no",
  birthDate: "owner_id_no",
  address: "owner_address",
  baseAddress: "owner_address",
  registeredAddress: "owner_address",
  // 차량 제원
  mileage: "mileage_km",
  displacement: "displacement_cc",
  totalWeight: "vehicle_weight_kg",
  grossWeight: "vehicle_weight_kg",
  length: "length_mm",
  width: "width_mm",
  height: "height_mm",
  cbm: "cbm",
  // 일자/금액
  deregistrationDate: "canceled_date",
  cancellationDate: "canceled_date",
  auctionDate: "auction_date",
  totalAmount: "bid_amount_krw",
  bidAmount: "bid_amount_krw",
  // 매수자 (경락확인서)
  buyerName: "buyer_name",
  buyerBusinessNumber: "buyer_biz_no",
  // 수출자
  exporterName: "exporter_name",
  exporterBizNo: "exporter_biz_no",
};

function gridKeyToServerKey(camelKey) {
  if (!camelKey || typeof camelKey !== "string") return camelKey;
  if (GRID_KEY_TO_SERVER_KEY[camelKey] !== undefined) {
    return GRID_KEY_TO_SERVER_KEY[camelKey];
  }
  // generic fallback: camelCase → snake_case
  return camelKey.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());
}

/**
 * 1단계: 기본 데이터타입 너비 (모든 문서 공통)
 */
const BASE_WIDTH = {
  // 숫자 타입
  numeric: 80, // 일반 숫자
  currency: 95, // 금액
  quantity: 70, // 수량
  percentage: 60, // 퍼센트
  index: 45, // 순번/인덱스
  // 텍스트 타입
  code: 90, // 코드 (Material, HS Code 등)
  shortText: 100, // 짧은 텍스트
  mediumText: 150, // 중간 텍스트
  longText: 200, // 긴 텍스트 (Description 등)
  date: 90, // 날짜
  unit: 55, // 단위 (EA, KG 등)
};

/**
 * 2단계: 문서 유형별 특화 필드 너비
 */
const DOCUMENT_TYPE_WIDTHS = {
  // 🚢 수출입 선적서류 (Invoice, Packing List, B/L, L/C 등) - 95% 사용
  shipping: {
    // 인보이스 필드
    item: 45,
    no: 45,
    seq: 45,
    invoiceno: 100,
    "invoice no": 100,
    "invoice number": 100,
    invoicenumber: 100,
    blno: 100,
    "bl no": 100,
    "b/l no": 100,
    pono: 100,
    "po no": 100,
    "po number": 100,
    "purchase order": 100,
    // 품목 필드 - ⭐ description은 flex로 처리
    description: 0,
    desc: 0,
    품명: 0,
    "goods description": 0,
    "description of goods": 0,
    descriptionofgoods: 0,
    material: 80,
    "material no": 80,
    materialcode: 80,
    productcode: 80,
    "product code": 80,
    hscode: 75,
    "hs code": 75,
    hsn: 75,
    hts: 75,
    tariff: 75,
    origin: 60,
    "country of origin": 80,
    coo: 50,
    composition: 60,
    // 수량/금액 필드
    quantity: 60,
    qty: 60,
    수량: 60,
    unitquantity: 60,
    "unit quantity": 60,
    unit: 50,
    uom: 50,
    "unit of measure": 60,
    unittype: 50,
    "unit type": 50,
    unitprice: 70,
    "unit price": 70,
    단가: 70,
    price: 70,
    amount: 80,
    total: 80,
    금액: 80,
    "total amount": 90,
    currency: 55,
    curr: 50,
    // 중량/용적 필드
    grossweight: 75,
    "gross weight": 75,
    netweight: 75,
    "net weight": 75,
    weight: 70,
    cbm: 60,
    m3: 60,
    volume: 70,
    packages: 60,
    cartons: 60,
    ctns: 55,
    pcs: 50,
    incoterms: 70,
  },

  // 🏢 사업자등록증
  businessRegistration: {
    사업자등록번호: 130,
    사업자번호: 130,
    상호: 150,
    법인명: 150,
    회사명: 150,
    대표자: 100,
    대표자명: 100,
    성명: 80,
    업태: 120,
    종목: 120,
    업종: 100,
    사업장주소: 250,
    주소: 200,
    본점소재지: 250,
    법인등록번호: 130,
    개업일: 90,
    개업연월일: 100,
  },
};

/**
 * 3단계: 필드 키워드 → 데이터타입 매핑 (기본 너비 fallback)
 */
const FIELD_TYPE_PATTERNS = [
  // 인덱스/순번
  { pattern: /^(item|no|seq|순번|번호)$/i, type: "index" },
  // 금액
  { pattern: /(amount|total|price|금액|가액|합계)/i, type: "currency" },
  // 수량
  { pattern: /(quantity|qty|수량|개수)/i, type: "quantity" },
  // 단위
  { pattern: /^(unit|uom|단위)$/i, type: "unit" },
  // 퍼센트
  { pattern: /(rate|percent|%|비율)/i, type: "percentage" },
  // 날짜
  { pattern: /(date|일|일자|작성일|발행일)/i, type: "date" },
  // 코드
  { pattern: /(code|번호|no$|number)/i, type: "code" },
  // 긴 텍스트 (Description 등)
  {
    pattern: /(description|desc|품명|상호|명칭|주소|remarks|비고)/i,
    type: "longText",
  },
  // 중간 텍스트
  { pattern: /(name|업태|종목)/i, type: "mediumText" },
];

/**
 * 컬럼 너비 결정 함수 (계층적 lookup)
 * @param {string} fieldKey - 필드 키
 * @param {string} documentType - 문서 유형 ('shipping' | 'businessRegistration')
 * @returns {number} 컬럼 너비 (px), 0이면 flex 컬럼
 */
const getOptimalColumnWidth = (fieldKey, documentType = "shipping") => {
  const keyLower = fieldKey.toLowerCase().replace(/[\s_-]/g, "");

  // 1단계: 문서 유형별 특화 너비 확인
  const docWidths = DOCUMENT_TYPE_WIDTHS[documentType];
  if (docWidths) {
    // 정확한 키 매치
    if (docWidths[keyLower] !== undefined) {
      return docWidths[keyLower];
    }

    // 부분 매칭 (키워드 포함 검사)
    for (const [configKey, width] of Object.entries(docWidths)) {
      if (keyLower.includes(configKey) || configKey.includes(keyLower)) {
        return width;
      }
    }
  }

  // 2단계: 필드 타입 패턴 매칭 → 기본 너비
  for (const { pattern, type } of FIELD_TYPE_PATTERNS) {
    if (pattern.test(fieldKey)) {
      return BASE_WIDTH[type];
    }
  }

  // 3단계: 기본값 (텍스트 길이 기반)
  return fieldKey.length > 10 ? BASE_WIDTH.mediumText : BASE_WIDTH.shortText;
};

// ============================================================

export default function PageBasedGridPanel({
  documentType,
  flashData,
  proData,
  extractedData, // ⭐ 검증 후 수정된 데이터 (단일 이미지용)
  pageResults,
  allItems: allItemsProp, // ⭐ 모든 페이지의 병합된 items 배열 (테이블 필드용)
  totalPages,
  selectedPageNumber,
  checkedPages = [],
  fileId,
  validatedPages = {},
  onValidatePage,
  onValidateMultiplePages,
  onValidateAllPages,
  isPageValidated,
  onProceed,
  isRightPanelCollapsed,
  onSaveVehicle,

  onFieldHover, // ⭐ 새로운 prop: 필드 호버 이벤트
  onFieldClick, // ⭐ 새로운 prop: 필드 클릭 이벤트
  onPageChange, // 📄 페이지 변경 콜백
  onPageToggle, // 📄 페이지 체크 토글 콜백
  templates = {},
  selectedTemplateId = "", // ⭐ 왼쪽 패널에서 선택한 템플릿 ID
  groundedFields = {}, // ⭐ [Grounding] LangExtract grounded 필드 데이터 { fieldName: { value, bbox, confidence } }
  crossValidation = null, // 🔧 교차 검증 데이터 (2차 모델 결과 포함)
  pdfFile = null, // 📄 PDF 파일 (교차검증 뷰어용)
  currentFilePreview = null, // 🖼️ AI 어시스턴트 이미지 질의용 (현재 파일 미리보기 URL)
  uploadedFilePath = "", // 📤 서버 업로드 파일 경로 (GCS 업로드용)
  onOpenAIPanel, // 🤖 AI 패널 열기 콜백 (App.js 레벨) - deprecated, 인라인 사이드바로 대체
  externalAIToggle, // 🤖 App.js 툴바 AI 버튼으로부터 토글 트리거
  // 탭 통합 props (RightPanelTabs에서 전달)
  activeTab = "results",
  onTabChange,
  hasTabChanges = false,
  // 🆕 PaddleOCR 1차 추출 결과 비교 (OCR 체크박스 활성화 시)
  paddleOcrResult = null,
  ocrEnabled = false,
  // 🆕 Donut 1차 모델 추출 결과 비교 (Donut 체크박스 활성화 시, 성능 비교용)
  donutResult = null,
  donutEnabled = false,
  onDonutChange = null,
}) {
  // 🔍 [DEBUG] Grounding 데이터 확인
  React.useEffect(() => {
    const groundedCount = Object.keys(groundedFields).length;
    const withBbox = Object.entries(groundedFields).filter(
      ([k, v]) => v?.bbox?.coordinates,
    ).length;

    // 🆕 테이블 행 grounding 키 확인
    const tableRowKeys = Object.keys(groundedFields).filter((k) =>
      k.includes("["),
    );

    console.log("🎯 [Grounding Debug] groundedFields:", {
      totalFields: groundedCount,
      fieldsWithBbox: withBbox,
      // 🆕 테이블 행 키 목록 (items[0], items[1] 등)
      tableRowKeys: tableRowKeys,
      hasItems0: !!groundedFields["items[0]"],
      items0Data: groundedFields["items[0]"],
      sampleFields: Object.entries(groundedFields)
        .slice(0, 5)
        .map(([k, v]) => ({
          key: k,
          hasBbox: !!v?.bbox?.coordinates,
          page: v?.bbox?.page,
          coords: v?.bbox?.coordinates,
        })),
    });
  }, [groundedFields]);

  // ⭐ 문서 타입 변경 시 자동으로 뷰 모드 전환 (Invoice 등은 Grid 기본)

  const [viewMode, setViewMode] = useState("form"); // 기본값은 'form', useEffect에서 덮어씌움
  const [editedData, setEditedData] = useState({});
  const [hoveredField, setHoveredField] = useState(null); // ⭐ 호버된 필드
  const [showMismatchOnly, setShowMismatchOnly] = useState(false); // 🔍 불일치만 표시 필터
  const [gridSafetyTrigger, setGridSafetyTrigger] = useState(0); // 🔧 FIX: Grid safety net 강제 재생성 트리거
  const [showCrossValidationModal, setShowCrossValidationModal] =
    useState(false); // 🔧 교차 검증 비교 모달
  const [showInlineCrossValidation, setShowInlineCrossValidation] =
    useState(false); // 🔧 인라인 교차 검증 그리드
  const [inlineCvColumnFilter, setInlineCvColumnFilter] = useState(null); // 🔧 인라인 CV 불일치 열 필터 (null=전체, 'fieldName'=해당 열만)
  const [verifiedCells, setVerifiedCells] = useState({}); // 🔧 확인완료 셀 { "page_idx_field": true }

  // 📄 [Page Navigation] onPageChange 최신 참조 유지 (closure 문제 방지)
  const onPageChangeRef = useRef(onPageChange);
  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  // 🎨 [Page Highlight] 현재 활성 페이지 번호 (그리드 행 하이라이트용)
  const activePageRef = useRef(null);
  const selectedRowRef = useRef(null);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState({
    car_name_kr: "",
    car_name_en: "",
    brand: "",
    model_year: "",
    trade_item_name: "",
    item_name: "",
    displacement: "",
    weight: "",
    cbm: "",
  });
  const [editingMasterId, setEditingMasterId] = useState(null); // 기존 데이터 업데이트용
  const [showCarNameMappingModal, setShowCarNameMappingModal] = useState(false); // 차량명 매핑 팝업
  const [carNameMappingData, setCarNameMappingData] = useState(null); // 차량명 매핑 데이터
  const [similarMatches, setSimilarMatches] = useState([]); // 유사 차량 목록
  const [searchingSimilarity, setSearchingSimilarity] = useState(false); // 유사도 검색 중
  const [showMasterSettingsModal, setShowMasterSettingsModal] = useState(false); // 기초설정 모달
  const [columnFilter, setColumnFilter] = useState("all"); // 🔧 컬럼 필터: 'all' | 'single' | 'table'
  const [showAIPanel, setShowAIPanel] = useState(false); // 🤖 AI 인라인 사이드바
  const [aiPrompt, setAiPrompt] = useState(""); // AI 프롬프트 입력
  const [aiLoading, setAiLoading] = useState(false); // AI 변환 로딩
  const [aiResult, setAiResult] = useState(null); // { transformedData, explanation }
  const [aiError, setAiError] = useState(null); // AI 에러 메시지
  const [aiTargetColumns, setAiTargetColumns] = useState([]); // [{field, headerText}]
  const [aiMessages, setAiMessages] = useState([]); // 대화 이력 [{role, content, transformedData, timestamp}]
  const [aiAttachedImages, setAiAttachedImages] = useState([]); // 🖼️ AI 첨부 이미지 [{id, dataUrl, name}]
  const aiAbortRef = useRef(null); // AbortController for cancel
  const aiChatEndRef = useRef(null); // 채팅 자동 스크롤
  const gridRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]); // 🔄 Undo 스택 (병합/셀편집 되돌리기)
  const [undoToastVisible, setUndoToastVisible] = useState(false); // 되돌리기 토스트 표시
  const undoToastTimerRef = useRef(null);
  const selectedColumnsRef = useRef([]); // 🆕 다중 열 선택 추적
  const lastClickedColumnRef = useRef(-1); // 🆕 마지막 클릭된 열 인덱스
  const retryTimerRef = useRef(null); // 🔧 그리드 DOM 리트라이 타이머
  const resizeObserverRef = useRef(null); // 🔧 그리드 컨테이너 ResizeObserver (높이 0 방지)
  const prevGridSignatureRef = useRef(null); // 🔧 그리드 컬럼 시그니처 (깜박임 방지)
  const gridCreationGenRef = useRef(0); // 🔧 FIX: 그리드 생성 세대 카운터 (stale retry 방지)
  // ⚠️ CRITICAL: gridDomRef를 제거하지 마세요 — React가 #horizontal_grid div를 재생성하면 AUIGrid 인스턴스 분리 감지용
  const gridDomRef = useRef(null);
  const heightObserverForGridRef = useRef(null); // 🔧 FIX: 높이 대기 ResizeObserver (component-level)
  const inlineCvGridRef = useRef(null); // 🔧 인라인 교차검증 그리드 인스턴스
  const inlineCvSyncLockRef = useRef(false); // 🔧 인라인 교차검증 행 동기화 락
  const verifiedCellsRef = useRef({}); // 🔧 확인완료 셀 ref (labelFunction 클로저용)

  // 🔍 찾기/바꾸기 패널 상태
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [frSearch, setFrSearch] = useState("");
  const [frReplace, setFrReplace] = useState("");
  const [frTargetCols, setFrTargetCols] = useState([]); // [{colIndex, field, headerText}]
  const [frMatchCase, setFrMatchCase] = useState(false);
  const [frMatchCount, setFrMatchCount] = useState(null); // 매치 수 표시용

  // 📊 셀 단위 일치율 (백엔드 differences 기반 — 상단 버튼 + 통계 바 통일)
  const cellLevelMatchRate = useMemo(() => {
    if (!crossValidation) return null;
    // 백엔드 matchRate 우선 사용 (compareResults 계산값)
    const pItems = allItemsProp || [];
    if (pItems.length === 0) return crossValidation.matchRate ?? null;
    const skipK = new Set([
      "pageNumber",
      "P",
      "p",
      "page",
      "differences",
      "metadata",
      "documentType",
      "idx",
      "_rowIndex",
      "_hasDiff",
    ]);
    let totalFields = 0,
      mismatchFields = 0;
    pItems.forEach((pItem) => {
      Object.keys(pItem).forEach((k) => {
        if (skipK.has(k) || k.startsWith("_")) return;
        totalFields++;
      });
      const diffs = pItem.differences || {};
      Object.entries(diffs).forEach(([k, d]) => {
        if (skipK.has(k) || k.startsWith("_")) return;
        if (d && d.match === false) mismatchFields++;
      });
    });
    return totalFields > 0
      ? Math.round(((totalFields - mismatchFields) / totalFields) * 100)
      : (crossValidation.matchRate ?? 100);
  }, [crossValidation, allItemsProp]);

  // 🧠 학습 데이터 저장 상태
  const [trainingSaveStatus, setTrainingSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [showTrainingViewer, setShowTrainingViewer] = useState(false); // 학습 데이터 조회 모달
  const [showVLMFineTuning, setShowVLMFineTuning] = useState(false); // VLM 파인튜닝 패널
  const [vlmSaveStatus, setVlmSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  // 🆕 AI HS 자동분류 — invoice line item 별 evidence-based 분류 + cell click modal
  const [isHsClassifying, setIsHsClassifying] = useState(false);
  const [hsClassifyStats, setHsClassifyStats] = useState(null); // { total, success, confidence_distribution }
  const [hsEvidenceModal, setHsEvidenceModal] = useState(null); // { rowIndex, description, composition } | null

  // 📝 텍스트 편집기 상태
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [textEditorContent, setTextEditorContent] = useState("");
  const [textEditorTarget, setTextEditorTarget] = useState({
    field: "",
    headerText: "",
    rowCount: 0,
  });

  // 📊 격자 편집기 상태
  const [showGridEditor, setShowGridEditor] = useState(false);
  const [gridEditorData, setGridEditorData] = useState({
    columnName: "",
    columnField: "",
    values: [],
  });

  // 🔄 Undo 토스트 표시 함수 (3초 후 자동 숨김)
  const showUndoToast = React.useCallback(() => {
    setUndoToastVisible(true);
    if (undoToastTimerRef.current) clearTimeout(undoToastTimerRef.current);
    undoToastTimerRef.current = setTimeout(
      () => setUndoToastVisible(false),
      5000,
    );
  }, []);

  // 🤖 App.js 툴바 AI 버튼 토글 연동 (커스텀 이벤트)
  useEffect(() => {
    const handleToggle = () => {
      setShowAIPanel((prev) => {
        if (!prev) {
          // 열 때: selectedColumnsRef에서 선택된 열 가져오기
          const selCols = selectedColumnsRef.current || [];
          const grid = gridRef.current || "#horizontal_grid";
          if (selCols.length > 0 && window.AUIGrid?.isCreated(grid)) {
            const layout = window.AUIGrid.getColumnLayout(grid);
            const targetCols = selCols
              .map((idx) => ({
                field: layout[idx]?.dataField,
                headerText: layout[idx]?.headerText || layout[idx]?.dataField,
              }))
              .filter((c) => c.field);
            setAiTargetColumns(targetCols);
          } else {
            setAiTargetColumns([]);
          }
          setAiResult(null);
          setAiError(null);
          setAiPrompt("");
          setAiMessages([]);
        }
        return !prev;
      });
    };
    window.addEventListener("toggleAIPanel", handleToggle);
    return () => window.removeEventListener("toggleAIPanel", handleToggle);
  }, []);

  // 🔄 열 선택 변경 → AI 사이드바 동기화 (CustomEvent 리스너)
  useEffect(() => {
    const handleColumnSelectionChanged = (e) => {
      if (!showAIPanel) return; // 패널 닫혀있으면 무시
      const { selectedIndices } = e.detail || {};
      const grid = gridRef.current || "#horizontal_grid";
      if (!window.AUIGrid?.isCreated(grid)) return;
      if (!selectedIndices || selectedIndices.length === 0) {
        setAiTargetColumns([]);
        return;
      }
      const layout = window.AUIGrid.getColumnLayout(grid);
      const targetCols = selectedIndices
        .map((idx) => ({
          field: layout[idx]?.dataField,
          headerText: layout[idx]?.headerText || layout[idx]?.dataField,
        }))
        .filter((c) => c.field);
      setAiTargetColumns(targetCols);
    };
    window.addEventListener(
      "columnSelectionChanged",
      handleColumnSelectionChanged,
    );
    return () =>
      window.removeEventListener(
        "columnSelectionChanged",
        handleColumnSelectionChanged,
      );
  }, [showAIPanel]);

  const performUndo = React.useCallback(() => {
    const grid = gridRef.current;
    if (!grid || !window.AUIGrid?.isCreated(grid)) return;

    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const lastAction = prev[prev.length - 1];
      const newStack = prev.slice(0, -1);

      console.log("↩️ [Undo] 실행:", lastAction.type);

      try {
        if (lastAction.type === "merge_column_left") {
          lastAction.beforeValues.forEach(
            ({ rowIndex, targetValue, sourceValue }) => {
              window.AUIGrid.updateRow(
                grid,
                {
                  [lastAction.targetField]: targetValue,
                  [lastAction.sourceField]: sourceValue,
                },
                rowIndex,
              );
            },
          );
        } else if (
          lastAction.type === "merge_up" ||
          lastAction.type === "merge_left"
        ) {
          lastAction.beforeValues.forEach(({ rowIndex, field, value }) => {
            window.AUIGrid.updateRow(grid, { [field]: value }, rowIndex);
          });
        } else if (lastAction.type === "merge_columns") {
          const currentLayout = window.AUIGrid.getColumnLayout(grid);
          const currentFields = new Set(currentLayout.map((c) => c.dataField));
          const sortedRemovedCols = [...lastAction.removedColumns].sort(
            (a, b) => a.columnIndex - b.columnIndex,
          );
          sortedRemovedCols.forEach((col) => {
            if (!currentFields.has(col.dataField)) {
              window.AUIGrid.addColumn(
                grid,
                {
                  dataField: col.dataField,
                  headerText: col.headerText,
                  width: col.width,
                  editable: true,
                },
                col.columnIndex,
              );
            }
          });
          lastAction.beforeValues.forEach(({ rowIndex, values }) => {
            window.AUIGrid.updateRow(grid, values, rowIndex);
          });
          console.log(
            "↩️ [Undo] 열 병합 되돌리기:",
            sortedRemovedCols.length,
            "개 열",
          );
        } else if (lastAction.type === "split_column") {
          [...lastAction.createdColumns].reverse().forEach((col) => {
            const freshLayout = window.AUIGrid.getColumnLayout(grid);
            const colIdx = freshLayout.findIndex(
              (c) => c.dataField === col.dataField,
            );
            if (colIdx >= 0) {
              window.AUIGrid.removeColumn(grid, colIdx);
            }
          });
          lastAction.beforeValues.forEach(({ rowIndex, originalValue }) => {
            window.AUIGrid.updateRow(
              grid,
              { [lastAction.sourceField]: originalValue },
              rowIndex,
            );
          });
          console.log(
            "↩️ [Undo] 열 분리 되돌리기:",
            lastAction.createdColumns.length,
            "개 열 삭제",
          );
        } else if (
          lastAction.type === "sort_column" ||
          lastAction.type === "delete_empty_rows"
        ) {
          // 정렬/빈행삭제 되돌리기: 전체 데이터 복원
          window.AUIGrid.setGridData(grid, lastAction.beforeData);
          console.log("↩️ [Undo]", lastAction.type, "되돌리기 완료");
        } else if (lastAction.type === "full_snapshot") {
          // 전체 스냅샷 되돌리기: 데이터 + 열 레이아웃 복원
          // 1) 열 레이아웃 복원 (열 추가/삭제 작업용)
          if (lastAction.beforeLayout) {
            try {
              const currentLayout = window.AUIGrid.getColumnLayout(grid);
              // 현재 열 전부 제거 (역순으로)
              for (let i = currentLayout.length - 1; i >= 0; i--) {
                window.AUIGrid.removeColumn(grid, i);
              }
              // 스냅샷 열 순서대로 복원
              lastAction.beforeLayout.forEach((col, idx) => {
                window.AUIGrid.addColumn(
                  grid,
                  {
                    dataField: col.dataField,
                    headerText: col.headerText,
                    width: col.width || 100,
                    editable: col.editable !== false,
                  },
                  idx,
                );
              });
            } catch (layoutErr) {
              console.warn(
                "↩️ [Undo] 열 레이아웃 복원 실패 (데이터는 복원 진행):",
                layoutErr,
              );
            }
          }
          // 2) 데이터 복원 (항상 실행)
          window.AUIGrid.setGridData(grid, lastAction.beforeData);
          console.log(
            "↩️ [Undo] full_snapshot 되돌리기:",
            lastAction.label || "",
          );
        } else if (lastAction.type === "cell_update") {
          lastAction.beforeValues.forEach(({ rowIndex, field, value }) => {
            window.AUIGrid.updateRow(grid, { [field]: value }, rowIndex);
          });
        } else if (lastAction.type === "fill_handle") {
          // 채우기 핸들 되돌리기: 각 셀을 원래 값으로 복원
          lastAction.entries.forEach(({ rowIndex, dataField, oldValue }) => {
            window.AUIGrid.setCellValue(
              grid,
              rowIndex,
              dataField,
              oldValue !== undefined ? oldValue : "",
            );
          });
          try {
            window.AUIGrid.refresh(grid);
          } catch (e) {}
          console.log(
            `↩️ [Undo] fill_handle 되돌리기: ${lastAction.entries.length}개 셀 복원`,
          );
        }
      } catch (err) {
        console.error("↩️ [Undo] 오류:", err);
      }

      // 토스트 숨김
      setUndoToastVisible(false);
      return newStack;
    });
  }, []);

  // 🆕 Ctrl+Z 전용 useEffect (capture phase로 AUIGrid보다 먼저 잡음)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        e.stopPropagation(); // AUIGrid가 이벤트 가로채지 못하도록
        performUndo();
      }
    };
    // capture: true → 이벤트 캡처 단계에서 처리 (AUIGrid 핸들러보다 먼저 실행)
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [performUndo]);

  // 🆕 헤더 컨텍스트 메뉴 오버레이 상태
  const [headerMenuVisible, setHeaderMenuVisible] = useState(false);
  const [headerMenuPosition, setHeaderMenuPosition] = useState({ x: 0, y: 0 });
  const [headerMenuTarget, setHeaderMenuTarget] = useState({
    columnIndex: -1,
    dataField: "",
    headerText: "",
  });

  // 🆕 그리드 메뉴 액션 처리 함수 (Benchmarking 대응) - 컴포넌트 스코프로 이동
  const handleGridMenuAction = React.useCallback(
    (actionId, targetData = {}) => {
      const grid = gridRef.current;
      if (!grid) return;

      const { rowIndex, columnIndex, dataField, selectedIndices } = targetData;
      console.log(`🚀 [GridMenu] Action: ${actionId}`, targetData);

      // 🆕 스냅샷 헬퍼: 데이터 + 열 레이아웃 저장
      const takeSnapshot = (label) => ({
        type: "full_snapshot",
        label,
        beforeData: window.AUIGrid.getGridData(grid).map((r) => ({ ...r })),
        beforeLayout: window.AUIGrid.getColumnLayout(grid).map((c) => ({
          ...c,
        })),
      });

      // 📐 자동 정렬: 실제 데이터 값을 검사하여 숫자→우측, 문자→좌측 정렬
      const autoAlignColumns = () => {
        try {
          const layout = window.AUIGrid.getColumnLayout(grid);
          const data = window.AUIGrid.getGridData(grid);
          if (!layout || !data || data.length === 0) return;

          // 숫자 판별 정규식: 쉼표 포함 숫자, 소수점, 음수
          const numRegex = /^-?[\d,]+\.?\d*$/;
          const skipFields = new Set(["No", "P", "rowNum", "pageNumber"]);

          let cssRules = "";
          layout.forEach((col, colIdx) => {
            if (!col.dataField || skipFields.has(col.dataField)) return;

            // 비어있지 않은 셀의 값 샘플링 (최대 20개)
            let numCount = 0,
              totalCount = 0;
            for (let i = 0; i < Math.min(data.length, 20); i++) {
              const val = String(data[i]?.[col.dataField] || "").trim();
              if (val === "") continue;
              totalCount++;
              if (numRegex.test(val.replace(/\s/g, ""))) numCount++;
            }

            // 50% 이상이 숫자면 우측 정렬
            const isNumeric = totalCount > 0 && numCount / totalCount >= 0.5;
            const align = isNumeric ? "right" : "left";

            // AUIGrid 셀 CSS: 열 인덱스 기반 (nth-child)
            cssRules += `
            .aui-grid .aui-grid-default-column[data-column-index="${colIdx}"] {
              text-align: ${align} !important;
            }
          `;
          });

          // 기존 스타일 제거 후 새로 주입
          let styleEl = document.getElementById("auto-align-style");
          if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.id = "auto-align-style";
            document.head.appendChild(styleEl);
          }
          styleEl.textContent = cssRules;
          console.log("📐 [AutoAlign] 열 정렬 갱신 완료");
        } catch (err) {
          console.warn("📐 [AutoAlign] 실패:", err);
        }
      };

      try {
        switch (actionId) {
          // ============ 열 관련 메뉴 ============
          case "mergeColumns": {
            // 🔗 TEXTJOIN 스타일 열 병합 (구분자 입력, 기본값: 공백)
            const selCols = selectedColumnsRef.current;
            if (!selCols || selCols.length < 2) {
              alert(
                "병합할 열을 2개 이상 선택하세요.\n(헤더를 클릭하여 열을 선택합니다)",
              );
              return;
            }

            const columnLayout = window.AUIGrid.getColumnLayout(grid);
            // 선택된 열을 왼쪽→오른쪽 순서로 정렬
            const sortedCols = [...selCols].sort((a, b) => a - b);
            const colInfos = sortedCols
              .map((idx) => ({
                index: idx,
                dataField: columnLayout[idx]?.dataField,
                headerText:
                  columnLayout[idx]?.headerText || columnLayout[idx]?.dataField,
                width: columnLayout[idx]?.width || 100,
              }))
              .filter((c) => c.dataField);

            if (colInfos.length < 2) return;

            // 구분자  입력 (TEXTJOIN 스타일)
            const colNames = colInfos.map((c) => c.headerText).join(" + ");
            const separator = prompt(
              `[${colNames}] 병합\n\n` +
                `구분자를 입력하세요:\n` +
                `• 공백 1개 (기본값) → "ABC DEF"\n` +
                `• 비워두기 → "ABCDEF"\n` +
                `• 쉼표 등 자유 입력 → "ABC, DEF"`,
              " ",
            );
            if (separator === null) return; // 취소

            const targetField = colInfos[0].dataField;
            const sourceInfos = colInfos.slice(1); // 삭제될 열들
            const gridData = window.AUIGrid.getGridData(grid);

            // ⚡ Undo 스냅샷: 병합 전 각 행의 원래 값 저장 (병합 전에 반드시!)
            const beforeValues = gridData.map((row, idx) => {
              const values = {};
              colInfos.forEach((c) => {
                values[c.dataField] = row[c.dataField] || "";
              });
              return { rowIndex: idx, values };
            });

            // 병합 실행: 각 행에서 선택된 열 값을 구분자로 결합 (빈 값 스킵)
            gridData.forEach((row, idx) => {
              const parts = colInfos
                .map((c) => String(row[c.dataField] || "").trim())
                .filter((v) => v !== "");
              const mergedValue = parts.join(separator);
              const updateObj = { [targetField]: mergedValue };
              // 삭제할 열들은 비우기 (removeColumn 전에)
              sourceInfos.forEach((c) => {
                updateObj[c.dataField] = "";
              });
              window.AUIGrid.updateRow(grid, updateObj, idx);
            });

            // 삭제될 열 레이아웃 정보 보존 (Undo 시 복원용)
            const removedColumns = sourceInfos.map((c) => ({
              dataField: c.dataField,
              headerText: c.headerText,
              width: c.width,
              columnIndex: c.index,
            }));

            // 열 삭제 (뒤에서부터 삭제해야 인덱스 안 밀림)
            [...sourceInfos].reverse().forEach((c) => {
              window.AUIGrid.removeColumn(grid, c.index);
            });

            // Undo 스택 저장 + 토스트 표시
            setUndoStack((prev) => [
              ...prev,
              {
                id: Date.now(),
                type: "merge_columns",
                targetField,
                removedColumns,
                separator,
                beforeValues,
              },
            ]);
            showUndoToast();

            // 열 선택 상태 초기화
            selectedColumnsRef.current = [];
            lastClickedColumnRef.current = -1;
            // style 태그 제거
            const existingStyle = document.getElementById(
              "column-selection-style",
            );
            if (existingStyle) existingStyle.remove();
            document
              .querySelectorAll(".selected-column-header")
              .forEach((el) => el.classList.remove("selected-column-header"));

            console.log(
              `✅ [GridMenu] 열 병합 완료: ${colNames} → ${targetField} (구분자: "${separator}", ${gridData.length}행)`,
            );
            break;
          }

          case "mergeColumnsPrompt": {
            // 🤖 AI 프롬프트로 열 변환 — 인라인 사이드바 열기
            const selCols = selectedColumnsRef.current;
            if (!selCols || selCols.length < 1) {
              alert("변환할 열을 1개 이상 선택하세요.");
              return;
            }
            const layout = window.AUIGrid.getColumnLayout(grid);
            const targetCols = selCols
              .map((idx) => ({
                field: layout[idx]?.dataField,
                headerText: layout[idx]?.headerText || layout[idx]?.dataField,
              }))
              .filter((c) => c.field);
            setAiTargetColumns(targetCols);
            setAiResult(null);
            setAiError(null);
            setAiPrompt("");
            setShowAIPanel(true);
            // 툴바 버튼 상태 동기화
            window.dispatchEvent(
              new CustomEvent("aiPanelStateChange", { detail: { open: true } }),
            );
            break;
          }

          case "splitColumn": {
            // ✂️ 열 분리 (Text to Columns) — 병합의 반대
            const splitColIdx =
              columnIndex >= 0
                ? columnIndex
                : (selectedColumnsRef.current?.[0] ?? -1);
            if (splitColIdx < 0) {
              alert("분리할 열을 선택하세요.");
              return;
            }

            const columnLayout = window.AUIGrid.getColumnLayout(grid);
            const sourceCol = columnLayout[splitColIdx];
            if (!sourceCol?.dataField) return;

            const sourceField = sourceCol.dataField;
            const sourceHeader = sourceCol.headerText || sourceField;

            // 구분자 + 최대 분리 수 입력
            const delimiter = prompt(
              `[${sourceHeader}] 열 분리\n\n` +
                `구분자를 입력하세요:\n` +
                `• 공백 (기본값)\n` +
                `• 쉼표, 탭, 기타 자유 입력`,
              " ",
            );
            if (delimiter === null) return;

            const maxSplitInput = prompt(
              `최대 분리 수를 입력하세요:\n` +
                `• 2 (기본값) → 원본열이 2개 열로 분리\n` +
                `• 3 → 3개 열로 분리\n` +
                `• 첫 구분자 기준으로만 분리하려면 2`,
              "2",
            );
            if (maxSplitInput === null) return;
            const maxParts = Math.max(parseInt(maxSplitInput) || 2, 2);

            const gridData = window.AUIGrid.getGridData(grid);

            // 1. 모든 행을 분리 (최대 maxParts개로 제한)
            const allParts = gridData.map((row) => {
              const val = String(row[sourceField] || "").trim();
              if (!val) return Array(maxParts).fill("");
              const parts = val.split(delimiter);
              // maxParts개로 제한: 앞부분은 개별 분리, 나머지는 마지막에 합침
              if (parts.length > maxParts) {
                const limited = parts.slice(0, maxParts - 1);
                limited.push(parts.slice(maxParts - 1).join(delimiter));
                return limited.map((s) => s.trim());
              }
              return parts.map((s) => s.trim());
            });

            // Undo 스냅샷: 원래 값 저장
            const beforeValues = gridData.map((row, idx) => ({
              rowIndex: idx,
              originalValue: row[sourceField] || "",
            }));

            // 2. 새 열 생성 (sourceHeader_1, sourceHeader_2, ...)
            const createdColumns = [];
            for (let i = 0; i < maxParts; i++) {
              const newField = `${sourceField}_split_${i + 1}`;
              const newHeader = `${sourceHeader}_${i + 1}`;
              const insertIdx = splitColIdx + 1 + i;
              window.AUIGrid.addColumn(
                grid,
                {
                  dataField: newField,
                  headerText: newHeader,
                  width: sourceCol.width || 100,
                  editable: true,
                },
                insertIdx,
              );
              createdColumns.push({
                dataField: newField,
                headerText: newHeader,
                columnIndex: insertIdx,
              });
            }

            // 3. 데이터 분배
            gridData.forEach((row, idx) => {
              const parts = allParts[idx];
              const updateObj = { [sourceField]: "" }; // 원본 열은 비우기
              createdColumns.forEach((col, i) => {
                updateObj[col.dataField] = parts[i] || "";
              });
              window.AUIGrid.updateRow(grid, updateObj, idx);
            });

            // Undo 스택 저장 + 토스트 표시
            setUndoStack((prev) => [
              ...prev,
              {
                id: Date.now(),
                type: "split_column",
                sourceField,
                createdColumns,
                beforeValues,
              },
            ]);
            showUndoToast();

            // 열 선택 초기화
            selectedColumnsRef.current = [];
            lastClickedColumnRef.current = -1;
            const existingStyle = document.getElementById(
              "column-selection-style",
            );
            if (existingStyle) existingStyle.remove();
            document
              .querySelectorAll(".selected-column-header")
              .forEach((el) => el.classList.remove("selected-column-header"));

            console.log(
              `✅ [GridMenu] 열 분리 완료: ${sourceHeader} → ${maxParts}개 열 (구분자: "${delimiter}")`,
            );
            break;
          }

          case "trimColumn": {
            // ✨ Trim — Excel TRIM과 동일: 앞뒤 공백 제거 + 내부 연속 공백을 1개로
            const trimCols =
              selectedColumnsRef.current?.length > 0
                ? selectedColumnsRef.current
                : columnIndex >= 0
                  ? [columnIndex]
                  : [];
            if (trimCols.length === 0) {
              alert("Trim할 열을 선택하세요.");
              return;
            }

            const colLayout = window.AUIGrid.getColumnLayout(grid);
            const gridData = window.AUIGrid.getGridData(grid);
            const beforeVals = [];
            const changeLog = []; // 변경 내역 미리보기용
            let trimCount = 0;
            const processedHeaders = [];

            const truncate = (s, len = 30) =>
              s.length > len ? s.slice(0, len) + "…" : s;

            trimCols.forEach((ci) => {
              const field = colLayout[ci]?.dataField;
              const header = colLayout[ci]?.headerText || field;
              if (!field) return;
              processedHeaders.push(header);
              gridData.forEach((row, ri) => {
                const original = String(row[field] || "");
                // Excel TRIM: 앞뒤 공백 제거 + 내부 연속 공백 → 단일 공백
                const trimmed = original.trim().replace(/\s+/g, " ");
                if (original !== trimmed) {
                  beforeVals.push({ rowIndex: ri, field, value: original });
                  changeLog.push({
                    row: ri + 1,
                    before: original,
                    after: trimmed,
                  });
                  window.AUIGrid.updateRow(grid, { [field]: trimmed }, ri);
                  trimCount++;
                }
              });
            });

            if (trimCount > 0) {
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "cell_update",
                  beforeValues: beforeVals,
                },
              ]);
              showUndoToast();

              // 미리보기: 5개 이하 → 전부, 5개 초과 → 처음 3개 + "...외 N개"
              const maxPreview = trimCount <= 5 ? trimCount : 3;
              const preview = changeLog
                .slice(0, maxPreview)
                .map(
                  (c) =>
                    `  • 행 ${c.row}: "${truncate(c.before)}" → "${truncate(c.after)}"`,
                )
                .join("\n");
              const suffix =
                trimCount > 5 ? `\n  ...외 ${trimCount - 3}개` : "";

              alert(
                `✅ ${trimCount}개 셀의 공백이 제거되었습니다.\n\n` +
                  `대상 열: ${processedHeaders.join(", ")}\n\n` +
                  `${preview}${suffix}\n\n` +
                  `Ctrl+Z로 되돌릴 수 있습니다.`,
              );
            } else {
              alert(
                `공백이 있는 셀이 없습니다.\n\n대상 열: ${processedHeaders.join(", ")}\n→ 이미 깨끗합니다 ✨`,
              );
            }
            console.log(`✅ [GridMenu] Trim 완료: ${trimCount}개 셀 수정`);
            break;
          }

          case "uppercaseColumn": {
            // 🔠 UPPERCASE — 선택 열의 영문을 대문자로 변환
            const ucCols =
              selectedColumnsRef.current?.length > 0
                ? selectedColumnsRef.current
                : columnIndex >= 0
                  ? [columnIndex]
                  : [];
            if (ucCols.length === 0) {
              alert("대문자 변환할 열을 선택하세요.");
              return;
            }

            const ucLayout = window.AUIGrid.getColumnLayout(grid);
            const ucData = window.AUIGrid.getGridData(grid);
            const ucBeforeVals = [];
            const ucChangeLog = [];
            let ucCount = 0;
            const ucHeaders = [];
            const ucTruncate = (s, len = 30) =>
              s.length > len ? s.slice(0, len) + "…" : s;

            ucCols.forEach((ci) => {
              const field = ucLayout[ci]?.dataField;
              const header = ucLayout[ci]?.headerText || field;
              if (!field) return;
              ucHeaders.push(header);
              ucData.forEach((row, ri) => {
                const original = String(row[field] || "");
                const uppercased = original.toUpperCase();
                if (original !== uppercased) {
                  ucBeforeVals.push({ rowIndex: ri, field, value: original });
                  ucChangeLog.push({
                    row: ri + 1,
                    before: original,
                    after: uppercased,
                  });
                  window.AUIGrid.updateRow(grid, { [field]: uppercased }, ri);
                  ucCount++;
                }
              });
            });

            if (ucCount > 0) {
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "cell_update",
                  beforeValues: ucBeforeVals,
                },
              ]);
              showUndoToast();

              const maxP = ucCount <= 5 ? ucCount : 3;
              const preview = ucChangeLog
                .slice(0, maxP)
                .map(
                  (c) =>
                    `  • 행 ${c.row}: "${ucTruncate(c.before)}" → "${ucTruncate(c.after)}"`,
                )
                .join("\n");
              const sfx = ucCount > 5 ? `\n  ...외 ${ucCount - 3}개` : "";

              alert(
                `✅ ${ucCount}개 셀이 대문자로 변환되었습니다.\n\n` +
                  `대상 열: ${ucHeaders.join(", ")}\n\n` +
                  `${preview}${sfx}\n\n` +
                  `Ctrl+Z로 되돌릴 수 있습니다.`,
              );
            } else {
              alert(
                `변환할 소문자가 없습니다.\n\n대상 열: ${ucHeaders.join(", ")}\n→ 이미 모두 대문자입니다`,
              );
            }
            console.log(`✅ [GridMenu] UPPERCASE 완료: ${ucCount}개 셀 수정`);
            break;
          }

          case "keepNumbersOnly": {
            // 🔢 숫자만 남기기 — 숫자, 소수점, 마이너스만 유지
            const numCols =
              selectedColumnsRef.current?.length > 0
                ? selectedColumnsRef.current
                : columnIndex >= 0
                  ? [columnIndex]
                  : [];
            if (numCols.length === 0) {
              alert("숫자만 남길 열을 선택하세요.");
              return;
            }

            const numLayout = window.AUIGrid.getColumnLayout(grid);
            const numData = window.AUIGrid.getGridData(grid);
            const numBeforeVals = [];
            const numChangeLog = [];
            let numCount = 0;
            const numHeaders = [];
            const numTrunc = (s, len = 30) =>
              s.length > len ? s.slice(0, len) + "…" : s;

            numCols.forEach((ci) => {
              const field = numLayout[ci]?.dataField;
              const header = numLayout[ci]?.headerText || field;
              if (!field) return;
              numHeaders.push(header);
              numData.forEach((row, ri) => {
                const original = String(row[field] || "");
                if (!original.trim()) return;
                // 숫자, 소수점, 마이너스, 콤마 유지 → 콤마 제거
                const numbersOnly = original
                  .replace(/[^0-9.\-,]/g, "")
                  .replace(/,/g, "");
                if (original !== numbersOnly) {
                  numBeforeVals.push({ rowIndex: ri, field, value: original });
                  numChangeLog.push({
                    row: ri + 1,
                    before: original,
                    after: numbersOnly,
                  });
                  window.AUIGrid.updateRow(grid, { [field]: numbersOnly }, ri);
                  numCount++;
                }
              });
            });

            if (numCount > 0) {
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "cell_update",
                  beforeValues: numBeforeVals,
                },
              ]);
              showUndoToast();
              const maxP = numCount <= 5 ? numCount : 3;
              const preview = numChangeLog
                .slice(0, maxP)
                .map(
                  (c) =>
                    `  • 행 ${c.row}: "${numTrunc(c.before)}" → "${numTrunc(c.after)}"`,
                )
                .join("\n");
              const sfx = numCount > 5 ? `\n  ...외 ${numCount - 3}개` : "";
              alert(
                `✅ ${numCount}개 셀에서 숫자만 남겼습니다.\n\n` +
                  `대상 열: ${numHeaders.join(", ")}\n\n` +
                  `${preview}${sfx}\n\n` +
                  `Ctrl+Z로 되돌릴 수 있습니다.`,
              );
            } else {
              alert(
                `변환할 셀이 없습니다.\n\n대상 열: ${numHeaders.join(", ")}\n→ 이미 숫자만 있습니다`,
              );
            }
            console.log(
              `✅ [GridMenu] 숫자만 남기기 완료: ${numCount}개 셀 수정`,
            );
            break;
          }

          case "keepTextOnly": {
            // 🔤 문자만 남기기 — 한글, 영문, 공백만 유지
            const txtCols =
              selectedColumnsRef.current?.length > 0
                ? selectedColumnsRef.current
                : columnIndex >= 0
                  ? [columnIndex]
                  : [];
            if (txtCols.length === 0) {
              alert("문자만 남길 열을 선택하세요.");
              return;
            }

            const txtLayout = window.AUIGrid.getColumnLayout(grid);
            const txtData = window.AUIGrid.getGridData(grid);
            const txtBeforeVals = [];
            const txtChangeLog = [];
            let txtCount = 0;
            const txtHeaders = [];
            const txtTrunc = (s, len = 30) =>
              s.length > len ? s.slice(0, len) + "…" : s;

            txtCols.forEach((ci) => {
              const field = txtLayout[ci]?.dataField;
              const header = txtLayout[ci]?.headerText || field;
              if (!field) return;
              txtHeaders.push(header);
              txtData.forEach((row, ri) => {
                const original = String(row[field] || "");
                if (!original.trim()) return;
                // 한글, 영문, 공백만 유지
                const textOnly = original
                  .replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ\s]/g, "")
                  .trim()
                  .replace(/\s+/g, " ");
                if (original !== textOnly) {
                  txtBeforeVals.push({ rowIndex: ri, field, value: original });
                  txtChangeLog.push({
                    row: ri + 1,
                    before: original,
                    after: textOnly,
                  });
                  window.AUIGrid.updateRow(grid, { [field]: textOnly }, ri);
                  txtCount++;
                }
              });
            });

            if (txtCount > 0) {
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "cell_update",
                  beforeValues: txtBeforeVals,
                },
              ]);
              showUndoToast();
              const maxP = txtCount <= 5 ? txtCount : 3;
              const preview = txtChangeLog
                .slice(0, maxP)
                .map(
                  (c) =>
                    `  • 행 ${c.row}: "${txtTrunc(c.before)}" → "${txtTrunc(c.after)}"`,
                )
                .join("\n");
              const sfx = txtCount > 5 ? `\n  ...외 ${txtCount - 3}개` : "";
              alert(
                `✅ ${txtCount}개 셀에서 문자만 남겼습니다.\n\n` +
                  `대상 열: ${txtHeaders.join(", ")}\n\n` +
                  `${preview}${sfx}\n\n` +
                  `Ctrl+Z로 되돌릴 수 있습니다.`,
              );
            } else {
              alert(
                `변환할 셀이 없습니다.\n\n대상 열: ${txtHeaders.join(", ")}\n→ 이미 문자만 있습니다`,
              );
            }
            console.log(
              `✅ [GridMenu] 문자만 남기기 완료: ${txtCount}개 셀 수정`,
            );
            break;
          }

          case "removeSpecialChars": {
            // 🧹 특수문자 제거 — 문자, 숫자, 공백, 소수점만 유지
            const scCols =
              selectedColumnsRef.current?.length > 0
                ? selectedColumnsRef.current
                : columnIndex >= 0
                  ? [columnIndex]
                  : [];
            if (scCols.length === 0) {
              alert("특수문자를 제거할 열을 선택하세요.");
              return;
            }

            const scLayout = window.AUIGrid.getColumnLayout(grid);
            const scData = window.AUIGrid.getGridData(grid);
            const scBeforeVals = [];
            const scChangeLog = [];
            let scCount = 0;
            const scHeaders = [];
            const scTrunc = (s, len = 30) =>
              s.length > len ? s.slice(0, len) + "…" : s;

            scCols.forEach((ci) => {
              const field = scLayout[ci]?.dataField;
              const header = scLayout[ci]?.headerText || field;
              if (!field) return;
              scHeaders.push(header);
              scData.forEach((row, ri) => {
                const original = String(row[field] || "");
                if (!original.trim()) return;
                // 한글, 영문, 숫자, 공백, 소수점, 마이너스만 유지
                const cleaned = original
                  .replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ0-9\s.\-]/g, "")
                  .trim()
                  .replace(/\s+/g, " ");
                if (original !== cleaned) {
                  scBeforeVals.push({ rowIndex: ri, field, value: original });
                  scChangeLog.push({
                    row: ri + 1,
                    before: original,
                    after: cleaned,
                  });
                  window.AUIGrid.updateRow(grid, { [field]: cleaned }, ri);
                  scCount++;
                }
              });
            });

            if (scCount > 0) {
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "cell_update",
                  beforeValues: scBeforeVals,
                },
              ]);
              showUndoToast();
              const maxP = scCount <= 5 ? scCount : 3;
              const preview = scChangeLog
                .slice(0, maxP)
                .map(
                  (c) =>
                    `  • 행 ${c.row}: "${scTrunc(c.before)}" → "${scTrunc(c.after)}"`,
                )
                .join("\n");
              const sfx = scCount > 5 ? `\n  ...외 ${scCount - 3}개` : "";
              alert(
                `✅ ${scCount}개 셀에서 특수문자가 제거되었습니다.\n\n` +
                  `대상 열: ${scHeaders.join(", ")}\n\n` +
                  `${preview}${sfx}\n\n` +
                  `Ctrl+Z로 되돌릴 수 있습니다.`,
              );
            } else {
              alert(
                `특수문자가 있는 셀이 없습니다.\n\n대상 열: ${scHeaders.join(", ")}\n→ 이미 깨끗합니다 ✨`,
              );
            }
            console.log(
              `✅ [GridMenu] 특수문자 제거 완료: ${scCount}개 셀 수정`,
            );
            break;
          }

          case "gridViewEditor": {
            // 📊 격자 편집기 — 선택 열을 구분자로 분리하여 격자 표시
            const gvColIdx =
              columnIndex >= 0
                ? columnIndex
                : (selectedColumnsRef.current?.[0] ?? -1);
            if (gvColIdx < 0) {
              alert("분리할 열을 선택하세요.");
              return;
            }

            const gvLayout = window.AUIGrid.getColumnLayout(grid);
            const gvData = window.AUIGrid.getGridData(grid);
            const gvField = gvLayout[gvColIdx]?.dataField;
            const gvHeader = gvLayout[gvColIdx]?.headerText || gvField;
            if (!gvField) return;

            const gvValues = gvData.map((row) => String(row[gvField] || ""));
            setGridEditorData({
              columnName: gvHeader,
              columnField: gvField,
              values: gvValues,
            });
            setShowGridEditor(true);
            console.log(
              `📊 [GridMenu] 격자 편집기 열기: ${gvHeader} (${gvValues.length}행)`,
            );
            break;
          }

          case "findReplaceInColumn": {
            // 🔍 찾기/바꾸기 — 선택 열 대상
            const frCols =
              selectedColumnsRef.current?.length > 0
                ? selectedColumnsRef.current
                : columnIndex >= 0
                  ? [columnIndex]
                  : [];
            if (frCols.length === 0) {
              alert("찾기/바꾸기할 열을 선택하세요.");
              return;
            }

            const frLayout = window.AUIGrid.getColumnLayout(grid);
            const targets = frCols
              .map((ci) => ({
                colIndex: ci,
                field: frLayout[ci]?.dataField,
                headerText: frLayout[ci]?.headerText || frLayout[ci]?.dataField,
              }))
              .filter((t) => t.field);

            setFrTargetCols(targets);
            setFrSearch("");
            setFrReplace("");
            setFrMatchCount(null);
            setShowFindReplace(true);
            console.log(
              `🔍 [GridMenu] 찾기/바꾸기 열기: ${targets.map((t) => t.headerText).join(", ")}`,
            );
            break;
          }

          case "sortColumn": {
            // 🔼 정렬 — 클릭한 열 기준 오름/내림차순
            const sortColIdx =
              columnIndex >= 0
                ? columnIndex
                : (selectedColumnsRef.current?.[0] ?? -1);
            if (sortColIdx < 0) {
              alert("정렬할 열을 선택하세요.");
              return;
            }

            const sortLayout = window.AUIGrid.getColumnLayout(grid);
            const sortField = sortLayout[sortColIdx]?.dataField;
            const sortHeader = sortLayout[sortColIdx]?.headerText || sortField;
            if (!sortField) return;

            const direction = prompt(
              `[${sortHeader}] 정렬 방향:\n\n` +
                `• 1 = 오름차순 (A→Z, 1→9)\n` +
                `• 2 = 내림차순 (Z→A, 9→1)`,
              "1",
            );
            if (direction === null) return;
            const isAsc = direction !== "2";

            const sortData = window.AUIGrid.getGridData(grid);
            // Undo: 전체 행 순서 스냅샷
            const beforeOrder = sortData.map((row, idx) => ({
              rowIndex: idx,
              row: { ...row },
            }));

            const sorted = [...sortData].sort((a, b) => {
              const va = String(a[sortField] || "").trim();
              const vb = String(b[sortField] || "").trim();
              // 숫자 비교 시도
              const na = parseFloat(va),
                nb = parseFloat(vb);
              if (!isNaN(na) && !isNaN(nb)) return isAsc ? na - nb : nb - na;
              return isAsc ? va.localeCompare(vb) : vb.localeCompare(va);
            });

            window.AUIGrid.setGridData(grid, sorted);

            setUndoStack((prev) => [
              ...prev,
              {
                id: Date.now(),
                type: "sort_column",
                beforeData: sortData,
              },
            ]);
            showUndoToast();
            console.log(
              `✅ [GridMenu] 정렬 완료: ${sortHeader} ${isAsc ? "오름차순" : "내림차순"}`,
            );
            break;
          }

          case "deleteEmptyRows": {
            // 🗑️ 빈 행 삭제 — 모든 셀이 비어있는 행 일괄 제거
            const emptyData = window.AUIGrid.getGridData(grid);
            const emptyLayout = window.AUIGrid.getColumnLayout(grid);
            const dataFields = emptyLayout
              .map((c) => c.dataField)
              .filter(Boolean);

            const emptyRowIndices = [];
            emptyData.forEach((row, idx) => {
              const allEmpty = dataFields.every((f) => {
                const v = String(row[f] || "").trim();
                return v === "" || v === "undefined" || v === "null";
              });
              if (allEmpty) emptyRowIndices.push(idx);
            });

            if (emptyRowIndices.length === 0) {
              alert("빈 행이 없습니다.");
              return;
            }

            const confirmDel = window.confirm(
              `빈 행 ${emptyRowIndices.length}개를 삭제하시겠습니까?`,
            );
            if (!confirmDel) return;

            // Undo: 전체 데이터 스냅샷
            const beforeEmptyData = JSON.parse(JSON.stringify(emptyData));

            // 뒤에서부터 삭제 (인덱스 변동 방지)
            [...emptyRowIndices].reverse().forEach((idx) => {
              window.AUIGrid.removeRow(grid, idx);
            });

            setUndoStack((prev) => [
              ...prev,
              {
                id: Date.now(),
                type: "delete_empty_rows",
                beforeData: beforeEmptyData,
              },
            ]);
            showUndoToast();
            console.log(
              `✅ [GridMenu] 빈 행 삭제 완료: ${emptyRowIndices.length}개 행`,
            );
            break;
          }
          case "addColumnBySelection": {
            const newColName = prompt("새 열 이름을 입력하세요:", "NewColumn");
            if (!newColName) return;
            const newColumn = {
              dataField: newColName.replace(/\s/g, "_"),
              headerText: newColName,
              width: 100,
              editable: true,
            };
            window.AUIGrid.addColumn(grid, newColumn, columnIndex + 1);
            console.log("✅ [GridMenu] 열 추가:", newColName);
            break;
          }

          case "copyColumn": {
            if (!dataField) return;
            const newColName = prompt(
              "복사할 열 이름을 입력하세요:",
              dataField + "_copy",
            );
            if (!newColName) return;

            const gridData = window.AUIGrid.getGridData(grid);
            const newColumn = {
              dataField: newColName.replace(/\s/g, "_"),
              headerText: newColName,
              width: 100,
              editable: true,
            };
            window.AUIGrid.addColumn(grid, newColumn, columnIndex + 1);

            gridData.forEach((row, idx) => {
              window.AUIGrid.updateRow(
                grid,
                { [newColName.replace(/\s/g, "_")]: row[dataField] || "" },
                idx,
              );
            });
            console.log("✅ [GridMenu] 열 복사:", dataField, "->", newColName);
            break;
          }

          case "addColumn": {
            const newColName = prompt("새 열 이름을 입력하세요:", "NewColumn");
            if (!newColName) return;
            const snap_addCol = takeSnapshot("열 추가");
            const newColumn = {
              dataField: newColName.replace(/\s/g, "_"),
              headerText: newColName,
              width: 100,
              editable: true,
            };
            window.AUIGrid.addColumn(grid, newColumn, columnIndex + 1);
            setUndoStack((prev) => [...prev, snap_addCol]);
            showUndoToast();
            console.log("✅ [GridMenu] 열 추가:", newColName);
            break;
          }

          case "deleteColumn": {
            let selectedColIndices = new Set();

            // 1. 헤더 선택 (selectedColumnsRef) 확인
            if (
              selectedColumnsRef.current &&
              selectedColumnsRef.current.length > 0
            ) {
              selectedColumnsRef.current.forEach((idx) =>
                selectedColIndices.add(idx),
              );
            }

            // 2. 셀 선택 (getSelectedItems) 확인 - 헤더 선택이 없을 경우 보완
            const selectedItems = window.AUIGrid.getSelectedItems(grid);
            if (selectedItems && selectedItems.length > 0) {
              selectedItems.forEach((item) =>
                selectedColIndices.add(item.columnIndex),
              );
            }

            // 우클릭한 컬럼이 선택 영역에 포함되어 있으면 다중 삭제, 아니면 해당 컬럼만 삭제
            let colIndicesToDelete = [];
            if (selectedColIndices.has(columnIndex)) {
              colIndicesToDelete = Array.from(selectedColIndices).sort(
                (a, b) => b - a,
              );
            } else {
              colIndicesToDelete = [columnIndex];
            }

            if (colIndicesToDelete.length === 0) {
              alert("삭제할 열을 선택하세요.");
              return;
            }

            const msg =
              colIndicesToDelete.length > 1
                ? `선택된 ${colIndicesToDelete.length}개 열을 삭제하시겠습니까?`
                : `'${dataField || "대상"}' 열을 삭제하시겠습니까?`;

            if (!window.confirm(msg)) return;

            const snap_delCol = takeSnapshot("열 삭제");

            // 인덱스가 큰 순서대로 삭제해야 앞쪽 인덱스가 변하지 않음
            colIndicesToDelete.forEach((idx) => {
              window.AUIGrid.removeColumn(grid, idx);
            });

            setUndoStack((prev) => [...prev, snap_delCol]);
            showUndoToast();
            console.log(
              "✅ [GridMenu] 열 삭제 완료:",
              colIndicesToDelete.length,
              "개",
            );
            break;
          }

          case "addAmountColumn": {
            const qtyField = prompt("수량 열 이름을 입력하세요:", "qty");
            if (!qtyField) return;
            const priceField = prompt("단가 열 이름을 입력하세요:", "uprice");
            if (!priceField) return;

            const snap_amt = takeSnapshot("금액 열 추가");
            const amountColName = "amount_calc";
            const newColumn = {
              dataField: amountColName,
              headerText: "금액(계산)",
              width: 100,
              editable: false,
              style: "text-align: right;",
            };
            window.AUIGrid.addColumn(grid, newColumn);

            const gridData = window.AUIGrid.getGridData(grid);
            gridData.forEach((row, idx) => {
              const qty =
                parseFloat(String(row[qtyField] || "0").replace(/,/g, "")) || 0;
              const price =
                parseFloat(String(row[priceField] || "0").replace(/,/g, "")) ||
                0;
              const amount = qty * price;
              window.AUIGrid.updateRow(
                grid,
                { [amountColName]: amount.toLocaleString() },
                idx,
              );
            });
            setUndoStack((prev) => [...prev, snap_amt]);
            showUndoToast();
            console.log("✅ [GridMenu] 금액 열 추가");
            break;
          }

          case "addUnitPriceColumn": {
            const qtyField = prompt("수량 열 이름을 입력하세요:", "qty");
            if (!qtyField) return;
            const amountField = prompt("금액 열 이름을 입력하세요:", "amount");
            if (!amountField) return;

            const snap_uprc = takeSnapshot("단가 열 추가");
            const unitPriceColName = "uprice_calc";
            const newColumn = {
              dataField: unitPriceColName,
              headerText: "단가(계산)",
              width: 100,
              editable: false,
              style: "text-align: right;",
            };
            window.AUIGrid.addColumn(grid, newColumn);

            const gridData = window.AUIGrid.getGridData(grid);
            gridData.forEach((row, idx) => {
              const qty =
                parseFloat(String(row[qtyField] || "0").replace(/,/g, "")) || 0;
              const amt =
                parseFloat(String(row[amountField] || "0").replace(/,/g, "")) ||
                0;
              const unitPrice = qty > 0 ? amt / qty : 0;
              window.AUIGrid.updateRow(
                grid,
                { [unitPriceColName]: unitPrice.toFixed(2) },
                idx,
              );
            });
            setUndoStack((prev) => [...prev, snap_uprc]);
            showUndoToast();
            console.log("✅ [GridMenu] 단가 열 추가");
            break;
          }

          // ============ 행 관련 메뉴 ============
          case "insertRow": {
            const count = targetData.rowCount || 1;
            const snap_insRow = takeSnapshot("행 삽입");
            for (let i = 0; i < count; i++) {
              window.AUIGrid.addRow(grid, {}, rowIndex);
            }
            setUndoStack((prev) => [...prev, snap_insRow]);
            showUndoToast();
            console.log(`✅ [GridMenu] 행 ${count}개 삽입:`, rowIndex);
            break;
          }

          case "addRow": {
            const snap_addRow = takeSnapshot("행 추가");
            window.AUIGrid.addRow(grid, {}, "last");
            setUndoStack((prev) => [...prev, snap_addRow]);
            showUndoToast();
            console.log("✅ [GridMenu] 행 추가");
            break;
          }

          case "deleteSelectedRows": {
            const rowIndicesToDel =
              selectedIndices && selectedIndices.length > 0
                ? [...selectedIndices].sort((a, b) => b - a)
                : [rowIndex].filter((i) => i >= 0);

            if (rowIndicesToDel.length === 0) {
              alert("삭제할 행을 선택하세요.");
              return;
            }

            const msg =
              rowIndicesToDel.length > 1
                ? `선택된 ${rowIndicesToDel.length}개 항목을 삭제하시겠습니까?`
                : `${rowIndicesToDel[0] + 1}번 행을 삭제하시겠습니까?`;

            if (window.confirm(msg)) {
              const snap_delRows = takeSnapshot("행 삭제");
              rowIndicesToDel.forEach((idx) => {
                window.AUIGrid.removeRow(grid, idx);
              });
              setUndoStack((prev) => [...prev, snap_delRows]);
              showUndoToast();
              console.log(
                "✅ [GridMenu] 행 삭제 완료:",
                rowIndicesToDel.length,
                "개",
              );
            }
            break;
          }

          case "mergeUp": {
            if (rowIndex <= 0) {
              alert("위로 병합할 수 없습니다.");
              return;
            }
            const gridData = window.AUIGrid.getGridData(grid);
            const currentValue = gridData[rowIndex]?.[dataField] || "";
            const aboveValue = gridData[rowIndex - 1]?.[dataField] || "";
            const mergedValue = aboveValue
              ? `${aboveValue}, ${currentValue}`
              : currentValue;

            const snap_mergeUp = takeSnapshot("위로 병합");
            window.AUIGrid.updateRow(
              grid,
              { [dataField]: mergedValue },
              rowIndex - 1,
            );
            window.AUIGrid.updateRow(grid, { [dataField]: "" }, rowIndex);
            setUndoStack((prev) => [...prev, snap_mergeUp]);
            showUndoToast();
            console.log("✅ [GridMenu] 위로 병합:", dataField);
            break;
          }

          case "clearCell": {
            const items = window.AUIGrid.getSelectedItems(grid);
            const snap_clear = takeSnapshot("셀 내용 지우기");
            if (!items || items.length === 0) {
              if (dataField) {
                window.AUIGrid.updateRow(grid, { [dataField]: "" }, rowIndex);
                setUndoStack((prev) => [...prev, snap_clear]);
                showUndoToast();
              }
              return;
            }
            items.forEach((item) => {
              if (item.dataField)
                window.AUIGrid.updateRow(
                  grid,
                  { [item.dataField]: "" },
                  item.rowIndex,
                );
            });
            setUndoStack((prev) => [...prev, snap_clear]);
            showUndoToast();
            console.log("✅ [GridMenu] 셀 삭제:", items.length, "개");
            break;
          }

          case "mergeLeft": {
            const columnLayout = window.AUIGrid.getColumnLayout(grid);
            if (columnIndex <= 0) {
              alert("왼쪽으로 병합할 수 없습니다.");
              return;
            }
            const leftField = columnLayout[columnIndex - 1]?.dataField;
            if (!leftField) return;

            const gridData = window.AUIGrid.getGridData(grid);
            const currentValue = gridData[rowIndex]?.[dataField] || "";
            const leftValue = gridData[rowIndex]?.[leftField] || "";
            const mergedValue = leftValue
              ? `${leftValue}, ${currentValue}`
              : currentValue;

            const snap_mergeLeft = takeSnapshot("왼쪽으로 병합");
            window.AUIGrid.updateRow(
              grid,
              { [leftField]: mergedValue, [dataField]: "" },
              rowIndex,
            );
            setUndoStack((prev) => [...prev, snap_mergeLeft]);
            showUndoToast();
            console.log(
              "✅ [GridMenu] 왼쪽으로 병합:",
              leftField,
              "<-",
              dataField,
            );
            break;
          }

          case "shiftCellsLeft": {
            const gridData = window.AUIGrid.getGridData(grid);
            const columnLayout = window.AUIGrid.getColumnLayout(grid);
            const row = gridData[rowIndex];
            if (!row) return;

            const snap_shiftL = takeSnapshot("셀 왼쪽 밀기");
            const updatedRow = { ...row };
            for (let i = columnIndex; i < columnLayout.length - 1; i++) {
              const currentField = columnLayout[i]?.dataField;
              const nextField = columnLayout[i + 1]?.dataField;
              if (currentField && nextField)
                updatedRow[currentField] = row[nextField] || "";
            }
            const lastField = columnLayout[columnLayout.length - 1]?.dataField;
            if (lastField) updatedRow[lastField] = "";

            window.AUIGrid.updateRow(grid, updatedRow, rowIndex);
            setUndoStack((prev) => [...prev, snap_shiftL]);
            showUndoToast();
            console.log(
              "✅ [GridMenu] 셀을 왼쪽으로 밀기:",
              rowIndex,
              columnIndex,
            );
            break;
          }

          case "shiftCellsUp": {
            const snap_shiftU = takeSnapshot("셀 위로 밀기");
            const gridData = window.AUIGrid.getGridData(grid);
            for (let i = rowIndex; i < gridData.length - 1; i++) {
              const nextValue = gridData[i + 1]?.[dataField] || "";
              window.AUIGrid.updateRow(grid, { [dataField]: nextValue }, i);
            }
            if (gridData.length > 0)
              window.AUIGrid.updateRow(
                grid,
                { [dataField]: "" },
                gridData.length - 1,
              );
            setUndoStack((prev) => [...prev, snap_shiftU]);
            showUndoToast();
            console.log("✅ [GridMenu] 셀을 위로 밀기:", rowIndex, dataField);
            break;
          }

          case "showDeleteDialog": {
            const deleteOption = window.prompt(
              "삭제 옵션을 선택하세요:\n\n" +
                "1: 셀을 왼쪽으로 밀기\n" +
                "2: 셀을 위로 밀기\n" +
                "3: 행 삭제\n" +
                "4: 열 삭제\n" +
                "5: 셀 내용만 지우기",
              "5",
            );
            if (!deleteOption) return;

            switch (deleteOption) {
              case "1":
                handleGridMenuAction("shiftCellsLeft", targetData);
                break;
              case "2":
                handleGridMenuAction("shiftCellsUp", targetData);
                break;
              case "3":
                handleGridMenuAction("deleteSelectedRows", targetData);
                break;
              case "4":
                handleGridMenuAction("deleteColumn", targetData);
                break;
              case "5":
                handleGridMenuAction("clearCell", targetData);
                break;
            }
            break;
          }

          default:
            console.warn("Unknown GridMenu action:", actionId);
            break;
        }
      } catch (err) {
        console.error("❌ [GridMenu] Action Error:", err);
      }

      // 🎨 액션 완료 후 열 선택 하이라이트 제거
      selectedColumnsRef.current = [];
      lastClickedColumnRef.current = -1;
      const existingStyle = document.getElementById("column-selection-style");
      if (existingStyle) existingStyle.remove();
      document
        .querySelectorAll(".selected-column-header")
        .forEach((el) => el.classList.remove("selected-column-header"));

      // 📐 모든 액션 완료 후 자동 정렬 갱신
      autoAlignColumns();
    },
    [],
  );

  // 🆕 그리드 셀 컨텍스트 메뉴 오버레이 상태 (Benchmarking)
  const [gridMenuVisible, setGridMenuVisible] = useState(false);
  const [gridMenuPosition, setGridMenuPosition] = useState({ x: 0, y: 0 });
  const [gridMenuTarget, setGridMenuTarget] = useState({
    rowIndex: -1,
    columnIndex: -1,
    dataField: "",
    rowCount: 1,
    rowRange: "",
    selectedIndices: [],
  });

  // 페이지 수 (pageResults가 있으면 PDF, 없으면 단일 이미지)
  const pageCount = totalPages || 1;
  const isPDF = !!pageResults || (totalPages && totalPages > 1);
  const currentPage = selectedPageNumber || 1; // 선택된 페이지 번호

  // 디버깅 로그 제거 (무한 루프 방지)

  // 현재 페이지의 데이터 가져오기 (useMemo로 메모이제이션)
  const pageData = React.useMemo(() => {
    // 🔍 디버깅: 입력 props 확인
    console.log("🔍 [PageBasedGridPanel] pageData 계산:", {
      isPDF,
      pageResultsLength: pageResults?.length,
      currentPage,
      hasFlashData: !!flashData,
      hasProData: !!proData,
      hasExtractedData: !!extractedData,
      flashDataKeys: flashData ? Object.keys(flashData) : [],
      proDataKeys: proData ? Object.keys(proData) : [],
      extractedDataKeys: extractedData ? Object.keys(extractedData) : [],
      pageResults0: pageResults?.[0]
        ? {
            pageNumber: pageResults[0].pageNumber,
            hasFlashData: !!pageResults[0].flashData,
            hasProData: !!pageResults[0].proData,
            hasExtractedData: !!pageResults[0].extractedData,
            keys: Object.keys(pageResults[0]),
          }
        : null,
    });

    if (isPDF && pageResults && pageResults.length > 0) {
      // 📄 현재 페이지 결과 찾기
      let pageResult = pageResults.find((p) => p.pageNumber === currentPage);

      // 🔧 페이지별 결과가 없으면 첫 번째 결과 사용 (모든 데이터가 page 1에 저장된 경우)
      // 이 경우 테이블 데이터는 P 필드로 필터링됨
      if (!pageResult && pageResults.length > 0) {
        console.log(
          "📄 [PageBasedGridPanel] 현재 페이지 결과 없음, pageResults[0] 사용:",
          currentPage,
        );
        pageResult = pageResults[0];
      }

      // ⭐ 검증 후 수정된 데이터(extractedData)가 있으면 우선 사용
      // extractedData는 검증 시 사용자가 수정한 최종 데이터
      let pageExtractedData = pageResult?.extractedData;

      // 🔧 FIX: extractedData에 중첩된 pageResults가 있으면 해당 페이지 데이터 추출
      if (pageExtractedData?.pageResults?.length > 0) {
        const nestedPageData =
          pageExtractedData.pageResults.find(
            (p) => p.pageNumber === currentPage || p.P === currentPage,
          ) || pageExtractedData.pageResults[0];

        if (nestedPageData && Object.keys(nestedPageData).length > 0) {
          console.log(
            "✅ [PageBasedGridPanel] Using nested pageResults data for page:",
            currentPage,
          );
          pageExtractedData = nestedPageData;
        }
      }

      // 🔧 FIX: extractedData가 비어있으면 pageResult 자체를 사용 (단일 필드 템플릿 호환)
      // 백엔드가 pageResults[].CBM, pageResults[].길 이 형태로 직접 필드를 반환하는 경우
      if (!pageExtractedData || Object.keys(pageExtractedData).length === 0) {
        const metaKeys = [
          "pageNumber",
          "P",
          "p",
          "page",
          "flashData",
          "proData",
          "extractedData",
          "documentType",
          "differences",
          "ocrImageSize",
          "boundingBoxes",
          "correctedImageUrl",
          "pngImage",
        ];
        const dataKeys = Object.keys(pageResult || {}).filter(
          (k) => !metaKeys.includes(k),
        );
        if (dataKeys.length > 0) {
          console.log(
            "📄 [pageData] pageResult 객체 자체를 extractedData로 사용:",
            currentPage,
            "keys:",
            dataKeys.slice(0, 5),
          );
          pageExtractedData = pageResult;
        }
      }

      console.log("🔍 [PageBasedGridPanel] PDF 모드 - pageResults 구조:", {
        totalPageResults: pageResults.length,
        currentPage,
        allPageNumbers: pageResults.map((p) => p.pageNumber),
        usingFallback: !pageResults.find((p) => p.pageNumber === currentPage),
        currentPageResult: pageResult
          ? {
              pageNumber: pageResult.pageNumber,
              hasFlashData: !!pageResult.flashData,
              hasProData: !!pageResult.proData,
              hasExtractedData: !!pageExtractedData,
              flashDataKeys: pageResult.flashData
                ? Object.keys(pageResult.flashData)
                : [],
              proDataKeys: pageResult.proData
                ? Object.keys(pageResult.proData)
                : [],
            }
          : "NOT FOUND",
      });

      console.log("🔍 [PageBasedGridPanel] PDF 모드 - pageResult:", {
        found: !!pageResult,
        pageNumber: pageResult?.pageNumber,
        hasFlashData: !!pageResult?.flashData,
        hasProData: !!pageResult?.proData,
        hasExtractedData: !!pageExtractedData,
      });

      return {
        flashData: pageResult?.flashData,
        proData: pageResult?.proData,
        extractedData: pageExtractedData, // ⭐ 검증 후 수정된 데이터
        documentType:
          pageResult?.documentType || pageExtractedData?.documentType,
        partial: pageResult?.partial, // ⭐ 부분 결과 플래그 추가
        differences: pageResult?.differences, // ⭐ 백엔드에서 계산된 차이점 객체 추가
        _currentPage: currentPage, // 📄 현재 페이지 정보 전달 (테이블 필터링용)
      };
    }
    // 단일 이미지인 경우 extractedData prop 사용 (props에서 가져옴)
    console.log("🔍 [PageBasedGridPanel] 단일 이미지 모드 - props 사용");
    return {
      flashData,
      proData,
      extractedData: extractedData || null, // ⭐ 단일 이미지의 경우 prop에서 가져옴
      documentType,
      partial: false,
    };
  }, [
    isPDF,
    pageResults,
    currentPage,
    flashData,
    proData,
    extractedData,
    documentType,
  ]);

  // ⭐ 문서 타입에 따른 뷰 모드 전환 (matchedTemplate 선언 이후에 별도 useEffect로 처리)

  // 문서 타입 가져오기 - 🔧 editedData.documentType 우선 (드롭다운 선택 반영)
  const documentTypeForLabel =
    editedData.documentType ||
    pageData.extractedData?.documentType ||
    pageData.proData?.documentType ||
    pageData.flashData?.documentType ||
    pageData.documentType;

  // ⭐ 매칭되는 템플릿 찾기 (selectedTemplateId 우선, 없으면 문서타입으로 자동 매칭)
  const matchedTemplate = React.useMemo(() => {
    if (!templates) return null;

    // 1️⃣ 왼쪽 패널에서 명시적으로 선택한 템플릿이 있으면 우선 사용
    if (selectedTemplateId) {
      const selectedTemplate = Object.values(templates).find(
        (t) => t.id === selectedTemplateId,
      );
      if (selectedTemplate) return selectedTemplate;
    }

    // 2️⃣ 선택된 템플릿이 없으면 문서 타입으로 자동 매칭
    const typeLabel =
      documentTypeForLabel?.toUpperCase().replace(/ /g, "_") || "";
    return Object.values(templates).find((t) => {
      const templateName = t.name.toUpperCase().replace(/ /g, "_");
      // 정확 일치 또는 포함 관계 (Invoice <-> Commercial Invoice)
      return (
        templateName === typeLabel ||
        typeLabel.includes(templateName) ||
        templateName.includes(typeLabel)
      );
    });
  }, [templates, documentTypeForLabel, selectedTemplateId]);

  // ⭐ 템플릿 확정 후 뷰 모드 자동 전환
  useEffect(() => {
    // 📄 템플릿에 tableFields가 있으면 horizontal 모드로 전환
    if (matchedTemplate?.tableFields?.length > 0) {
      console.log(
        "✅ [viewMode] Horizontal Grid Mode Auto-Switch (tableFields detected):",
        matchedTemplate.name,
      );
      setViewMode("horizontal");
      return;
    }

    const type = documentType || pageData?.documentType || "";
    if (!type) return;

    // 인보이스, 패킹리스트 류 감지
    const isMultiRow = [
      "INVOICE",
      "PACKING_LIST",
      "COMMERCIAL_INVOICE",
      "B/L",
      "BILL_OF_LADING",
    ].some((t) => type.toUpperCase().replace(/ /g, "_").includes(t));

    if (isMultiRow) {
      console.log("✅ [viewMode] Horizontal Grid Mode Auto-Switch for:", type);
      setViewMode("horizontal");
    }
  }, [matchedTemplate, documentType, pageData?.documentType]);
  // 필드명 한글 매핑 (문서 타입에 따라 다르게 표시)
  // 필드명 한글 매핑 (문서 타입에 따라 다르게 표시)
  const getFieldNameMap = () => {
    const baseMap = {
      documentType: "문서타입",
      plateNumber: "차량등록번호",
      vin: "차대번호",
      model: "차명",
      modelType: "형식",
      name: "이름",
      modelYear: "연식",
      registrationDate: "최초등록일",
      mileage: "주행거리",
      displacement: "배기량",
      deregistrationDate: "말소일자",
      disposalDate: "폐차일자",
      businessName: "상호명",
      businessNumber: "사업자번호",
      issueDate: "발급일자",
      licenseNumber: "면허번호",
      totalWeight: "총중량",
      width: "너비",
      height: "높이",
      length: "길이",
      cbm: "CBM",
      openDate: "개업일자",
      supplierBusinessName: "공급자상호",
      supplierBusinessNumber: "공급자사업자번호",
      supplierOwnerName: "공급자성명",
      buyerBusinessName: "공급받는자상호",
      buyerBusinessNumber: "공급받는자사업자번호",
      taxAmount: "세액",
    };

    // ⭐ 템플릿이 있는 경우 모든 템플릿 필드 추가 (단일, 테이블, 합성)
    if (matchedTemplate) {
      const templateMap = { ...baseMap };

      // 단일 필드
      (matchedTemplate.singleFields || matchedTemplate.fields || []).forEach(
        (field) => {
          const key = typeof field === "string" ? field : field.key;
          const displayName =
            typeof field === "object" && field.displayName
              ? field.displayName
              : key;
          templateMap[key] = displayName;
        },
      );

      // 테이블 컬럼
      (matchedTemplate.tableFields || []).forEach((table) => {
        (table.columns || []).forEach((col) => {
          templateMap[col.key] = col.displayName || col.key;
        });
      });

      // 합성 필드
      (matchedTemplate.computedFields || []).forEach((cf) => {
        templateMap[cf.key] = cf.displayName || cf.key;
      });

      return templateMap;
    }

    // 경락확인서인 경우 특별 라벨 사용
    if (documentTypeForLabel === "경락확인서") {
      return {
        ...baseMap,
        ownerName: "성명",
        residentNumber: "주민(법인)등록번호",
        address: "주소(사용본거지)",
        totalAmount: "매입금액(VAT포)",
        auctionDate: "경락일자",
        buyerName: "상호",
        buyerBusinessNumber: "사업자등록번호",
      };
    }

    // 기본 매핑 (자동차등록증 등)
    return {
      ...baseMap,
      ownerName: "소유자명",
      residentNumber: "생년월일\n(법인등록번호)",
      address: "사용본거지",
      totalAmount: "총금액",
      auctionDate: "경락일자",
    };
  };

  const fieldNameMap = getFieldNameMap();

  // 체크된 페이지만 필터링
  // 📄 checkedPages가 비어있으면 모든 페이지를 사용
  // 🔧 타입 불일치 방지: pageNumber를 숫자로 변환하여 비교
  const checkedPageResults =
    isPDF && pageResults
      ? checkedPages.length > 0
        ? pageResults.filter((p) => {
            const pageNum = Number(p.pageNumber);
            const isChecked = checkedPages.some((cp) => Number(cp) === pageNum);
            return isChecked;
          })
        : pageResults // 체크된 페이지 없으면 전체 사용
      : pageResults;

  console.log("🔍 [checkedPageResults]:", {
    isPDF,
    pageResultsLength: pageResults?.length,
    pageResultsPageNumbers: pageResults?.map((p) => p.pageNumber),
    checkedPages,
    checkedPageResultsLength: checkedPageResults?.length,
  });

  // 현재 페이지가 체크되지 않았는지 확인
  const isCurrentPageChecked = checkedPages.includes(currentPage);
  // 디버깅 로그 제거 (무한 루프 방지)

  // CBM 자동 계산 함수
  // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
  const calculateCBM = (length, width, height) => {
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    if (l === 0 || w === 0 || h === 0) return null;
  };

  // Flash와 Pro 데이터 비교 (extractedData 우선 사용)
  const compareData = (
    flash,
    pro,
    isPartial,
    extractedData = null,
    differences = null,
  ) => {
    const rows = [];
    // ⭐ extractedData가 있으면 모든 키를 포함하도록 함
    const allKeys = new Set([
      ...Object.keys(flash || {}),
      ...Object.keys(pro || {}),
      ...Object.keys(extractedData || {}),
    ]);

    // ⭐ extractedData만 있고 다른 데이터가 없을 때도 필드 생성
    if (
      extractedData &&
      Object.keys(extractedData).length > 0 &&
      !flash &&
      !pro
    ) {
      // extractedData의 모든 키를 allKeys에 추가 (이미 추가되어 있지만 명시적으로 확인)
      Object.keys(extractedData).forEach((key) => {
        if (
          key !== "metadata" &&
          key !== "timestamp" &&
          key !== "source" &&
          key !== "pageNumber" &&
          key !== "docCode"
        ) {
          allKeys.add(key);
        }
      });
    }

    // ⭐ 문서 타입별 필수 필드 강제 추가
    const docType =
      extractedData?.documentType || pro?.documentType || flash?.documentType;

    // ⭐ 템플릿이 있는 경우 템플릿 필드 강제 추가
    if (matchedTemplate) {
      matchedTemplate.fields.forEach((field) =>
        allKeys.add(typeof field === "string" ? field : field.key),
      );
    } else if (docType === "경락확인서") {
      // 매도자 정보 필드 강제 추가 (경락확인서에 필수)
      const sellerFields = ["residentNumber", "address"];
      sellerFields.forEach((field) => allKeys.add(field));

      // 차량상세 필드 강제 추가 (경락확인서에 없을 수 있지만 템플릿 통일을 위해 표시)
      // 길이, 너비, 높이는 화면에서 제외 (스크롤 최소화)
      const vehicleDetailFields = [
        "modelYear",
        "mileage",
        "displacement",
        "totalWeight",
        "cbm",
      ];
      vehicleDetailFields.forEach((field) => allKeys.add(field));

      // 매수자 정보 필드 강제 추가 (경락확인서에 필수)
      const buyerFields = ["buyerName", "buyerBusinessNumber"];
      buyerFields.forEach((field) => allKeys.add(field));
    } else if (
      docType === "자동차등록증" ||
      docType === "자동차등록원부" ||
      docType === "말소등록증"
    ) {
      // ⭐ 자동차등록증/등록원부/말소등록증의 경우 차량 상세 필드 강제 추가
      // (검증 결과에는 있지만 추출 데이터에 없을 수 있음)
      // 연식, 주행거리, 배기량, 총중량, 길이, 너비, 높이 + 소유자 정보 항상 표시
      const vehicleSpecFields = [
        "ownerName",
        "residentNumber",
        "address", // 소유자 정보 추가
        "modelYear",
        "mileage",
        "displacement",
        "totalWeight",
        "length",
        "width",
        "height",
      ];
      vehicleSpecFields.forEach((field) => allKeys.add(field));
    }

    // CBM 자동 계산 추가
    const flashCBM = calculateCBM(flash?.length, flash?.width, flash?.height);
    const proCBM = calculateCBM(pro?.length, pro?.width, pro?.height);
    const extractedCBM = calculateCBM(
      extractedData?.length,
      extractedData?.width,
      extractedData?.height,
    );

    if (flashCBM) {
      flash = { ...flash, cbm: flashCBM };
      allKeys.add("cbm");
    }
    if (proCBM) {
      pro = { ...pro, cbm: proCBM };
      allKeys.add("cbm");
    }
    if (extractedCBM) {
      allKeys.add("cbm");
    }

    // ✅ 비교 로직 개선을 위한 정규화 함수 (백엔드와 동일하게 적용)
    const normalizeString = (str) => {
      if (!str) return "";
      let s = String(str).trim();
      if (!s) return "";
      if (/^(null|none|undefined)$/i.test(s)) return "";
      const cleanNumStr = s.replace(/,/g, "");
      const num = Number(cleanNumStr);
      if (!isNaN(num) && cleanNumStr !== "") {
        return String(num);
      }
      let normalized = s.normalize("NFC");
      normalized = normalized.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
        String.fromCharCode(s.charCodeAt(0) - 0xfee0),
      );
      normalized = normalized.toUpperCase();
      normalized = normalized.replace(/[^a-zA-Z0-9가-힣]/g, "");
      return normalized;
    };

    // ⭐ 한글 필드명 <-> 영문 키 매핑 (템플릿 호환성용)
    const KEY_MAPPING = {
      사업자등록번호: ["businessNumber", "crn", "regNumber"],
      등록번호: ["businessNumber", "plateNumber", "regNumber"], // 문맥에 따라 다름
      법인등록번호: ["corporateNumber", "juridicalNumber", "residentNumber"],
      상호: ["businessName", "companyName", "name"],
      "상호(법인명)": ["businessName", "companyName"],
      "법인명(단체명)": ["businessName", "companyName"],
      성명: ["ownerName", "name", "representative"],
      대표자: ["ownerName", "representative", "ceo"],
      개업연월일: ["openDate", "startDate", "establishmentDate"],
      사업장소재지: ["address", "businessAddress"],
      "사업장 소재지": ["address", "businessAddress"],
      본점소재지: ["headquartersAddress", "address"],
      "본점 소재지": ["headquartersAddress", "address"],
      사업의종류: ["businessType", "businessCategory"],
      "사업의 종류": ["businessType", "businessCategory"],
      업태: ["businessCondition", "businessCategory", "businessType"],
      종목: ["businessItem", "item"],
      주소: ["address"],
      발급일자: ["issueDate", "date"],
      차량등록번호: ["plateNumber"],
      차대번호: ["vin"],
      차명: ["model", "carName"],
      연식: ["modelYear", "year"],
      원동기형식: ["engineType"],
      사용본거지: ["address", "ownerAddress"],
    };

    // ⭐ 안전한 문자열 변환 (배열/객체는 [object Object] 대신 JSON 또는 빈값 처리)
    const safeStringify = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === "object") {
        // 배열이나 객체는 테이블 데이터 → 단일 필드에서는 빈값 처리
        return "";
      }
      return String(val).trim();
    };

    allKeys.forEach((key) => {
      if (
        key === "metadata" ||
        key === "timestamp" ||
        key === "source" ||
        key === "pageNumber" ||
        key === "docCode"
      )
        return;

      // ⭐ 배열/객체 값은 테이블 데이터로 처리되므로 단일 필드 행에서 제외
      const anyValue =
        (extractedData || {})[key] || (pro || {})[key] || (flash || {})[key];
      if (
        Array.isArray(anyValue) ||
        (anyValue !== null && typeof anyValue === "object")
      )
        return;

      // ⭐ 값 조회 시 매핑된 키도 함께 확인
      const getValue = (source, currentKey) => {
        if (!source) return undefined;

        // 1. 직접 키 조회
        if (source[currentKey] !== undefined) return source[currentKey];

        // 2. 매핑된 영문 키 조회 (currentKey가 한글인 경우)
        if (KEY_MAPPING[currentKey]) {
          for (const mappedKey of KEY_MAPPING[currentKey]) {
            if (source[mappedKey] !== undefined) return source[mappedKey];
          }
        }

        // 3. 역방향 조회 (currentKey가 영문이고 source가 한글 키를 가질 경우 - 드문 케이스)
        return undefined;
      };

      const flashValue = getValue(flash, key);
      const proValue = getValue(pro, key);
      const extractedValue = getValue(extractedData, key);

      const normalizedFlash = safeStringify(flashValue);
      const normalizedPro = safeStringify(proValue);
      const normalizedExtracted = safeStringify(extractedValue);

      let currentValue = "";
      if (
        extractedData &&
        (extractedData[key] !== undefined ||
          getValue(extractedData, key) !== undefined)
      ) {
        currentValue = normalizedExtracted;
      } else if (normalizedPro) {
        currentValue = normalizedPro;
      } else if (normalizedFlash) {
        currentValue = normalizedFlash;
      }

      // ⭐ 차이점 판단 로직 개선
      // 1. extractedData가 있으면 (검증 완료) 차이 없음 처리
      // 2. differences 객체가 있으면 (백엔드 계산) 그것을 우선 사용
      // 3. 없으면 프론트엔드에서 비교
      let isDifferent = false;

      if (extractedData) {
        isDifferent = false;
      } else if (differences && differences[key]) {
        // ⭐ 백엔드 차이점 정보 사용
        // match가 false이거나, flash/pro 값이 다른 경우
        isDifferent =
          differences[key].match === false ||
          differences[key].different === true;
      } else if (isPartial) {
        isDifferent = false;
      } else {
        // 프론트엔드 비교 (백엔드 정보 없을 때 fallback)
        const normFlash = normalizeString(normalizedFlash);
        const normPro = normalizeString(normalizedPro);
        isDifferent = normFlash && normPro && normFlash !== normPro;
      }

      rows.push({
        key: key,
        fieldName: key,
        flashValue: normalizedFlash,
        proValue: normalizedPro,
        currentValue: currentValue,
        isDifferent: isDifferent,
      });
    });

    return rows;
  };

  // ⭐ extractedData를 포함하여 비교 (검증 후 수정된 데이터 우선 사용)
  // ⭐ useMemo로 메모이제이션하여 불필요한 재계산 방지
  const dataRows = React.useMemo(() => {
    const result = compareData(
      pageData.flashData,
      pageData.proData,
      pageData.partial,
      pageData.extractedData,
      pageData.differences,
    );
    console.log("🔍 [dataRows] currentPage:", currentPage, "결과:", {
      dataRowsLength: result.length,
      hasFlashData: !!pageData.flashData,
      hasProData: !!pageData.proData,
      hasExtractedData: !!pageData.extractedData,
      flashDataKeys: pageData.flashData
        ? Object.keys(pageData.flashData).slice(0, 5)
        : [],
    });
    return result;
  }, [pageData, currentPage]);

  // ⭐ 디버깅 로그 제거 (무한 루프 방지)
  // useEffect의 dependency에 dataRows.length가 포함되어 있어 무한 루프 발생 가능

  // 필드 순서 정렬 (스키마 기반) - useMemo로 안정화
  // 경락확인서는 매수자 정보와 금액 정보 추가
  // 필드 순서 정렬 (스키마 기반) - useMemo로 안정화
  // 경락확인서는 매수자 정보와 금액 정보 추가
  const getFieldOrder = () => {
    // ⭐ 템플릿이 있는 경우 모든 필드 타입 포함, outputOrder로 정렬
    if (matchedTemplate) {
      const allFields = [];

      // 단일 필드
      (matchedTemplate.singleFields || matchedTemplate.fields || []).forEach(
        (f, idx) => {
          const field = typeof f === "string" ? { key: f } : f;
          const order =
            field.outputOrder !== undefined ? field.outputOrder : idx + 1;
          if (order > 0) allFields.push({ key: field.key, order });
        },
      );

      // 테이블 컬럼
      (matchedTemplate.tableFields || []).forEach((table, tableIdx) => {
        (table.columns || []).forEach((col, colIdx) => {
          const order = col.outputOrder || 100 + tableIdx * 100 + colIdx + 1;
          allFields.push({ key: col.key, order });
        });
      });

      // 합성 필드
      (matchedTemplate.computedFields || []).forEach((cf, idx) => {
        const order = cf.outputOrder ?? cf.order ?? 200 + idx + 1;
        if (order > 0) allFields.push({ key: cf.key, order });
      });

      // outputOrder로 정렬
      allFields.sort((a, b) => a.order - b.order);

      return ["documentType", ...allFields.map((f) => f.key)];
    }

    if (documentTypeForLabel === "경락확인서") {
      return [
        "documentType",
        "plateNumber",
        "vin",
        "model",
        "ownerName",
        "residentNumber",
        "address", // 매도자 정보
        "modelYear",
        "mileage",
        "displacement",
        "totalWeight",
        "cbm",
        "buyerName",
        "buyerBusinessNumber", // 매수자 정보
        "totalAmount",
        "auctionDate", // 금액 정보
      ];
    }

    // 기본 순서 (자동차등록증 등)
    return [
      "documentType",
      "plateNumber",
      "vin",
      "model",
      "ownerName",
      "residentNumber",
      "address",
      "modelYear",
      "mileage",
      "displacement",
      "totalWeight",
      "length",
      "width",
      "height",
      "cbm",
      "totalAmount",
      "auctionDate",
      "deregistrationDate",
    ];
  };

  const fieldOrder = getFieldOrder();

  const sortedDataRows = React.useMemo(() => {
    let rows = [...dataRows];

    // 데이터 소스 (extractedData > proData > flashData)
    const sourceData =
      pageData.extractedData || pageData.proData || pageData.flashData || {};

    // 🔍 디버그: 현재 페이지 데이터 확인
    console.log(
      "🔍 [sortedDataRows] currentPage:",
      currentPage,
      "sourceData:",
      sourceData,
    );

    // ⭐ 템플릿이 있으면 누락된 필드 및 합성 필드 추가
    if (matchedTemplate) {
      const existingKeys = new Set(rows.map((r) => r.key));

      // 🔧 테이블 필드 처리: 배열 데이터에서 값 추출하여 "|" 로 조인
      const tableFieldsSource = matchedTemplate.tableFields || [];
      console.log("🔍 [sortedDataRows] 테이블 필드 처리 시작:", {
        tableFieldsCount: tableFieldsSource.length,
        tableNames: tableFieldsSource.map((t) => t.tableName),
        sourceDataKeys: Object.keys(sourceData),
        currentPage,
        totalPages,
        // 🔍 직접 items 확인
        hasItems: !!sourceData.items,
        itemsLength: sourceData.items?.length || 0,
        firstItemSample: sourceData.items?.[0]
          ? JSON.stringify(sourceData.items[0]).substring(0, 150)
          : "N/A",
      });

      tableFieldsSource.forEach((table) => {
        const tableName = table.tableName;
        // ⭐ 우선순위: allItemsProp (병합 items) > sourceData[tableName] > sourceData.items
        // allItemsProp는 모든 페이지의 items를 포함하므로 멀티페이지 PDF에서 정확
        let tableData = [];
        if (
          allItemsProp &&
          Array.isArray(allItemsProp) &&
          allItemsProp.length > 0
        ) {
          console.log(
            `📦 [sortedDataRows] allItemsProp 사용 (${allItemsProp.length}개 items, 전체 페이지)`,
          );
          tableData = allItemsProp;
        } else if (
          sourceData[tableName] &&
          Array.isArray(sourceData[tableName]) &&
          sourceData[tableName].length > 0
        ) {
          tableData = sourceData[tableName];
        } else if (sourceData.items && Array.isArray(sourceData.items)) {
          console.log(`📦 [sortedDataRows] sourceData.items 폴백 사용`);
          tableData = sourceData.items;
        }

        console.log("📊 [sortedDataRows] 테이블 데이터:", {
          tableName,
          isArray: Array.isArray(tableData),
          tableDataLength: tableData?.length || 0,
          firstItemKeys: tableData?.[0]
            ? Object.keys(tableData[0]).join(", ")
            : "EMPTY", // 🔍 키 목록 (문자열)
          firstItemP:
            tableData?.[0]?.P ||
            tableData?.[0]?.p ||
            tableData?.[0]?.["P."] ||
            "N/A",
          totalPages,
        });

        // 📄 체크된 페이지들로 필터링 (모든 경우에 적용)
        if (
          Array.isArray(tableData) &&
          tableData.length > 0 &&
          checkedPages &&
          checkedPages.length > 0
        ) {
          const filteredData = tableData.filter((item) => {
            // 🔧 pageNumber, P, p 모두 확인
            const itemPage = item.pageNumber ?? item.P ?? item.p ?? 1;
            const pageNum =
              typeof itemPage === "string"
                ? parseInt(itemPage, 10)
                : Number(itemPage);
            return checkedPages.includes(pageNum);
          });
          if (tableData.length !== filteredData.length) {
            console.log("📄 [sortedDataRows] 페이지 필터링:", {
              tableName,
              checkedPages,
              before: tableData.length,
              after: filteredData.length,
              samplePages: tableData
                .slice(0, 5)
                .map((i) => i.pageNumber ?? i.P ?? i.p ?? "N/A"),
            });
          }
          tableData = filteredData;
        }

        (table.columns || []).forEach((col) => {
          // 테이블 행들에서 해당 컬럼 값 추출하여 "|" 로 조인
          let joinedValue = "";
          if (Array.isArray(tableData) && tableData.length > 0) {
            const values = tableData
              .map((item) => {
                // 1. 계산된 컬럼 처리
                if (col.type === "computed" && col.formula) {
                  let computed = col.formula;
                  const pattern = /\{([^}]+)\}/g;
                  let hasReplacement = false;

                  // 변수 치환 ({Qty} -> 5)
                  computed = computed.replace(pattern, (_, key) => {
                    hasReplacement = true;
                    // 대소문자 무시 키 찾기
                    const valKey = Object.keys(item).find(
                      (k) => k.toLowerCase() === key.toLowerCase(),
                    );
                    let val = valKey ? item[valKey] : "";

                    // 숫자인 경우 콤마 제거
                    if (
                      typeof val === "string" &&
                      /^-?[\d,]+(\.\d+)?$/.test(val)
                    ) {
                      return val.replace(/,/g, "");
                    }
                    return val || 0; // 수학 계산을 위해 빈 값은 0으로 처리 (상황에 따라 다름)
                  });

                  // 수학 식 계산 시도 (eval 사용 - 주의 필요)
                  // 단순히 문자열 결합을 원하는 경우도 있으므로, 숫자/연산자만 있을 때만 eval 시도
                  if (hasReplacement) {
                    try {
                      // 안전을 위해 허용된 문자만 있는지 확인 (숫자, 연산자, 공백, 괄호)
                      if (/^[\d+\-*/.()\s]+$/.test(computed)) {
                        // eslint-disable-next-line no-eval
                        const result = eval(computed);
                        return String(result); // 결과를 문자열로 반환
                      }
                    } catch (e) {
                      console.warn(
                        "Formula eval failed, using raw string:",
                        computed,
                        e,
                      );
                    }
                  }

                  // eval 실패하거나 수학식이 아니면 치환된 문자열 그대로 반환 (문자열 결합)
                  // 다시 한번 치환 (이번엔 0이 아닌 빈 문자열로, 문자열 결합용)
                  let stringComputed = col.formula;
                  stringComputed = stringComputed.replace(pattern, (_, key) => {
                    const valKey = Object.keys(item).find(
                      (k) => k.toLowerCase() === key.toLowerCase(),
                    );
                    return valKey ? item[valKey] : "";
                  });
                  return stringComputed;
                }

                // 2. 일반 컬럼 처리
                // 대소문자 무시 키 매칭
                const val =
                  item[col.key] ||
                  Object.keys(item).find(
                    (k) => k.toLowerCase() === col.key.toLowerCase(),
                  )
                    ? item[
                        Object.keys(item).find(
                          (k) => k.toLowerCase() === col.key.toLowerCase(),
                        )
                      ]
                    : "";
                return val || "";
              })
              .filter((v) => v !== "" && v !== null && v !== undefined);
            joinedValue = values.join(" | ");
          }

          // 기존 항목 업데이트 또는 새로 추가
          const existingIdx = rows.findIndex((r) => r.key === col.key);
          if (existingIdx >= 0) {
            if (!rows[existingIdx].currentValue && joinedValue) {
              rows[existingIdx].currentValue = joinedValue;
            }
            rows[existingIdx].isTableField = true;
            rows[existingIdx].tableName = tableName;
          } else {
            rows.push({
              key: col.key,
              fieldName: col.displayName || col.key,
              flashValue: "",
              proValue: "",
              currentValue: joinedValue,
              isDifferent: false,
              isTableField: true,
              tableName: tableName,
            });
            existingKeys.add(col.key);
          }
        });
      });

      // fieldOrder에 있지만 dataRows에 없는 필드 추가 (빈 값)
      fieldOrder.forEach((key) => {
        if (key !== "documentType" && !existingKeys.has(key)) {
          // sourceData에서 직접 값 가져오기 시도
          let value = sourceData[key] || "";
          if (typeof value === "object") value = JSON.stringify(value);

          rows.push({
            key: key,
            fieldName: key,
            flashValue: "",
            proValue: "",
            currentValue: value,
            isDifferent: false,
          });
          existingKeys.add(key);
        }
      });

      // 🔧 합성 필드 계산 및 추가/업데이트
      (matchedTemplate.computedFields || []).forEach((cf, idx) => {
        // ⭐ outputOrder가 없으면 900+idx 기본값 (항상 표시)
        const order = cf.outputOrder ?? cf.order ?? 900 + idx + 1;
        console.log(
          "📋 [sortedDataRows] 합성필드:",
          cf.key,
          "outputOrder:",
          cf.outputOrder,
          "=> order:",
          order,
        );

        // outputOrder가 0이면 제외, 그 외 모두 포함
        if (order > 0) {
          // 포뮬러에서 값 계산
          let computedValue = cf.formula || "";
          const pattern = /\{([^}]+)\}/g;
          let match;
          while ((match = pattern.exec(cf.formula || "")) !== null) {
            const formulaKey = match[1];

            // 1. rows에서 값 찾기 (대소문자 무시)
            let val = "";
            const sourceRow = rows.find(
              (r) => r.key.toLowerCase() === formulaKey.toLowerCase(),
            );
            if (sourceRow?.currentValue) {
              val = sourceRow.currentValue;
            } else {
              // 2. sourceData에서 직접 찾기 (대소문자 무시)
              const dataKey = Object.keys(sourceData).find(
                (k) => k.toLowerCase() === formulaKey.toLowerCase(),
              );
              if (dataKey) {
                val = sourceData[dataKey];
                if (typeof val === "object") val = ""; // 객체면 빈 값
              }

              // 3. 테이블 데이터에서 첫 번째 행 값 찾기
              if (!val) {
                tableFieldsSource.forEach((table) => {
                  let tableData = sourceData[table.tableName] || [];
                  // 🔧 FIX: items 폴백
                  if (tableData.length === 0 && sourceData.items)
                    tableData = sourceData.items;
                  if (Array.isArray(tableData) && tableData.length > 0) {
                    const firstItem = tableData[0];
                    const itemKey = Object.keys(firstItem).find(
                      (k) => k.toLowerCase() === formulaKey.toLowerCase(),
                    );
                    if (itemKey && firstItem[itemKey]) {
                      val = firstItem[itemKey];
                    }
                  }
                });
              }
            }

            computedValue = computedValue.replace(
              new RegExp(`\\{${formulaKey}\\}`, "gi"),
              val || "",
            );
          }

          // 기존 항목 찾기
          const existingIdx = rows.findIndex((r) => r.key === cf.key);
          if (existingIdx >= 0) {
            rows[existingIdx].currentValue = computedValue;
            rows[existingIdx].isComputed = true;
            rows[existingIdx].displayName = cf.displayName || cf.key;
          } else {
            rows.push({
              key: cf.key,
              fieldName: cf.displayName || cf.key,
              flashValue: "",
              proValue: "",
              currentValue: computedValue,
              isDifferent: false,
              isComputed: true,
            });
          }
        }
      });
    }

    // fieldOrder 기준으로 정렬
    const sorted = rows.sort((a, b) => {
      const indexA = fieldOrder.indexOf(a.key);
      const indexB = fieldOrder.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return sorted;
  }, [
    dataRows,
    fieldOrder,
    documentTypeForLabel,
    matchedTemplate,
    pageData,
    currentPage,
    totalPages,
    checkedPages,
  ]); // 📄 checkedPages 추가 - 다중 페이지 선택용

  // 일치율 계산
  const matchedCount = sortedDataRows.filter((r) => !r.isDifferent).length;
  const matchRate =
    sortedDataRows.length > 0
      ? Math.round((matchedCount / sortedDataRows.length) * 100)
      : 100;

  // 편집 데이터 초기화 (extractedData 우선 사용)
  // ⭐ sortedDataRows를 의존성에서 제거하여 무한 루프 방지
  // 대신 pageData의 개별 필드를 직접 사용
  useEffect(() => {
    const initialData = {};
    sortedDataRows.forEach((r) => {
      // ⭐ currentValue는 이미 extractedData > proData > flashData 우선순위로 설정됨
      initialData[r.key] = r.currentValue;
    });
    setEditedData(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageData.flashData,
    pageData.proData,
    pageData.extractedData,
  ]);

  // 필드 변경 핸들러
  const handleFieldChange = (key, value) => {
    setEditedData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 필드 그룹 정의 (문서 타입에 따라 다르게 구성)
  const getFieldGroups = () => {
    let groups = [];
    const definedFields = new Set();

    if (matchedTemplate) {
      // ⭐ 템플릿 기반 그룹 생성 - outputOrder 정렬된 통합 필드 리스트
      const allFields = [];

      // 단일 필드 (singleFields 또는 fields)
      const singleFieldsSource =
        matchedTemplate.singleFields || matchedTemplate.fields || [];
      singleFieldsSource.forEach((f, idx) => {
        const field = typeof f === "string" ? { key: f, displayName: f } : f;
        const order =
          field.outputOrder !== undefined ? field.outputOrder : idx + 1;
        if (order > 0) {
          allFields.push({
            key: field.key,
            displayName: field.displayName || field.key,
            order: order,
            fieldType: "single",
          });
        }
      });

      // 테이블 컬럼들
      const tableFieldsSource = matchedTemplate.tableFields || [];
      let tableColIdx = 0;
      tableFieldsSource.forEach((table, tableIdx) => {
        (table.columns || []).forEach((col, colIdx) => {
          const order = col.outputOrder || 100 + tableIdx * 100 + colIdx + 1;
          allFields.push({
            key: col.key,
            displayName: col.displayName || col.key,
            order: order,
            fieldType: "table",
            tableName: table.tableName,
          });
          tableColIdx++;
        });
      });

      // 합성 필드 (computedFields)
      const computedFieldsSource = matchedTemplate.computedFields || [];
      computedFieldsSource.forEach((cf, idx) => {
        const order = cf.outputOrder ?? cf.order ?? 200 + idx + 1;
        if (order > 0) {
          allFields.push({
            key: cf.key,
            displayName: cf.displayName || cf.key,
            order: order,
            fieldType: "computed",
            formula: cf.formula,
          });
        }
      });

      // outputOrder로 정렬
      allFields.sort((a, b) => a.order - b.order);

      groups = [
        {
          name: "문서 정보",
          fields: allFields.map((f) => f.key),
          fieldMetadata: allFields, // 메타데이터 저장 (fieldType, formula 등)
        },
      ];
    } else if (documentTypeForLabel === "경락확인서") {
      groups = [
        { name: "차량 정보", fields: ["plateNumber", "vin", "model"] },
        {
          name: "매도자 정보",
          fields: ["ownerName", "residentNumber", "address"],
        },
        {
          name: "차량 상세",
          fields: [
            "modelYear",
            "mileage",
            "displacement",
            "totalWeight",
            "cbm",
          ],
        },
        {
          name: "매수자정보(수출화주)",
          fields: ["buyerName", "buyerBusinessNumber"],
        },
        { name: "금액 정보", fields: ["totalAmount", "auctionDate"] },
      ];
    } else if (documentTypeForLabel === "신분증") {
      // ⭐ 신분증 전용 그룹
      groups = [
        {
          name: "신분증 정보",
          fields: [
            "name",
            "residentNumber",
            "address",
            "issueDate",
            "licenseNumber",
          ],
        },
      ];
    } else {
      // 기본 그룹 (자동차등록증 등)
      groups = [
        { name: "차량 정보", fields: ["plateNumber", "vin", "model"] },
        {
          name: "소유자 정보",
          fields: ["ownerName", "residentNumber", "address"],
        },
        {
          name: "차량 상세",
          fields: [
            "modelYear",
            "mileage",
            "displacement",
            "totalWeight",
            "length",
            "width",
            "height",
            "cbm",
          ],
        },
      ];
    }

    // 이미 그룹에 포함된 필드들 수집
    groups.forEach((group) => {
      group.fields.forEach((field) => definedFields.add(field));
    });

    // ⭐ 그룹에 포함되지 않은 나머지 필드 찾기 (기타 정보)
    // 🔧 템플릿 모드에서는 기타 정보 표시하지 않음 (템플릿 필드만 표시)
    if (!matchedTemplate) {
      const remainingFields = sortedDataRows
        .filter((r) => !definedFields.has(r.key) && r.key !== "documentType")
        .map((r) => r.key);

      if (remainingFields.length > 0) {
        groups.push({
          name: "기타 정보", // 템플릿에 없는 추가 필드들
          fields: remainingFields,
        });
      }
    }

    return groups;
  };

  const fieldGroups = getFieldGroups();

  // 폼 렌더링 - 심플 한 줄 헤더 형식 (그룹핑)
  const renderForm = () => {
    // ⭐ 디버깅 로그 제거 (무한 루프 방지)

    if (sortedDataRows.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          <p>⚠️ 데이터가 없습니다.</p>
          <p className="text-xs mt-2">
            flashData: {pageData.flashData ? "있음" : "없음"}
          </p>
          <p className="text-xs">
            proData: {pageData.proData ? "있음" : "없음"}
          </p>
          <p className="text-xs">
            extractedData: {pageData.extractedData ? "있음" : "없음"}
          </p>
        </div>
      );
    }

    return (
      <div className="p-2 space-y-2">
        {fieldGroups.map((group, groupIdx) => {
          const groupFields = sortedDataRows.filter((r) =>
            group.fields.includes(r.key),
          );

          // ⭐ 디버깅: 그룹 필드 확인
          if (group.name === "차량 상세") {
            // 디버깅 로그 제거 (무한 루프 방지)
          }

          // ⭐ 필드가 없으면 빈 값으로 필드 생성 (경락확인서, 자동차등록증 등)
          if (groupFields.length === 0) {
            const docType =
              pageData.extractedData?.documentType ||
              pageData.proData?.documentType ||
              pageData.flashData?.documentType;

            // 경락확인서: 차량상세 및 매수자정보 그룹은 항상 표시
            if (
              docType === "경락확인서" &&
              (group.name === "차량 상세" ||
                group.name === "매수자정보(수출화주)")
            ) {
              // 필드를 빈 값으로 생성
              const emptyFields = group.fields.map((fieldKey) => ({
                key: fieldKey,
                fieldName: fieldKey,
                flashValue: "",
                proValue: "",
                currentValue: "",
                isDifferent: false,
              }));
              return (
                <div
                  key={groupIdx}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-600">
                      {group.name}
                    </h3>
                  </div>
                  <div className="bg-white">
                    {emptyFields.map((r, idx) => {
                      const currentValue = editedData[r.key] ?? r.currentValue;
                      // ⭐ 숫자 필드에 천 단위 콤마 적용
                      const numericFieldsWithComma = [
                        "mileage",
                        "displacement",
                        "totalWeight",
                        "length",
                        "width",
                        "height",
                        "cbm",
                      ];
                      const isNumericField = numericFieldsWithComma.includes(
                        r.key,
                      );

                      let displayValue = currentValue || "";
                      if (
                        isNumericField &&
                        currentValue !== null &&
                        currentValue !== undefined &&
                        currentValue !== ""
                      ) {
                        try {
                          const cleanValue = String(currentValue)
                            .replace(/,/g, "")
                            .trim();
                          const numValue = parseFloat(cleanValue);
                          if (!isNaN(numValue) && isFinite(numValue)) {
                            displayValue = numValue.toLocaleString("ko-KR");
                          }
                        } catch (e) {
                          displayValue = currentValue || "";
                        }
                      }

                      const handleNumericChange = (e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        handleFieldChange(r.key, rawValue);
                      };

                      return (
                        <div
                          key={idx}
                          className={
                            idx < emptyFields.length - 1
                              ? "border-b border-gray-100"
                              : ""
                          }
                        >
                          <div className="flex items-center gap-2 py-1.5 px-3">
                            <span className="text-xs text-gray-600 w-32 flex-shrink-0">
                              {fieldNameMap[r.key] || r.key}
                            </span>
                            <input
                              type="text"
                              value={displayValue}
                              onChange={
                                isNumericField
                                  ? handleNumericChange
                                  : (e) =>
                                      handleFieldChange(r.key, e.target.value)
                              }
                              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="값 없음"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // ⭐ 자동차등록증/등록원부/말소등록증: 차량 상세 그룹은 항상 표시 (연식, 주행거리, 배기량 등)
            if (
              (docType === "자동차등록증" ||
                docType === "자동차등록원부" ||
                docType === "말소등록증") &&
              group.name === "차량 상세"
            ) {
              // 필드를 빈 값으로 생성
              const emptyFields = group.fields.map((fieldKey) => ({
                key: fieldKey,
                fieldName: fieldKey,
                flashValue: "",
                proValue: "",
                currentValue: "",
                isDifferent: false,
              }));
              return (
                <div
                  key={groupIdx}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-600">
                      {group.name}
                    </h3>
                  </div>
                  <div className="bg-white">
                    {emptyFields.map((r, idx) => {
                      const currentValue = editedData[r.key] ?? r.currentValue;
                      // ⭐ 숫자 필드에 천 단위 콤마 적용
                      const numericFieldsWithComma = [
                        "mileage",
                        "displacement",
                        "totalWeight",
                        "length",
                        "width",
                        "height",
                        "cbm",
                      ];
                      const isNumericField = numericFieldsWithComma.includes(
                        r.key,
                      );

                      let displayValue = currentValue || "";
                      if (
                        isNumericField &&
                        currentValue !== null &&
                        currentValue !== undefined &&
                        currentValue !== ""
                      ) {
                        try {
                          const cleanValue = String(currentValue)
                            .replace(/,/g, "")
                            .trim();
                          const numValue = parseFloat(cleanValue);
                          if (!isNaN(numValue) && isFinite(numValue)) {
                            displayValue = numValue.toLocaleString("ko-KR");
                          }
                        } catch (e) {
                          displayValue = currentValue || "";
                        }
                      }

                      const handleNumericChange = (e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        handleFieldChange(r.key, rawValue);
                      };

                      return (
                        <div
                          key={idx}
                          className={
                            idx < emptyFields.length - 1
                              ? "border-b border-gray-100"
                              : ""
                          }
                        >
                          <div className="flex items-center gap-2 py-1.5 px-3">
                            <span className="text-xs text-gray-600 w-32 flex-shrink-0">
                              {fieldNameMap[r.key] || r.key}
                            </span>
                            <input
                              type="text"
                              value={displayValue}
                              onChange={
                                isNumericField
                                  ? handleNumericChange
                                  : (e) =>
                                      handleFieldChange(r.key, e.target.value)
                              }
                              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="값 없음"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return null;
          }

          return (
            <div
              key={groupIdx}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* 그룹 헤더 */}
              <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                <h3 className="text-xs font-semibold text-gray-600">
                  {group.name}
                </h3>
              </div>

              {/* 그룹 필드들 */}
              <div className="bg-white">
                {groupFields.map((r, idx) => {
                  const currentValue = editedData[r.key] ?? r.currentValue;
                  const hasDifference = r.isDifferent;

                  // ⭐ [Grounding] 해당 필드에 위치 정보가 있는지 확인
                  // 🆕 테이블 필드: items[0].FieldName (셀별) 또는 items[0] (행별) 체크
                  const directGrounding =
                    groundedFields[r.key]?.bbox?.coordinates;
                  const cellGrounding =
                    r.isTableField &&
                    groundedFields[`items[0].${r.key}`]?.bbox?.coordinates;
                  const rowGrounding =
                    r.isTableField &&
                    groundedFields["items[0]"]?.bbox?.coordinates;
                  const hasGrounding =
                    directGrounding || cellGrounding || rowGrounding;

                  return (
                    <div
                      key={idx}
                      className={
                        idx < groupFields.length - 1
                          ? "border-b border-gray-100"
                          : ""
                      }
                    >
                      {/* 필드명: 값 (한 줄) */}
                      <div
                        className={`flex items-center gap-2 py-1.5 px-3 transition-all ${
                          hasDifference ? "bg-gray-50" : ""
                        } ${
                          hoveredField === r.key
                            ? "bg-blue-100 border-l-4 border-l-blue-500"
                            : ""
                        } ${
                          hasGrounding
                            ? "cursor-pointer hover:bg-yellow-50 border-l-2 border-l-yellow-400"
                            : "cursor-default"
                        }`}
                        title={
                          hasGrounding
                            ? "🎯 클릭하면 PDF에서 해당 위치로 이동합니다"
                            : ""
                        }
                        onMouseEnter={() => {
                          setHoveredField(r.key);
                          // ⭐ 부모 컴포넌트에 호버 이벤트 전달
                          if (onFieldHover) {
                            onFieldHover(r.key, currentValue);
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredField(null);
                          if (onFieldHover) {
                            onFieldHover(null, null);
                          }
                        }}
                        onClick={() => {
                          // ⭐ [Grounding] 필드 클릭 시 해당 PDF 위치로 이동
                          let grounding = groundedFields[r.key];

                          // 🆕 [Table Grounding] 테이블 필드인 경우 grounding 조회
                          if (!grounding?.bbox?.coordinates && r.isTableField) {
                            // ⭐ 첫 번째 열(Item)은 행 전체 하이라이트
                            const isFirstColumn = r.key === "Item";

                            if (isFirstColumn) {
                              // 행 레벨 grounding 사용 (행 전체 너비)
                              const rowKey = `items[0]`;
                              grounding = groundedFields[rowKey];
                              console.log(
                                `📦 [Table Grounding] '${r.key}' → 행 전체 '${rowKey}':`,
                                grounding?.bbox?.coordinates ? "✅" : "❌",
                              );
                            } else {
                              // 셀별 grounding 시도
                              const cellKey = `items[0].${r.key}`;
                              grounding = groundedFields[cellKey];

                              // 셀별 없으면 행 레벨 폴백
                              if (!grounding?.bbox?.coordinates) {
                                const rowKey = `items[0]`;
                                grounding = groundedFields[rowKey];
                                console.log(
                                  `📦 [Table Grounding] '${r.key}' → 행 폴백 '${rowKey}':`,
                                  grounding?.bbox?.coordinates ? "✅" : "❌",
                                );
                              } else {
                                console.log(
                                  `📦 [Table Grounding] '${r.key}' → 셀 '${cellKey}':`,
                                  grounding?.bbox?.coordinates ? "✅" : "❌",
                                );
                              }
                            }
                          }

                          const hasGroundingCoords =
                            grounding?.bbox?.coordinates;

                          // 🔍 [DEBUG] 클릭 이벤트 디버깅
                          console.log("🖱️ [Grounding Click]", {
                            fieldKey: r.key,
                            isTableField: r.isTableField,
                            currentValue,
                            groundingData: grounding,
                            hasGrounding: !!hasGroundingCoords,
                            willNavigate:
                              !!hasGroundingCoords &&
                              !!grounding?.bbox?.page !== undefined,
                          });

                          if (onFieldClick) {
                            onFieldClick({
                              fieldKey: r.key,
                              fieldValue: currentValue,
                              // Grounding 정보 (LangExtract에서 반환)
                              page: hasGroundingCoords
                                ? grounding.bbox.page + 1
                                : null, // 0-indexed → 1-indexed
                              coordinates: hasGroundingCoords
                                ? grounding.bbox.coordinates
                                : null,
                              confidence: grounding?.confidence || null,
                              hasGrounding: !!hasGroundingCoords,
                            });
                          }
                        }}
                      >
                        <label className="text-xs font-medium text-gray-600 whitespace-pre-wrap min-w-[120px]">
                          {fieldNameMap[r.fieldName] || r.fieldName}:
                        </label>
                        {r.key === "documentType" ? (
                          <select
                            value={currentValue}
                            onChange={(e) =>
                              handleFieldChange(r.key, e.target.value)
                            }
                            disabled={validatedPages[currentPage]}
                            className={`flex-1 px-2 py-0.5 border rounded text-xs focus:outline-none transition-colors ${
                              validatedPages[currentPage]
                                ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                                : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                            }`}
                          >
                            <option value="">문서 타입 선택</option>
                            <option value="경락확인서">경락확인서</option>
                            <option value="자동차매매계약서">
                              자동차매매계약서
                            </option>
                            <option value="자동차등록원부">
                              자동차등록원부
                            </option>
                            <option value="자동차등록증">자동차등록증</option>
                            <option value="말소등록증">말소등록증</option>
                            <option value="사업자등록증">사업자등록증</option>
                            <option value="신분증">신분증</option>
                            <option value="폐차증">폐차증</option>
                            <option value="세금계산서">세금계산서</option>
                            <option value="기타">기타</option>
                          </select>
                        ) : (
                          (() => {
                            // ⭐ 숫자 필드에 천 단위 콤마 적용
                            const numericFieldsWithComma = [
                              "mileage",
                              "displacement",
                              "totalWeight",
                              "length",
                              "width",
                              "height",
                              "cbm",
                            ];
                            const isNumericField =
                              numericFieldsWithComma.includes(r.key);

                            let displayValue = currentValue;
                            if (
                              isNumericField &&
                              currentValue !== null &&
                              currentValue !== undefined &&
                              currentValue !== ""
                            ) {
                              try {
                                const cleanValue = String(currentValue)
                                  .replace(/,/g, "")
                                  .trim();
                                const numValue = parseFloat(cleanValue);
                                if (!isNaN(numValue) && isFinite(numValue)) {
                                  displayValue =
                                    numValue.toLocaleString("ko-KR");
                                }
                              } catch (e) {
                                // 포맷팅 실패 시 원본 값 사용
                                displayValue = currentValue;
                              }
                            }

                            const handleNumericChange = (e) => {
                              const rawValue = e.target.value.replace(/,/g, "");
                              handleFieldChange(r.key, rawValue);
                            };

                            return (
                              <input
                                type="text"
                                value={displayValue}
                                onChange={
                                  isNumericField
                                    ? handleNumericChange
                                    : (e) =>
                                        handleFieldChange(r.key, e.target.value)
                                }
                                disabled={validatedPages[currentPage]}
                                className={`flex-1 px-2 py-0.5 border rounded text-xs focus:outline-none transition-colors ${
                                  validatedPages[currentPage]
                                    ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                                    : hasDifference
                                      ? "border-[#DADCE0] bg-[#FEF7E0] text-[#202124] focus:ring-1 focus:ring-[#EA8600] focus:border-[#EA8600] font-medium" // 차이점 있을 때 부드러운 amber 스타일
                                      : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                                }`}
                                placeholder={
                                  validatedPages[currentPage]
                                    ? "검증 완료"
                                    : "값을 입력하세요"
                                }
                              />
                            );
                          })()
                        )}
                        {/* Pure OCR 출처 배지: OCR vs DB 보강 vs 빈값 */}
                        {/* Phase 1.5b G3: map camelCase grid key → server snake_case key */}
                        {r.key !== "documentType" &&
                          (() => {
                            const _srvKey = gridKeyToServerKey(r.key);
                            return (
                              <span className="ml-1 flex-shrink-0">
                                <FieldSourceBadge
                                  fieldKey={r.key}
                                  value={currentValue}
                                  rawValue={
                                    pageData?.extractedData?._raw?.[_srvKey]
                                  }
                                  dbAugmented={
                                    pageData?.extractedData?._enrichment
                                      ?.db_augmented?.[_srvKey]
                                  }
                                />
                              </span>
                            );
                          })()}
                        {hasDifference && (
                          <span className="text-xs text-[#EA8600] font-medium whitespace-nowrap ml-2">
                            ⚠ 차이
                          </span>
                        )}
                      </div>
                      {/* 차이 비교 정보 - 부드러운 톤 */}
                      {hasDifference && (
                        <div className="px-3 pb-1.5 pt-0.5 flex gap-4 text-xs text-[#5F6368] bg-[#FEF7E0] rounded-b-lg mx-1 mb-1">
                          <div className="flex-1">
                            <span className="font-medium text-[#B06000]">
                              {getSourceNickname(pageData?.flashData?.source) ||
                                "추출1:"}
                              :
                            </span>{" "}
                            <span className="text-[#5F6368]">
                              {r.flashValue || "(없음)"}
                            </span>
                          </div>
                          <div className="flex-1">
                            <span className="font-medium text-[#B06000]">
                              {getSourceNickname(pageData?.proData?.source) ||
                                "추출2:"}
                              :
                            </span>{" "}
                            <span className="text-[#5F6368]">
                              {r.proValue || "(없음)"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 가로 그리드 AUIGrid 초기화 — useLayoutEffect: paint 전에 그리드 생성하여 빈 상태 방지
  useLayoutEffect(() => {
    // 🔧 FIX: 세대 카운터는 조건 체크 전에 증가 — 조건 불충족 시에도 이전 stale retry를 무효화
    const gen = ++gridCreationGenRef.current;

    // 🔧 FIX: 이전 높이 대기 ResizeObserver 정리 (리소스 누수 방지)
    if (heightObserverForGridRef.current) {
      try {
        heightObserverForGridRef.current.disconnect();
      } catch (e) {}
      heightObserverForGridRef.current = null;
    }

    console.log("🔧 [HorizontalGrid useEffect] 조건 체크:", {
      hasAUIGrid: !!window.AUIGrid,
      viewMode,
      pageResultsLength: pageResults?.length,
      gen,
    });

    // 📄 pageResults 기준으로 체크 (체크 상태와 관계없이 그리드 유지)
    if (
      !window.AUIGrid ||
      viewMode !== "horizontal" ||
      !pageResults ||
      pageResults.length === 0
    ) {
      console.log("🔧 [HorizontalGrid useEffect] 조건 불충족으로 return");
      return;
    }

    console.log("✅ [HorizontalGrid useEffect] 초기화 시작");
    const gridId = "horizontal_grid";

    // 🔧 FIX: 컬럼 시그니처로 "전체 재생성" vs "데이터만 업데이트" 판단 (깜박임 방지)
    const columnSignature = JSON.stringify({
      viewMode,
      templateId: matchedTemplate?.id || "",
      templateName: matchedTemplate?.name || "",
      columnFilter,
      tableFieldsCount:
        matchedTemplate?.tableFields?.reduce(
          (sum, t) => sum + (t.columns?.length || 0),
          0,
        ) || 0,
      singleFieldsCount: (
        matchedTemplate?.singleFields ||
        matchedTemplate?.fields ||
        []
      ).length,
    });

    // 🔧 FIX: gridRef가 있더라도 DOM에 그리드가 실제로 존재하는지 확인
    // (checkedPages=[] → early return → DOM에서 그리드 제거 → 다시 checkedPages=[1] 전환 시 stale ref 방지)
    const currentGridElement = document.getElementById(gridId);
    // ⚠️ CRITICAL: DOM element identity 체크 — 제거 금지
    // React가 조건부 렌더링(mismatch bar 등)으로 div를 재생성하면
    // AUIGrid 인스턴스가 분리되어 setGridData가 화면에 반영되지 않음
    const domIdentityMatch =
      gridDomRef.current &&
      currentGridElement &&
      gridDomRef.current === currentGridElement;
    const gridStillExists =
      gridRef.current &&
      currentGridElement &&
      domIdentityMatch &&
      window.AUIGrid.isCreated("#" + gridId);
    const isDataOnlyUpdate =
      gridStillExists && prevGridSignatureRef.current === columnSignature;

    if (!domIdentityMatch && gridRef.current) {
      console.warn(
        "⚠️ [HorizontalGrid] DOM element 변경 감지 — React가 #horizontal_grid div를 재생성함. 전체 재생성 필요.",
      );
    }

    if (isDataOnlyUpdate) {
      // 🔧 컬럼 동일 + 그리드 존재 → 데이터만 업데이트 (깜박임 없음)
      console.log("📊 [HorizontalGrid] 데이터만 업데이트 (컬럼 변경 없음)");
    } else {
      // 컬럼 변경 또는 첫 생성 → 전체 재생성
      prevGridSignatureRef.current = columnSignature;
    }

    // 기존 그리드 제거 (전체 재생성 시에만)
    if (!isDataOnlyUpdate) {
      if (gridRef.current) {
        try {
          window.AUIGrid.destroy(gridRef.current);
        } catch (e) {
          console.warn("그리드 제거 실패:", e);
        }
        gridRef.current = null;
      }
    }

    // DOM이 준비될 때까지 대기 (리트라이 메커니즘 포함) — 높이 0 방지
    let retryCount = 0;
    const maxRetries = 15;
    const tryInitGrid = () => {
      // 🔧 FIX: 세대 체크 — 이전 useEffect의 stale retry가 최신 데이터를 덮어쓰지 않도록 방지
      if (gen !== gridCreationGenRef.current) {
        console.log(
          `🔧 [tryInitGrid] Stale gen(${gen} vs ${gridCreationGenRef.current}), skip`,
        );
        return;
      }
      const gridElement = document.getElementById(gridId);
      if (!gridElement || gridElement.clientHeight < 10) {
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(
            `⏳ [HorizontalGrid] DOM/크기 대기 중... (${retryCount}/${maxRetries}) height=${gridElement?.clientHeight || 0}`,
          );
          retryTimerRef.current = setTimeout(
            tryInitGrid,
            retryCount < 5 ? 100 : 300,
          );
          return;
        }
        if (!gridElement) {
          console.error(
            `그리드 DOM 요소를 찾을 수 없습니다: ${gridId} (${maxRetries}회 시도 후 포기)`,
          );
          return;
        }
        // 🔧 FIX: 높이 0에서 강제 생성하면 AUIGrid가 행을 렌더링하지 않음
        // ResizeObserver로 높이가 확보될 때까지 대기 후 생성
        console.warn(
          `⚠️ [HorizontalGrid] 컨테이너 높이=${gridElement.clientHeight}, ResizeObserver로 높이 확보 대기...`,
        );
        heightObserverForGridRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentRect.height >= 10) {
              console.log(
                `✅ [HorizontalGrid] ResizeObserver: 높이 확보됨 (${Math.round(entry.contentRect.height)}px) → 그리드 생성`,
              );
              if (heightObserverForGridRef.current) {
                heightObserverForGridRef.current.disconnect();
                heightObserverForGridRef.current = null;
              }
              // 🔧 FIX: 세대 체크 — stale observer 콜백이면 건너뜀
              if (gen !== gridCreationGenRef.current) {
                console.log(
                  `🔧 [ResizeObserver] Stale gen(${gen} vs ${gridCreationGenRef.current}), skip`,
                );
                break;
              }
              retryCount = 0; // 리셋하여 정상 진입
              tryInitGrid();
              break;
            }
          }
        });
        heightObserverForGridRef.current.observe(gridElement);
        return;
      }

      // 🔧 템플릿이 있으면 템플릿 필드 사용, 없으면 추출된 데이터 키 사용
      // ⭐ 필드 타입 정보 포함: { key, fieldType: 'single' | 'table' }
      let allFieldsWithType = [];

      if (matchedTemplate) {
        // 단일 필드 추가 (outputOrder 포함)
        const singleFieldsSource =
          matchedTemplate.singleFields || matchedTemplate.fields || [];
        singleFieldsSource.forEach((f, idx) => {
          const field = typeof f === "string" ? { key: f } : f;
          const order =
            field.outputOrder !== undefined ? field.outputOrder : idx + 1;
          allFieldsWithType.push({
            key: field.key,
            fieldType: "single",
            outputOrder: order,
          });
        });

        // 테이블 컬럼 추가 (outputOrder 포함)
        const tableFieldsSource = matchedTemplate.tableFields || [];
        tableFieldsSource.forEach((table, tableIdx) => {
          (table.columns || []).forEach((col, colIdx) => {
            const order = col.outputOrder || 100 + tableIdx * 100 + colIdx + 1;
            allFieldsWithType.push({
              key: col.key,
              fieldType: "table",
              tableName: table.tableName,
              outputOrder: order,
            });
          });
        });

        // 🔧 합성 필드 추가 (formula 포함, outputOrder 포함)
        const computedFieldsSource = matchedTemplate.computedFields || [];
        computedFieldsSource.forEach((cf, idx) => {
          const order = cf.outputOrder ?? cf.order ?? 200 + idx + 1;
          allFieldsWithType.push({
            key: cf.key,
            fieldType: "computed",
            formula: cf.formula,
            displayName: cf.displayName,
            outputOrder: order,
          });
        });

        // 🔧 outputOrder로 정렬 (필드설정 탭 순서와 동일)
        allFieldsWithType.sort(
          (a, b) => (a.outputOrder || 999) - (b.outputOrder || 999),
        );

        console.log(
          "📋 Using template fields for grid (sorted by outputOrder):",
          allFieldsWithType.map(
            (f) => `${f.key}(${f.fieldType}:${f.outputOrder})`,
          ),
        );
      } else {
        // 🔧 Fallback: 추출된 데이터에서 필드 수집 (모두 single로 처리)
        const allFieldsSet = new Set();
        checkedPageResults.forEach((page) => {
          const data = page.flashData || page.proData || {};
          Object.keys(data).forEach((key) => {
            if (
              key !== "metadata" &&
              key !== "timestamp" &&
              key !== "source" &&
              key !== "pageNumber" &&
              key !== "documentType"
            ) {
              allFieldsSet.add(key);
            }
          });
        });
        allFieldsWithType = Array.from(allFieldsSet).map((key) => ({
          key,
          fieldType: "single",
        }));
      }

      // 🔧 컬럼 필터 적용
      let filteredFields = allFieldsWithType;
      if (columnFilter === "single") {
        filteredFields = allFieldsWithType.filter(
          (f) => f.fieldType === "single",
        );
      } else if (columnFilter === "table") {
        filteredFields = allFieldsWithType.filter(
          (f) => f.fieldType === "table",
        );
      }

      // 기존 allFields 변수 유지 (하위 호환성)
      const allFields = filteredFields.map((f) => f.key);

      // 🆕 HS 자동분류용 HSCode field 식별 (case/공백/dash insensitive)
      const isHsCodeFieldKey = (k) =>
        String(k || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, "") === "hscode";
      const hsCodeFieldKey = allFields.find(isHsCodeFieldKey) || null;
      const hasHsCodeColumn = !!hsCodeFieldKey;

      // 컬럼 정의 - 🔧 페이지 헤더 축소, 문서타입 열 삭제
      const horizontalColumns = [
        {
          dataField: "pageNumber",
          headerText: "P", // 🔧 "페이지" → "P" (가로폭 축소)
          width: 35,
          editable: false,
          style: "center-align",
          headerStyle: "center-align",
          footer: {
            text: "Σ",
            style: "text-align: center; font-weight: bold; color: #555;",
          },
        },
        {
          dataField: "idx",
          headerText: "#", // 🔧 페이지 내 순서
          width: 30,
          editable: false,
          style: "center-align",
          headerStyle: "center-align",
        },
        // 🔧 문서타입 열 삭제 (사용자 요청)
        // ⭐ getOptimalColumnWidth 사용하여 필드별 최적 너비 적용
        ...allFields.map((field) => {
          // ⭐ 숫자형 필드 감지 (합계 표시용) - 영문 + 한글 패턴
          const isNumeric =
            /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|순중량|총중량|부피|합계|세금|비용|갯수|개수|Unit.*Price|Net.*Weight|Gross.*Weight/i.test(
              field,
            );

          // 📐 계층적 너비 시스템 적용
          const optimalWidth = getOptimalColumnWidth(field, "shipping");

          // 🔧 width가 0인 경우 기본값 200px 적용 (flex 대신 고정 너비)
          // Description 등 긴 필드도 고정 너비로 표시하고 스크롤바로 처리
          const finalWidth = optimalWidth === 0 ? 200 : optimalWidth;

          return {
            dataField: field,
            headerText: fieldNameMap[field] || field,
            // 🔧 모든 컬럼에 고정 너비 적용 (widthRate 제거)
            width: finalWidth,
            editable: true,
            editRenderer: {
              type: "InputEditRenderer",
            },
            // ⭐ 교차 검증 불일치 시 빨간색 글자 diff 표시 (TemplateRenderer 사용)
            renderer: {
              type: "TemplateRenderer",
            },
            labelFunction: (
              rowIndex,
              columnIndex,
              value,
              headerText,
              item,
              dataField,
            ) => {
              // 교차 검증 불일치가 있을 때만 diff HTML 렌더링
              const diff = item?.differences?.[dataField];
              if (diff && diff.match === false) {
                // ⭐ backend compareResults: { primary: "1차 값", secondary: "2차 값", match: false }
                const primaryVal = String(
                  diff.primary ?? diff.flash ?? value ?? "",
                );
                const currentVal = String(value ?? "");
                // 🔧 셀 값이 원래 flash 값과 다르면 사용자가 수정한 것 → diff 렌더링 생략
                if (currentVal !== primaryVal) {
                  if (isNumeric && currentVal !== "") {
                    const num = parseFloat(currentVal.replace(/,/g, ""));
                    if (!isNaN(num) && isFinite(num))
                      return num.toLocaleString("ko-KR", {
                        maximumFractionDigits: 6,
                      });
                  }
                  return currentVal;
                }
                const secondaryVal = String(diff.secondary ?? diff.pro ?? "");
                // 🔧 대소문자만 다르면 일치로 처리 (통관 시 대문자 통일)
                if (primaryVal.toLowerCase() === secondaryVal.toLowerCase()) {
                  return String(value ?? "");
                }
                // 🔧 LCS(최장 공통 부분열) 기반 diff: 삽입/삭제에도 정확한 차이만 빨간색
                const escCh = (c) =>
                  c === "&"
                    ? "&amp;"
                    : c === "<"
                      ? "&lt;"
                      : c === ">"
                        ? "&gt;"
                        : c;
                const a = primaryVal,
                  b = secondaryVal;
                const m = a.length,
                  n = b.length;
                // LCS DP 테이블 구축
                const dp = Array(m + 1)
                  .fill(null)
                  .map(() => new Uint16Array(n + 1));
                for (let i = 1; i <= m; i++) {
                  for (let j = 1; j <= n; j++) {
                    dp[i][j] =
                      a[i - 1].toLowerCase() === b[j - 1].toLowerCase()
                        ? dp[i - 1][j - 1] + 1
                        : Math.max(dp[i - 1][j], dp[i][j - 1]);
                  }
                }
                // 역추적: primaryVal의 어떤 글자가 LCS에 포함되는지 확인
                const inLCS = new Set();
                let li = m,
                  lj = n;
                while (li > 0 && lj > 0) {
                  if (a[li - 1].toLowerCase() === b[lj - 1].toLowerCase()) {
                    inLCS.add(li - 1);
                    li--;
                    lj--;
                  } else if (dp[li - 1][lj] >= dp[li][lj - 1]) {
                    li--;
                  } else {
                    lj--;
                  }
                }
                // LCS에 없는 글자만 빨간색 (= 실제 차이나는 글자)
                let html = "";
                for (let i = 0; i < m; i++) {
                  const esc = escCh(a[i]);
                  html += inLCS.has(i)
                    ? esc
                    : `<span style="color:#DC2626;font-weight:700;">${esc}</span>`;
                }
                return html;
              }
              // diff 없음: 숫자 필드는 formatString이 처리하도록 원본 반환
              if (
                isNumeric &&
                value !== null &&
                value !== undefined &&
                value !== ""
              ) {
                const num =
                  typeof value === "number"
                    ? value
                    : parseFloat(String(value).replace(/,/g, ""));
                if (!isNaN(num) && isFinite(num)) {
                  return num.toLocaleString("ko-KR", {
                    maximumFractionDigits: 6,
                  });
                }
              }
              return String(value ?? "");
            },
            // ⭐ 스타일 함수: 페이지 그룹 하이라이트 (교차 검증 노란 배경 제거 → 텍스트 빨간색으로 충분)
            // 🔧 AUIGrid에서는 styleFunction 사용
            styleFunction: (
              rowIndex,
              columnIndex,
              value,
              headerText,
              item,
              dataField,
            ) => {
              let styleClass = isNumeric ? "right-align" : "left-align";

              // 🔍 [Cross Validation] 불일치 셀 배경 하이라이트
              const diff = item?.differences?.[dataField];
              if (diff && diff.match === false) {
                const primaryVal = String(
                  diff.primary ?? diff.flash ?? value ?? "",
                );
                const secondaryVal = String(diff.secondary ?? diff.pro ?? "");
                if (primaryVal.toLowerCase() !== secondaryVal.toLowerCase()) {
                  styleClass += " cv-diff-cell";
                }
              }

              // 🔧 동적 CSS 클래스(page-group-highlight, page-group-selected-row) 제거
              // AUIGrid 가상 스크롤 셀 재활용 시 CSS 클래스가 화면 위치에 잔류하는 문제
              // AUIGrid 기본 선택 스타일로 대체 (aui-grid-selection-row-bg)

              // 🆕 AI tint 는 별도 AIHSCode column 에서 담당 — HSCode (수출국 참고값) 은 건들지 X

              return styleClass;
            },
            dataType: isNumeric ? "numeric" : "string",
            formatString: isNumeric ? "#,##0.######" : null, // 🔧 소수점 6자리까지 표시 (반올림 방지)
            // ⭐ 푸터 합계 설정
            footer: isNumeric
              ? {
                  operation: "SUM",
                  style: "text-align: right; font-weight: bold; color: #333;",
                  formatString: "#,##0.######", // 🔧 소수점 6자리까지 표시
                }
              : null,
          };
        }),
      ];

      // 🆕 AIHSCode column + AI 신뢰도 dot column — HSCode 바로 뒤에 2개 신규 삽입.
      //   HSCode (수출국 참고값) 은 건들지 않고, AI 분류 결과는 AIHSCode 에만 채움.
      //   사용자가 두 column 을 비교 검토 가능. 다르면 노란 강조.
      if (hasHsCodeColumn) {
        const aiHsCodeCol = {
          dataField: "AIHSCode",
          headerText: "AI HSCode",
          width: 90,
          editable: true,
          editRenderer: { type: "InputEditRenderer" },
          renderer: { type: "TemplateRenderer" },
          style: "left-align",
          headerStyle: "center-align",
          dataType: "string",
          labelFunction: (rowIndex, ci, value, ht, item) => {
            const v = String(value ?? "");
            const sug = item?._aiSuggestion;
            const hsExport = String(item?.[hsCodeFieldKey] || "").trim();
            // suggestion 만 있고 user 미확정 — grey 로 제안 미리보기
            if (!v && sug?.hsk)
              return `<span style="color:#94a3b8">${sug.hsk}</span>`;
            // 수출국 HSCode 와 다르면 노란 강조 (검토 alert)
            if (v && hsExport && v !== hsExport)
              return `<span style="background:#fef9c3;padding:0 2px;border-radius:2px" title="수출국 HSCode (${hsExport}) 와 다름 — 검토 필요">${v}</span>`;
            return v;
          },
          styleFunction: (ri, ci, value, ht, item) => {
            const prov = item?._provenance;
            const sug = item?._aiSuggestion;
            let s = "left-align";
            if (prov === "user_confirmed") s += " rig-tint-user-confirmed";
            else if (prov === "ai_suggested") s += " rig-tint-ai-confirmed";
            else if (sug && !value) s += " rig-tint-suggested";
            return s;
          },
        };
        const aiDotCol = {
          dataField: "_aiHsConfidence",
          headerText: "AI",
          width: 36,
          editable: false,
          style: "center-align",
          headerStyle: "center-align",
          renderer: { type: "TemplateRenderer" },
          labelFunction: (rowIndex, ci, value, ht, item) => {
            const c = item?._aiSuggestion?.confidence;
            if (!c) return "";
            const color =
              c === "high" ? "#16a34a" : c === "medium" ? "#eab308" : "#ef4444";
            const title =
              c === "high" ? "HIGH 신뢰" : c === "medium" ? "MEDIUM" : "LOW";
            // data-ai-dot marker — cellClick 미발화 케이스 대비, document mousedown 으로 우회 잡힘
            return `<span data-ai-dot="${rowIndex}" style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${color};cursor:pointer" title="${title} — 클릭하면 분류 근거"></span>`;
          },
        };
        const hsIdx = horizontalColumns.findIndex((c) =>
          isHsCodeFieldKey(c.dataField),
        );
        if (hsIdx >= 0)
          horizontalColumns.splice(hsIdx + 1, 0, aiHsCodeCol, aiDotCol);
        else horizontalColumns.push(aiHsCodeCol, aiDotCol);
      }

      // ⭐ 데이터 준비 - 🔧 테이블 데이터를 행 단위로 펼침
      let horizontalData = [];

      // 템플릿에 tableFields가 있는지 확인
      const tableFieldsSource = matchedTemplate?.tableFields || [];
      const hasTableFields = tableFieldsSource.length > 0;

      // 🔍 [진단] 데이터 준비 전 상태 확인
      const allItemPageNumbers = [
        ...new Set(
          (allItemsProp || []).map((item) =>
            Number(item.pageNumber ?? item.P ?? 1),
          ),
        ),
      ].sort();
      const prPageNumbers = pageResults.map((p) => Number(p.pageNumber));
      console.log("🔍 [horizontalGrid 진단] 데이터 준비:", {
        checkedPages,
        pageResultsCount: pageResults.length,
        pageResultsPageNumbers: prPageNumbers,
        allItemsPropCount: allItemsProp?.length || 0,
        allItemsUniquePages: allItemPageNumbers,
        hasTableFields,
      });

      // 🔧 FIX: pageResults를 기본으로 사용하되, checkedPages 필터링 적용
      // 핵심: allItemsProp에 데이터가 있는 페이지는 checkedPages와 무관하게 포함
      // (checkedPages 업데이트 타이밍 문제 방지 - extraction 후 [1]→[1,2] 전환 지연)
      let filteredPageResults;
      if (checkedPages && checkedPages.length > 0) {
        // 1단계: checkedPages 기본 필터
        filteredPageResults = pageResults.filter((p) =>
          checkedPages.includes(Number(p.pageNumber)),
        );

        // 🔧 checkedPages 필터링만 사용 (이전 타이밍 이슈 워크어라운드 제거됨)
      } else {
        filteredPageResults = pageResults;
      }

      // 🔧 합성 pageResult: checkedPages에 있지만 pageResults에 없는 페이지 → allItemsProp에서 생성
      if (
        allItemsProp &&
        allItemsProp.length > 0 &&
        checkedPages &&
        checkedPages.length > 0
      ) {
        const existingPageNumbers = new Set(
          filteredPageResults.map((p) => Number(p.pageNumber)),
        );
        // checkedPages에 있는 페이지만 대상으로 함 (미체크 페이지는 제외)
        const missingPages = checkedPages
          .map(Number)
          .filter((p) => !existingPageNumbers.has(p));

        if (missingPages.length > 0) {
          missingPages.forEach((missingPageNum) => {
            const pageItems = allItemsProp.filter((item) => {
              const itemPage = Number(item.pageNumber ?? item.P ?? 1);
              return itemPage === Number(missingPageNum);
            });
            if (pageItems.length > 0) {
              console.log(
                `📄 [horizontalGrid] 페이지 ${missingPageNum} 합성 pageResult 생성 (${pageItems.length}개 items)`,
              );
              filteredPageResults.push({
                pageNumber: Number(missingPageNum),
                _synthetic: true,
              });
            }
          });
          filteredPageResults.sort(
            (a, b) => Number(a.pageNumber) - Number(b.pageNumber),
          );
        }
      }

      // 🔧 FIX: 실제 PDF 페이지 수를 초과하는 phantom pageResults 방어 필터
      // Gemini가 테이블 행 수만큼 pageResults를 생성하는 버그 대응 (예: 1페이지 PDF → 3 pageResults)
      if (
        totalPages &&
        totalPages > 0 &&
        filteredPageResults.length > totalPages
      ) {
        const before = filteredPageResults.length;
        filteredPageResults = filteredPageResults.filter(
          (p) =>
            Number(p.pageNumber) >= 1 && Number(p.pageNumber) <= totalPages,
        );
        if (filteredPageResults.length < before) {
          console.warn(
            `⚠️ [horizontalGrid] Phantom pageResults 제거: ${before} → ${filteredPageResults.length} (totalPages=${totalPages})`,
          );
        }
      }

      console.log(
        "📄 [horizontalGrid] 최종 filteredPageResults:",
        filteredPageResults.map((p) => p.pageNumber),
      );

      filteredPageResults.forEach((page, pageIdx) => {
        const flashData = page.flashData || {};
        const proData = page.proData || {};
        // 🔧 fallback 순서: extractedData → 1차(flashData) → 2차(proData)
        // 이전엔 proData 가 flashData 보다 먼저라 2차 모델 결과가 1차 자리를 덮어쓰는 버그
        let extractedData = page.extractedData || flashData || proData;

        // 🔧 FIX: extractedData가 비어있으면 page 자체를 사용 (단일 필드 템플릿 호환)
        // 백엔드가 pageResults[].CBM, pageResults[].길 이 형태로 직접 필드를 반환하는 경우
        if (!extractedData || Object.keys(extractedData).length === 0) {
          // page에서 메타데이터 키를 제외한 실제 데이터가 있는지 확인
          const metaKeys = [
            "pageNumber",
            "P",
            "p",
            "page",
            "flashData",
            "proData",
            "extractedData",
            "documentType",
            "differences",
          ];
          const dataKeys = Object.keys(page).filter(
            (k) => !metaKeys.includes(k),
          );
          if (dataKeys.length > 0) {
            console.log(
              "📄 [horizontalGrid] page 객체 자체를 extractedData로 사용:",
              page.pageNumber,
            );
            extractedData = page;
          }
        }

        // 🔧 테이블 데이터가 있으면 행 단위로 펼침
        if (hasTableFields) {
          // 첫 번째 테이블 기준으로 행 생성
          const firstTable = tableFieldsSource[0];
          const tableName = firstTable.tableName;
          const currentPageNum = Number(page.pageNumber);

          // 🔧 FIX: allItemsProp가 있으면 PRIMARY source로 사용 (정확한 per-page 데이터)
          // 백엔드가 모든 items를 단일 pageResult에 넣는 경우, extractedData[tableName]은
          // 모든 페이지 데이터를 포함하지만 pageNumber가 부정확할 수 있음.
          // allItemsProp(top-level items)은 정확한 pageNumber를 가지므로 우선 사용.
          let tableData = [];
          if (
            allItemsProp &&
            Array.isArray(allItemsProp) &&
            allItemsProp.length > 0
          ) {
            // allItemsProp에서 현재 페이지 번호에 해당하는 items만 사용
            tableData = allItemsProp.filter((item) => {
              const itemPage = Number(item.pageNumber ?? item.P ?? 1);
              return itemPage === currentPageNum;
            });
            if (tableData.length > 0) {
              console.log(
                `📦 [horizontalGrid] allItemsProp PRIMARY 사용 - 페이지 ${currentPageNum}: ${tableData.length}개`,
              );
            }
          }

          // allItemsProp에 해당 페이지 데이터가 없으면 extractedData[tableName] 폴백
          if (tableData.length === 0) {
            tableData = extractedData[tableName] || [];
            if (tableData.length === 0 && extractedData.items) {
              console.log(
                `📦 [horizontalGrid] '${tableName}' 없음, 'items' 폴백 사용`,
              );
              tableData = extractedData.items;
            }
            // 폴백 데이터는 현재 페이지로 필터링
            if (Array.isArray(tableData) && tableData.length > 0) {
              const beforeCount = tableData.length;
              tableData = tableData.filter((item) => {
                const itemPage =
                  item.pageNumber ?? item.P ?? item.p ?? page.pageNumber ?? 1;
                const pageNum =
                  typeof itemPage === "string"
                    ? parseInt(itemPage, 10)
                    : Number(itemPage);
                return pageNum === currentPageNum;
              });
              if (beforeCount !== tableData.length) {
                console.log("📄 [horizontalGrid] 폴백 데이터 페이지 필터링:", {
                  tableName,
                  currentPageNum,
                  before: beforeCount,
                  after: tableData.length,
                });
              }
            }
          }

          if (Array.isArray(tableData) && tableData.length > 0) {
            // 테이블 행마다 그리드 행 생성
            tableData.forEach((item, itemIdx) => {
              // 🔧 pageNumber로 통일
              const resolvedPageNumber =
                item.pageNumber ?? page.pageNumber ?? 1;

              // ⭐ 교차 검증: _itemDiffs에서 현재 행의 차이점 추출
              const pageDiffs = page.differences || {};
              const itemDiffs = pageDiffs._itemDiffs || [];
              const rowItemDiff = itemDiffs.find((d) => d.rowIndex === itemIdx);
              // 행별 differences = 헤더 필드 차이 + 아이템 행 차이 + 🔧 item 직접 차이 병합
              const rowDifferences = {
                ...pageDiffs, // 헤더 필드 차이
                ...(rowItemDiff
                  ? Object.fromEntries(
                      Object.entries(rowItemDiff.diffs).map(([k, v]) => [
                        k,
                        { ...v, match: false },
                      ]),
                    )
                  : {}),
                ...(item.differences || {}), // 🔧 backend에서 직접 첨부한 differences (gemini-extract-pdf-native)
              };

              const rowData = {
                // 🔧 pageNumber: 페이지 번호 (그리드 P 열에 표시)
                pageNumber:
                  typeof resolvedPageNumber === "string"
                    ? parseInt(resolvedPageNumber, 10)
                    : Number(resolvedPageNumber),
                // 🔧 idx: 페이지 내 순서 (그리드 # 열에 표시)
                idx: item.idx ?? itemIdx + 1,
                documentType: page.documentType || "",
                differences: rowDifferences,
                _hasDiff:
                  !!rowItemDiff ||
                  Object.keys(item.differences || {}).length > 0, // ⭐ 플래그 확장
                _rowIndex: itemIdx + 1, // 행 번호 표시용
              };

              // 🔧 필드별 값 설정 (단일/테이블/합성 필드 구분)
              filteredFields.forEach((fieldInfo) => {
                const field = fieldInfo.key;

                if (fieldInfo.fieldType === "computed" && fieldInfo.formula) {
                  // 🔧 합성 필드: 현재 행(item)의 데이터로 formula 계산
                  let computedValue = fieldInfo.formula;
                  const pattern = /\{([^}]+)\}/g;

                  computedValue = computedValue.replace(
                    pattern,
                    (_, formulaKey) => {
                      // 1. 현재 item에서 값 찾기 (테이블 컬럼 우선)
                      let val = "";
                      const itemKey = Object.keys(item).find(
                        (k) => k.toLowerCase() === formulaKey.toLowerCase(),
                      );
                      if (itemKey) {
                        val = item[itemKey];
                      } else {
                        // 2. 단일 필드(extractedData)에서 찾기
                        const dataKey = Object.keys(extractedData).find(
                          (k) => k.toLowerCase() === formulaKey.toLowerCase(),
                        );
                        if (dataKey) val = extractedData[dataKey];
                      }

                      // 숫자 콤마 제거
                      if (
                        typeof val === "string" &&
                        /^-?[\d,]+(\.\d+)?$/.test(val)
                      ) {
                        return val.replace(/,/g, "");
                      }
                      return val || 0;
                    },
                  );

                  // 수식 계산 시도
                  try {
                    if (/^[\d\s+\-*/().]+$/.test(computedValue)) {
                      computedValue = eval(computedValue);
                    }
                  } catch (e) {
                    // 계산 실패 시 원본 유지
                  }

                  rowData[field] = computedValue;
                } else if (fieldInfo.fieldType === "table") {
                  // 테이블 컬럼: 해당 아이템의 값 사용
                  const val =
                    item[field] ||
                    Object.keys(item).find(
                      (k) => k.toLowerCase() === field.toLowerCase(),
                    )
                      ? item[
                          Object.keys(item).find(
                            (k) => k.toLowerCase() === field.toLowerCase(),
                          )
                        ]
                      : "";
                  rowData[field] = val || "";
                } else {
                  // 단일 필드: 추출 데이터에서 가져오기
                  rowData[field] =
                    extractedData[field] ||
                    proData[field] ||
                    flashData[field] ||
                    "";
                }
              });

              horizontalData.push(rowData);
            });
          } else {
            // 테이블 데이터 없으면 단일 행
            const rowData = {
              pageNumber: page.pageNumber,
              documentType: page.documentType || "",
              differences: page.differences || {},
              _rowIndex: 1,
            };

            // 🔧 필드별 값 설정 (합성 필드 포함)
            filteredFields.forEach((fieldInfo) => {
              const field = fieldInfo.key;

              if (fieldInfo.fieldType === "computed" && fieldInfo.formula) {
                // 합성 필드: extractedData로 formula 계산 (테이블 데이터 없음)
                let computedValue = fieldInfo.formula;
                const pattern = /\{([^}]+)\}/g;

                computedValue = computedValue.replace(
                  pattern,
                  (_, formulaKey) => {
                    const dataKey = Object.keys(extractedData).find(
                      (k) => k.toLowerCase() === formulaKey.toLowerCase(),
                    );
                    let val = dataKey ? extractedData[dataKey] : "";
                    if (
                      typeof val === "string" &&
                      /^-?[\d,]+(\.\d+)?$/.test(val)
                    ) {
                      return val.replace(/,/g, "");
                    }
                    return val || 0;
                  },
                );

                try {
                  if (/^[\d\s+\-*/().]+$/.test(computedValue)) {
                    computedValue = eval(computedValue);
                  }
                } catch (e) {}

                rowData[field] = computedValue;
              } else {
                rowData[field] =
                  extractedData[field] ||
                  proData[field] ||
                  flashData[field] ||
                  "";
              }
            });

            horizontalData.push(rowData);
          }
        } else {
          // 테이블 필드 없음 - 기존 로직 (페이지당 1행)
          const rowData = {
            pageNumber: page.pageNumber,
            documentType: page.documentType || "",
            differences: page.differences || {},
          };

          // 🔧 필드별 값 설정 (합성 필드 포함)
          filteredFields.forEach((fieldInfo) => {
            const field = fieldInfo.key;

            if (fieldInfo.fieldType === "computed" && fieldInfo.formula) {
              // 합성 필드: extractedData로 formula 계산
              let computedValue = fieldInfo.formula;
              const pattern = /\{([^}]+)\}/g;

              computedValue = computedValue.replace(
                pattern,
                (_, formulaKey) => {
                  const dataKey = Object.keys(extractedData).find(
                    (k) => k.toLowerCase() === formulaKey.toLowerCase(),
                  );
                  let val = dataKey ? extractedData[dataKey] : "";
                  if (
                    typeof val === "string" &&
                    /^-?[\d,]+(\.\d+)?$/.test(val)
                  ) {
                    return val.replace(/,/g, "");
                  }
                  return val || 0;
                },
              );

              try {
                if (/^[\d\s+\-*/().]+$/.test(computedValue)) {
                  computedValue = eval(computedValue);
                }
              } catch (e) {}

              rowData[field] = computedValue;
            } else {
              rowData[field] =
                extractedData[field] ||
                proData[field] ||
                flashData[field] ||
                "";
            }
          });

          horizontalData.push(rowData);
        }
      });

      // 🔧 빈 값은 빈 문자열("")로 유지 (진짜 빈 셀로 표시)

      // 🔧 컴팩트 그리드 스타일 적용
      const horizontalGridProps = {
        headerHeight: 32,
        rowHeight: 28,
        // 🔧 fillColumnSizeMode 제거 - 모든 컬럼이 자신의 너비대로 표시되도록
        // fillColumnSizeMode: true, // 이 옵션이 Description 컬럼을 전체 너비로 확장시켜 다른 컬럼 숨김
        hScrollPolicy: "auto", // 🔧 가로 스크롤바: 컬럼이 넘치면 자동 표시
        enableFilter: false,
        enableSorting: false, // 🔧 헤더 클릭 시 열 전체 선택으로 변경 (정렬 비활성화)
        showRowNumColumn: true, // 행 번호 표시
        editable: true,
        // 🆕 열 드래그 이동 활성화
        enableMovingColumn: true,
        enableColumnResize: true,
        // ⭐ 셀 블록 선택 모드: 마우스 드래그로 셀 범위 선택 가능
        selectionMode: "multipleCells",
        selectionStyle: {
          backgroundColor: "#DBEAFE", // 연한 파란색 (선택된 셀)
          color: "#1A1B1E", // 로고 색상 텍스트
        },
        // ⭐ 푸터 활성화
        showFooter: true,
        footerPosition: "bottom",
        footerHeight: 26, // 선택 합계 표시용 높이 증가
        // 🆕 컨텍스트 메뉴 활성화 (기본 메뉴 = 셀 관련)
        useContextMenu: true,
        contextMenuItems: [
          {
            label: "행 추가",
            callback: function (event) {
              const position = window.prompt(
                "행 추가 위치:\n1: 위에 추가\n2: 아래에 추가",
                "2",
              );
              if (position === "1") {
                window.AUIGrid.addRow(grid, {}, "selectionUp");
                console.log("✅ [ContextMenu] 행 위에 추가");
              } else if (position === "2") {
                window.AUIGrid.addRow(grid, {}, "selectionDown");
                console.log("✅ [ContextMenu] 행 아래에 추가");
              }
            },
          },
          {
            label: "행 삭제",
            callback: function (event) {
              const selectedItems = window.AUIGrid.getSelectedItems(grid);

              // 선택된 행들의 인덱스 추출 (중복 제거 및 내림차순 정렬)
              const selectedRowIndices =
                selectedItems && selectedItems.length > 0
                  ? Array.from(
                      new Set(selectedItems.map((item) => item.rowIndex)),
                    ).sort((a, b) => b - a)
                  : [];

              // 1. 다중 선택된 경우 (우클릭한 행이 선택 범위에 포함되어 있거나, 다른 행들이 선택된 경우)
              if (selectedRowIndices.length > 1) {
                if (
                  window.confirm(
                    `선택된 ${selectedRowIndices.length}개 행을 삭제하시겠습니까?`,
                  )
                ) {
                  selectedRowIndices.forEach((idx) => {
                    window.AUIGrid.removeRow(grid, idx);
                  });
                  console.log(
                    "✅ [ContextMenu] 다중 행 삭제:",
                    selectedRowIndices.length,
                    "개",
                  );
                }
              }
              // 2. 단일 선택 또는 선택 없음 (우클릭한 행만 삭제)
              else {
                const { rowIndex } = event;
                if (rowIndex < 0) {
                  alert("삭제할 행을 선택하세요.");
                  return;
                }
                if (
                  window.confirm(`${rowIndex + 1}번 행을 삭제하시겠습니까?`)
                ) {
                  window.AUIGrid.removeRow(grid, rowIndex);
                  console.log("✅ [ContextMenu] 단일 행 삭제:", rowIndex);
                }
              }
            },
          },
          {
            label: "열 삭제",
            callback: function (event) {
              const { dataField, columnIndex } = event;
              if (!dataField || columnIndex < 0) {
                alert("삭제할 열을 선택하세요.");
                return;
              }
              if (window.confirm(`'${dataField}' 열을 삭제하시겠습니까?`)) {
                window.AUIGrid.removeColumn(grid, columnIndex);
                console.log("✅ [ContextMenu] 열 삭제:", dataField);
              }
            },
          },
          { label: "-" },
          {
            label: "현재 셀을 위 셀에 병합",
            callback: function (event) {
              const { rowIndex, dataField } = event;
              if (rowIndex <= 0) {
                alert("위로 병합할 수 없습니다. (첫 번째 행)");
                return;
              }
              const gridData = window.AUIGrid.getGridData(grid);
              const currentValue = gridData[rowIndex]?.[dataField] || "";
              const aboveValue = gridData[rowIndex - 1]?.[dataField] || "";
              const mergedValue = aboveValue
                ? `${aboveValue}, ${currentValue}`
                : currentValue;

              // Undo 저장
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "merge_up",
                  beforeValues: [
                    {
                      rowIndex: rowIndex - 1,
                      field: dataField,
                      value: aboveValue,
                    },
                    {
                      rowIndex: rowIndex,
                      field: dataField,
                      value: currentValue,
                    },
                  ],
                },
              ]);

              window.AUIGrid.updateRow(
                grid,
                { [dataField]: mergedValue },
                rowIndex - 1,
              );
              window.AUIGrid.updateRow(grid, { [dataField]: "" }, rowIndex);
              console.log("✅ [ContextMenu] 위로 병합:", dataField);
            },
          },
          {
            label: "현재 셀을 왼쪽 셀에 병합",
            callback: function (event) {
              const { rowIndex, dataField, columnIndex } = event;
              const columnLayout = window.AUIGrid.getColumnLayout(grid);
              if (columnIndex <= 0) {
                alert("왼쪽으로 병합할 수 없습니다. (첫 번째 열)");
                return;
              }
              const leftField = columnLayout[columnIndex - 1]?.dataField;
              if (!leftField) return;

              const gridData = window.AUIGrid.getGridData(grid);
              const currentValue = gridData[rowIndex]?.[dataField] || "";
              const leftValue = gridData[rowIndex]?.[leftField] || "";
              const mergedValue = leftValue
                ? `${leftValue}, ${currentValue}`
                : currentValue;

              // Undo 저장
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "merge_left",
                  beforeValues: [
                    { rowIndex, field: leftField, value: leftValue },
                    { rowIndex, field: dataField, value: currentValue },
                  ],
                },
              ]);

              window.AUIGrid.updateRow(
                grid,
                { [leftField]: mergedValue, [dataField]: "" },
                rowIndex,
              );
              console.log(
                "✅ [ContextMenu] 왼쪽으로 병합:",
                leftField,
                "<-",
                dataField,
              );
            },
          },
          { label: "-" },
          {
            label: "🔗 열 전체를 왼쪽 열에 병합",
            callback: function (event) {
              const { dataField, columnIndex } = event;
              const columnLayout = window.AUIGrid.getColumnLayout(grid);
              if (columnIndex <= 0) {
                alert("왼쪽으로 병합할 수 없습니다. (첫 번째 열)");
                return;
              }
              const leftField = columnLayout[columnIndex - 1]?.dataField;
              if (!leftField) return;

              const gridData = window.AUIGrid.getGridData(grid);

              if (
                !window.confirm(
                  `'${dataField}' 열 전체를 '${leftField}' 열에 병합하시겠습니까?`,
                )
              ) {
                return;
              }

              // Undo 저장 (열 전체 병합)
              const beforeValues = [];
              gridData.forEach((row, idx) => {
                const currentValue = row[dataField] || "";
                const leftValue = row[leftField] || "";
                if (currentValue) {
                  beforeValues.push({
                    rowIndex: idx,
                    targetValue: leftValue,
                    sourceValue: currentValue,
                  });
                }
              });

              // 열 전체 병합 실행
              beforeValues.forEach(({ rowIndex, targetValue, sourceValue }) => {
                const mergedValue = targetValue
                  ? `${targetValue}, ${sourceValue}`
                  : sourceValue;
                window.AUIGrid.updateRow(
                  grid,
                  { [leftField]: mergedValue, [dataField]: "" },
                  rowIndex,
                );
              });

              if (beforeValues.length > 0) {
                setUndoStack((prev) => [
                  ...prev,
                  {
                    id: Date.now(),
                    type: "merge_column_left",
                    sourceField: dataField,
                    targetField: leftField,
                    beforeValues,
                  },
                ]);
              }
              console.log(
                "✅ [ContextMenu] 열 전체 왼쪽 병합:",
                leftField,
                "<-",
                dataField,
                "(",
                beforeValues.length,
                "행)",
              );
            },
          },
        ],
      };

      // 데이터 설정 (🔍 불일치 필터 적용)
      const gridDataToSet = showMismatchOnly
        ? horizontalData.filter(
            (row) =>
              row.differences &&
              Object.values(row.differences).some((d) => d.match === false),
          )
        : horizontalData;

      // 🔧 FIX: 데이터만 변경된 경우 → setGridData로 업데이트 (깜박임 방지)
      if (isDataOnlyUpdate && gridRef.current) {
        try {
          window.AUIGrid.setGridData(gridRef.current, gridDataToSet);
          // 🔧 안전 검증: setGridData 후 실제 행 수 확인
          const actualRowCount =
            window.AUIGrid.getGridData(gridRef.current)?.length || 0;
          if (gridDataToSet.length > 0 && actualRowCount === 0) {
            console.warn(
              "⚠️ [horizontalGrid] setGridData 후 행 0개 — 전체 재생성으로 전환",
            );
            prevGridSignatureRef.current = null;
            // fall through to full recreation below
          } else {
            const pageDistribution = gridDataToSet.reduce((acc, row) => {
              const pn = row.pageNumber || "unknown";
              acc[pn] = (acc[pn] || 0) + 1;
              return acc;
            }, {});
            console.log(
              "📊 [horizontalGrid] 데이터만 업데이트 완료 (깜박임 없음):",
              {
                totalRows: gridDataToSet.length,
                pageDistribution,
                checkedPages,
              },
            );
            // 🔧 데이터 업데이트 후에도 컬럼 너비 자동 맞춤 (값 기준)
            setTimeout(() => {
              try {
                if (
                  gridRef.current &&
                  window.AUIGrid.isCreated(gridRef.current)
                ) {
                  autoFitAllColumns(gridRef.current);
                  window.AUIGrid.resize(gridRef.current);
                }
              } catch (e) {}
            }, 150);
            return; // 전체 재생성 건너뛰기
          }
        } catch (e) {
          console.warn(
            "📊 [horizontalGrid] 데이터 업데이트 실패, 전체 재생성으로 전환:",
            e,
          );
          prevGridSignatureRef.current = null; // 다음번에 전체 재생성하도록
        }
        // 🔧 여기로 올 경우: 그리드 파괴 후 전체 재생성
        if (gridRef.current) {
          try {
            window.AUIGrid.destroy(gridRef.current);
          } catch (e) {}
          gridRef.current = null;
        }
      }

      const grid = window.AUIGrid.create(
        `#${gridId}`,
        horizontalColumns,
        horizontalGridProps,
      );
      gridRef.current = grid;
      gridDomRef.current = document.getElementById(gridId); // 🔧 FIX: DOM element 추적

      window.AUIGrid.setGridData(grid, gridDataToSet);

      // 🆕 cellClick — AI dot 또는 _aiSuggestion 있는 HSCode cell 클릭 → evidence modal
      if (hasHsCodeColumn) {
        const readNormField = (item, candidates) => {
          if (!item) return "";
          for (const k of Object.keys(item)) {
            const norm = String(k)
              .toLowerCase()
              .replace(/[\s_-]+/g, "");
            if (candidates.includes(norm)) {
              const v = item[k];
              if (v !== null && v !== undefined && v !== "") return String(v);
            }
          }
          return "";
        };
        window.AUIGrid.bind(grid, "cellClick", function (event) {
          // AI dot cell 만 modal — AIHSCode 셀은 직접 편집 가능 (modal 안 열림),
          // HSCode 셀은 수출국 참고값 (편집/touch 안 함)
          if (event?.dataField !== "_aiHsConfidence") return;
          const desc = readNormField(event.item, [
            "description",
            "desc",
            "품명",
            "goodsdescription",
            "descriptionofgoods",
            "item",
            "artigo",
          ]);
          const comp = readNormField(event.item, [
            "composition",
            "재질",
            "material",
          ]);
          setHsEvidenceModal({
            rowIndex: event.rowIndex,
            description: desc,
            composition: comp,
          });
        });
      }

      // 열 경계 더블클릭 → 자동 너비 조절
      setupColumnAutoFit(grid, `#${gridId}`);

      // ✏️ 엑셀 스타일 Fill Handle (채우기 핸들)
      setupFillHandle(grid, `#${gridId}`, {
        onFill: (undoEntries) => {
          // 메인 undo 스택에 push → Ctrl+Z로 되돌리기 가능
          setUndoStack((prev) => [
            ...prev,
            {
              type: "fill_handle",
              entries: undoEntries,
              label: `채우기 핸들: ${undoEntries.length}개 셀`,
            },
          ]);
          setUndoToastVisible(true);
        },
      });

      // 🔍 [진단] 그리드에 설정된 데이터 확인
      const pageDistribution = gridDataToSet.reduce((acc, row) => {
        const pn = row.pageNumber || "unknown";
        acc[pn] = (acc[pn] || 0) + 1;
        return acc;
      }, {});
      console.log("📊 [horizontalGrid] 그리드 전체 생성 완료:", {
        totalRows: gridDataToSet.length,
        pageDistribution,
        checkedPages,
      });

      // ⭐ 푸터 합계 설정 - setFooter API 사용
      const footerLayout = [];
      allFields.forEach((field) => {
        // 숫자형 필드 감지
        const isNumericField =
          /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|순중량|총중량|부피|합계|세금|비용|갯수|개수|Unit.*Price|Net.*Weight|Gross.*Weight/i.test(
            field,
          );
        if (isNumericField) {
          footerLayout.push({
            dataField: field,
            positionField: field,
            operation: "SUM",
            formatString: "#,##0.##",
            style: "text-align: right; font-weight: bold; color: #333;",
          });
        }
      });
      if (footerLayout.length > 0) {
        window.AUIGrid.setFooter(grid, footerLayout);
        console.log(
          "📊 [Footer] 합계 설정:",
          footerLayout.map((f) => f.dataField),
        );
      }

      // 🔧 컬럼 너비 자동 맞춤 - 데이터 내용 기준 최적화 (Shift+더블클릭과 동일)
      const doResize = () => {
        try {
          if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
            window.AUIGrid.resize(gridRef.current);
          }
        } catch (e) {}
      };
      const doAutoFit = () => {
        try {
          if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
            autoFitAllColumns(gridRef.current);
            window.AUIGrid.resize(gridRef.current);
            console.log(
              "📐 [Grid] 초기 로드 → 전체 컬럼 데이터 기반 자동 너비 적용",
            );
          }
        } catch (e) {}
      };
      // 다단계: 먼저 resize로 그리드 렌더 → 그다음 데이터 기반 autoFit
      setTimeout(doResize, 50);
      setTimeout(doAutoFit, 200);
      setTimeout(doAutoFit, 500);

      // 🔧 ResizeObserver: 컨테이너 크기 변경 시 자동 resize (간헐적 높이 0 문제 해결)
      if (resizeObserverRef.current) {
        try {
          resizeObserverRef.current.disconnect();
        } catch (e) {}
      }
      const resizeTarget = document.getElementById(gridId);
      if (resizeTarget && typeof ResizeObserver !== "undefined") {
        let lastHeight = resizeTarget.clientHeight;
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const newHeight = entry.contentRect.height;
            if (newHeight > 10 && Math.abs(newHeight - lastHeight) > 5) {
              lastHeight = newHeight;
              doResize();
              console.log(
                `📐 [ResizeObserver] 컨테이너 크기 변경 감지: ${newHeight}px → 그리드 resize`,
              );
            }
          }
        });
        resizeObserverRef.current.observe(resizeTarget);
      }

      // 셀 편집 완료 이벤트 + Undo 저장
      window.AUIGrid.bind(grid, "cellEditEnd", function (event) {
        if (event.oldValue !== event.value) {
          setUndoStack((prev) => [
            ...prev,
            {
              id: Date.now(),
              type: "cell_update",
              beforeValues: [
                {
                  rowIndex: event.rowIndex,
                  field: event.dataField,
                  value: event.oldValue,
                },
              ],
            },
          ]);
          console.log(
            "📝 [HorizontalGrid] 셀 편집:",
            event.dataField,
            event.oldValue,
            "->",
            event.value,
          );
        }
      });

      // Ctrl+Z는 별도 useEffect에서 처리 (stale closure 방지)

      // ╔══════════════════════════════════════════════════════════════════════════╗
      // ║  🔒 필수 기능: Google Sheets 스타일 열 선택 (Column Selection)           ║
      // ║                                                                          ║
      // ║  ⚠️ 이 코드를 수정할 때 반드시 아래 사항을 확인하세요:                   ║
      // ║                                                                          ║
      // ║  1. setSelectionBlock 사용 금지: AUIGrid 네이티브 선택은 녹색/파란       ║
      // ║     테두리를 생성하므로 사용하지 않음. 순수 CSS로만 하이라이트.          ║
      // ║                                                                          ║
      // ║  2. CSS 클래스(.column-selected-cell) 직접 추가 금지:                    ║
      // ║     AUIGrid는 hover 시 셀 DOM을 재생성하여 클래스가 제거됨.             ║
      // ║     반드시 <style> 태그 + nth-child 구조적 셀렉터 사용.                 ║
      // ║                                                                          ║
      // ║  3. nth-child 오프셋 하드코딩 금지:                                      ║
      // ║     getCellElementByIndex(grid, 0, colIdx)로 실제 td 위치를             ║
      // ║     자동 감지한 후 nth-child 값을 결정.                                  ║
      // ║                                                                          ║
      // ║  관련 파일:                                                               ║
      // ║  - PageBasedGridPanel.jsx: L228-229 (useRef 선언)                        ║
      // ║  - PageBasedGridPanel.jsx: 이 블록 (이벤트 핸들러)                       ║
      // ║  - index.css: .selected-column-header (헤더 하이라이트)                  ║
      // ║  - index.css: .aui-grid-selection-* 오버라이드 (녹색→파란색)             ║
      // ╚══════════════════════════════════════════════════════════════════════════╝
      // Ctrl+Click: 개별 열 추가/토글, Shift+Click: 범위 선택
      let isColumnSelecting = false; // 가드 플래그
      const COLUMN_STYLE_ID = "column-selection-style";

      // 열 하이라이트 제거 (헤더 CSS + <style> 태그)
      const clearColumnHighlight = () => {
        document
          .querySelectorAll(".aui-grid-header-renderer.selected-column-header")
          .forEach((el) => {
            el.classList.remove("selected-column-header");
          });
        const existingStyle = document.getElementById(COLUMN_STYLE_ID);
        if (existingStyle) existingStyle.remove();
      };

      // 열 전체 하이라이트 적용 (헤더 CSS + <style> 태그 주입)
      // getCellElementByIndex로 실제 td 위치를 자동 감지 → nth-child 오프셋 정확
      const applyColumnHighlight = (columns) => {
        clearColumnHighlight();
        const gridEl = document.getElementById("horizontal_grid");
        if (!gridEl) return;

        // 1. 헤더 하이라이트
        const headerCells = gridEl.querySelectorAll(
          ".aui-grid-header-renderer",
        );
        columns.forEach((colIdx) => {
          if (headerCells[colIdx]) {
            headerCells[colIdx].classList.add("selected-column-header");
          }
        });

        // 2. 바디 셀 하이라이트: getCellElementByIndex로 nth-child 위치 자동 감지
        //    → <style> 태그 주입 (hover 시 셀 재생성에도 유지됨)
        const rowCount = window.AUIGrid.getRowCount(grid);
        if (columns.length > 0 && rowCount > 0) {
          const nthChildPositions = [];
          columns.forEach((colIdx) => {
            try {
              const cellEl = window.AUIGrid.getCellElementByIndex(
                grid,
                0,
                colIdx,
              );
              if (cellEl && cellEl.parentElement) {
                // 실제 <td>의 형제들 중 몇 번째인지 계산
                const siblings = cellEl.parentElement.children;
                for (let i = 0; i < siblings.length; i++) {
                  if (siblings[i] === cellEl) {
                    nthChildPositions.push(i + 1); // nth-child는 1-based
                    break;
                  }
                }
              }
            } catch (e) {
              /* 감지 실패 시 무시 */
            }
          });

          if (nthChildPositions.length > 0) {
            const style = document.createElement("style");
            style.id = COLUMN_STYLE_ID;
            // .aui-grid-body-panel 한정으로 적용하여 헤더/푸터 영향 없음
            const selectors = nthChildPositions.map(
              (n) =>
                `#horizontal_grid .aui-grid-body-panel .aui-grid-table td:nth-child(${n})`,
            );
            style.textContent = `${selectors.join(",\n")} {
  background-color: #DBEAFE !important;
}`;
            document.head.appendChild(style);
          }
        }
      };

      window.AUIGrid.bind(grid, "headerClick", function (e) {
        const columnIndex = e.columnIndex;
        const dataField = e.dataField;
        if (!dataField || dataField === "_rownum") return false;

        const rowCount = window.AUIGrid.getRowCount(grid);
        if (rowCount <= 0) return false;

        isColumnSelecting = true;

        if (e.orgEvent?.shiftKey && lastClickedColumnRef.current >= 0) {
          // Shift+Click: 범위 선택
          const start = Math.min(lastClickedColumnRef.current, columnIndex);
          const end = Math.max(lastClickedColumnRef.current, columnIndex);
          selectedColumnsRef.current = [];
          for (let i = start; i <= end; i++) {
            selectedColumnsRef.current.push(i);
          }
          applyColumnHighlight(selectedColumnsRef.current);
          window.dispatchEvent(
            new CustomEvent("columnSelectionChanged", {
              detail: { selectedIndices: [...selectedColumnsRef.current] },
            }),
          );
          console.log(
            "📎 [HorizontalGrid] Shift+클릭 범위 선택:",
            start,
            "~",
            end,
          );
        } else if (e.orgEvent?.ctrlKey || e.orgEvent?.metaKey) {
          // Ctrl+Click: 개별 열 토글
          const idx = selectedColumnsRef.current.indexOf(columnIndex);
          if (idx >= 0) {
            selectedColumnsRef.current.splice(idx, 1);
          } else {
            selectedColumnsRef.current.push(columnIndex);
          }
          applyColumnHighlight(selectedColumnsRef.current);
          window.dispatchEvent(
            new CustomEvent("columnSelectionChanged", {
              detail: { selectedIndices: [...selectedColumnsRef.current] },
            }),
          );
          console.log(
            "📌 [HorizontalGrid] Ctrl+클릭 다중 선택:",
            selectedColumnsRef.current,
          );
        } else {
          // 일반 클릭: 단일 열 선택
          selectedColumnsRef.current = [columnIndex];
          applyColumnHighlight([columnIndex]);
          window.dispatchEvent(
            new CustomEvent("columnSelectionChanged", {
              detail: { selectedIndices: [columnIndex] },
            }),
          );
          console.log(
            "📐 [HorizontalGrid] 헤더 클릭 - 열 전체 선택:",
            dataField,
          );
        }

        lastClickedColumnRef.current = columnIndex;
        setTimeout(() => {
          isColumnSelecting = false;
        }, 50);
        // enableSorting: false 이미 정렬 방지 → return false 제거 (AUIGrid 내장 더블클릭 자동 너비 허용)
      });

      // 그리드 본문 클릭 시 하이라이트 해제
      // DOM-level mousedown 사용: AUIGrid cellClick보다 신뢰성 높음
      window.AUIGrid.bind(grid, "cellClick", function () {
        if (isColumnSelecting) return;
        if (selectedColumnsRef.current.length > 0) {
          selectedColumnsRef.current = [];
          lastClickedColumnRef.current = -1;
          clearColumnHighlight();
          window.dispatchEvent(
            new CustomEvent("columnSelectionChanged", {
              detail: { selectedIndices: [] },
            }),
          );
          console.log("📐 [HorizontalGrid] cellClick → 열 선택 해제");
        }
      });

      // 🔒 보조 해제: 그리드 DOM 클릭으로도 해제 (cellClick 미발화 대비)
      const gridEl = document.getElementById("horizontal_grid");
      if (gridEl) {
        gridEl.addEventListener("mousedown", (e) => {
          if (isColumnSelecting) return;
          // 헤더 클릭이면 무시 (headerClick에서 처리)
          const isHeaderClick = e.target.closest(
            ".aui-grid-header-renderer, .aui-grid-header-panel",
          );
          if (isHeaderClick) return;
          if (selectedColumnsRef.current.length > 0) {
            selectedColumnsRef.current = [];
            lastClickedColumnRef.current = -1;
            clearColumnHighlight();
            window.dispatchEvent(
              new CustomEvent("columnSelectionChanged", {
                detail: { selectedIndices: [] },
              }),
            );
            console.log("📐 [HorizontalGrid] mousedown → 열 선택 해제 (보조)");
          }
        });
      }

      // 🆕 헤더 컨텍스트 메뉴 설정 (열 관련 메뉴)
      const headerContextMenuItems = [
        { label: "🔗 선택 열 병합...", callback: "mergeColumns" },
        { label: "🤖 AI로 열 변환...", callback: "mergeColumnsPrompt" },
        { label: "-" },
        { label: "열 복사", callback: "copyColumn" },
        { label: "열 추가", callback: "addColumn" },
        { label: "열 삭제", callback: "deleteColumn" },
        { label: "-" },
        { label: "수량×단가 금액 열 추가", callback: "addAmountColumn" },
        { label: "수량/금액으로 단가 열 추가", callback: "addUnitPriceColumn" },
      ];

      // 🆕 셀 컨텍스트 메뉴 설정 (행 관련 메뉴)
      const cellContextMenuItems = [
        { label: "행 삽입", callback: "insertRow" },
        { label: "행 추가", callback: "addRow" },
        { label: "-" },
        { label: "선택된 행 삭제", callback: "deleteSelectedRows" },
        { label: "-" },
        { label: "선택된 열 위로 병합", callback: "mergeUp" },
        { label: "삭제", callback: "clearCell" },
        { label: "선택된 열 왼쪽으로 병합", callback: "mergeLeft" },
      ];

      // 🆕 컨텍스트 메뉴 이벤트 핸들러 (레거시 패턴 적용 + Benchmarking 고도화)
      window.AUIGrid.bind(grid, "contextMenu", function (event) {
        const { dataField, rowIndex, columnIndex, target } = event;
        console.log("📋 [ContextMenu] event:", {
          target,
          dataField,
          rowIndex,
          columnIndex,
        });

        // 뷰포트 경계 처리 공통 함수
        const getMenuPosition = (pageX, pageY, menuWidth, menuHeight) => {
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          let posX = pageX;
          let posY = pageY;

          if (posX + menuWidth > viewportWidth)
            posX = viewportWidth - menuWidth;
          if (posY + menuHeight > viewportHeight)
            posY = viewportHeight - menuHeight;
          return { x: posX, y: posY };
        };

        // 헤더 영역 우클릭 시
        if (target === "header") {
          const pos = getMenuPosition(event.pageX, event.pageY, 180, 280);
          setHeaderMenuVisible(true);
          setGridMenuVisible(false); // 셀 메뉴는 닫기
          setHeaderMenuPosition(pos);
          setHeaderMenuTarget({
            columnIndex: event.columnIndex,
            dataField: event.dataField || "",
            headerText: event.headerText || event.dataField || "",
          });
          return false;
        }

        // 그리드 영역(셀/행번호) 우클릭 시 - 고도화된 동적 메뉴 표시
        else {
          console.log("📋 [ContextMenu] 그리드 본문 클릭 - 동적 메뉴 표시");

          // 선택 정보 분석
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          const selectedIndices = Array.from(
            new Set(selectedItems.map((item) => item.rowIndex)),
          ).sort((a, b) => a - b);

          let rowCount = 1;
          let rowRange = "";

          if (selectedIndices.length > 1) {
            rowCount = selectedIndices.length;
            const minIdx = Math.min(...selectedIndices) + 1;
            const maxIdx = Math.max(...selectedIndices) + 1;
            rowRange = `${minIdx} - ${maxIdx}`;
          } else {
            rowRange = `${rowIndex + 1}`;
          }

          const pos = getMenuPosition(event.pageX, event.pageY, 220, 350);
          setGridMenuVisible(true);
          setHeaderMenuVisible(false); // 헤더 메뉴는 닫기
          setGridMenuPosition(pos);
          setGridMenuTarget({
            rowIndex,
            columnIndex,
            dataField,
            rowCount,
            rowRange,
            selectedIndices,
          });

          return false; // 기본 메뉴 표시 안함
        }
      });

      // 🆕 키보드 이벤트 핸들러 (Excel Style Hotkeys)
      window.AUIGrid.bind(grid, "keyDown", function (event) {
        // 1. F2: 셀 편집 모드 진입
        if (event.keyCode === 113) {
          window.AUIGrid.openInputer(grid);
          return false;
        }

        // 2. Ctrl + '+' (Plus/Add): 행/열 추가
        // 키코드: 187(=/+), 107(NumpadAdd)
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.keyCode === 187 ||
            event.keyCode === 107 ||
            event.key === "+" ||
            event.key === "=")
        ) {
          event.preventDefault(); // 브라우저 줌 방지

          // 열이 선택된 상태면 열 추가
          if (
            selectedColumnsRef.current &&
            selectedColumnsRef.current.length > 0
          ) {
            handleGridMenuAction("addColumn");
          } else {
            // 그 외에는 행 추가
            handleGridMenuAction("addRow");
          }
          return false;
        }

        // 3. Ctrl + '-' (Minus/Subtract): 행/열 삭제
        // 키코드: 189(-/_), 109(NumpadSubtract)
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.keyCode === 189 ||
            event.keyCode === 109 ||
            event.key === "-" ||
            event.key === "_")
        ) {
          event.preventDefault(); // 브라우저 줌 방지

          // 열이 선택된 상태면 열 삭제
          if (
            selectedColumnsRef.current &&
            selectedColumnsRef.current.length > 0
          ) {
            handleGridMenuAction("deleteColumn");
          } else {
            // 행 삭제
            const selectedItems = window.AUIGrid.getSelectedItems(grid);
            if (selectedItems && selectedItems.length > 0) {
              const selectedIndices = Array.from(
                new Set(selectedItems.map((item) => item.rowIndex)),
              ).sort((a, b) => b - a);
              // 다중 행 삭제 확인 로직은 handleGridMenuAction 내부에 있음
              handleGridMenuAction("deleteSelectedRows", { selectedIndices });
            }
          }
          return false;
        }

        // 4. Delete (46) - 엑셀 스타일: 내용 지우기 (Clear Content)
        if (event.keyCode === 46) {
          // 선택 영역 내용 지우기
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          if (selectedItems && selectedItems.length > 0) {
            // 엑셀처럼 내용만 지우기 (행 삭제 아님)
            handleGridMenuAction("clearCell");
          }
          return false;
        }

        // 5. Ctrl + Space: 열 전체 선택 (현재 선택된 셀 기준)
        if ((event.ctrlKey || event.metaKey) && event.keyCode === 32) {
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          if (selectedItems && selectedItems.length > 0) {
            const colIdx = selectedItems[0].columnIndex;
            // 기존 열 선택 로직 호출 (헤더 클릭 시뮬레이션)
            selectedColumnsRef.current = [colIdx];
            applyColumnHighlight([colIdx]);
            window.dispatchEvent(
              new CustomEvent("columnSelectionChanged", {
                detail: { selectedIndices: [colIdx] },
              }),
            );
          }
          event.preventDefault();
          return false;
        }

        // 6. Shift + Space: 행 전체 선택 (현재 선택된 셀 기준)
        if (event.shiftKey && event.keyCode === 32) {
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          if (selectedItems && selectedItems.length > 0) {
            const rowIndex = selectedItems[0].rowIndex;
            // 행 선택 스타일 적용
            selectedRowRef.current = rowIndex;
            window.AUIGrid.setSelectionByIndex(grid, rowIndex, 0); // 첫 번째 컬럼으로 포커스 이동하며 행 선택 시각적 힌트?
            // AUIGrid에서 행 전체 선택 API가 명확하지 않으므로, 선택된 행 스타일링으로 대체
            try {
              window.AUIGrid.refresh(grid);
            } catch (e) {}
          }
          event.preventDefault();
          return false;
        }

        // 7. 방향키 네비게이션 (기존 로직 유지)
        if (event.keyCode === 38 || event.keyCode === 40) {
          const currentRow =
            selectedRowRef.current !== null ? selectedRowRef.current : 0;
          const gridData = window.AUIGrid.getGridData(grid);
          const totalRows = gridData ? gridData.length : 0;
          if (totalRows === 0) return false;

          const targetRow =
            event.keyCode === 40
              ? Math.min(currentRow + 1, totalRows - 1)
              : Math.max(currentRow - 1, 0);

          // 1. AUIGrid 시각적 선택 업데이트
          window.AUIGrid.setSelectionByIndex(grid, targetRow, 0);

          // 2. 타겟 행 데이터 읽기 (동기 — gridData에서 직접)
          const item = gridData[targetRow];
          if (item) {
            const rowPageNumber = parseInt(
              item.P || item.pageNumber || item.page,
              10,
            );

            // 3. 📄 PDF 페이지 이동
            if (
              rowPageNumber &&
              !isNaN(rowPageNumber) &&
              onPageChangeRef.current
            ) {
              onPageChangeRef.current(rowPageNumber);
            }

            // 4. 🎨 ref 업데이트 + 그리드 리프레시 (cellClick과 동일)
            activePageRef.current = rowPageNumber || null;
            selectedRowRef.current = targetRow;
            try {
              window.AUIGrid.refresh(grid);
            } catch (e) {}

            // 5. 🔍 PDF 텍스트 하이라이트 (cellClick의 onFieldClick과 동일 패턴)
            // 가장 긴 텍스트 필드를 선택 (Description 등 — 짧은 "#" 같은 값은 제외)
            if (onFieldClick) {
              try {
                const colInfoList = window.AUIGrid.getColumnInfoList(grid);
                let bestField = null,
                  bestValue = "",
                  bestLen = 0;
                for (const col of colInfoList) {
                  const df = col.dataField;
                  if (
                    !df ||
                    df === "No." ||
                    df === "P" ||
                    df === "pageNumber" ||
                    df === "#"
                  )
                    continue;
                  const val = item[df];
                  if (val && typeof val === "string" && val.length > bestLen) {
                    bestField = df;
                    bestValue = val;
                    bestLen = val.length;
                  }
                }
                if (bestField && bestLen > 3) {
                  onFieldClick({
                    fieldKey: bestField,
                    fieldValue: bestValue,
                    page: rowPageNumber || null,
                    coordinates: null,
                    confidence: null,
                    hasGrounding: false,
                  });
                }
              } catch (e) {}
            }
          }

          return false; // AUIGrid 기본 화살표 동작 차단
        }

        // 8. Alt + Key Shortcuts (Header Actions)
        if (event.altKey) {
          switch (event.keyCode) {
            case 77: // M (Merge)
              if (selectedColumnsRef.current.length > 0) {
                handleGridMenuAction("mergeColumns");
                return false;
              }
              break;
            case 83: // S (Split)
              if (selectedColumnsRef.current.length > 0) {
                handleGridMenuAction("splitColumn");
                return false;
              }
              break;
            case 65: // A (AI)
              if (selectedColumnsRef.current.length > 0) {
                handleGridMenuAction("mergeColumnsPrompt");
                return false;
              }
              break;
            case 84: // T (Trim)
              if (selectedColumnsRef.current.length > 0) {
                handleGridMenuAction("trimColumn");
                return false;
              }
              break;
            case 85: // U (Uppercase)
              if (selectedColumnsRef.current.length > 0) {
                handleGridMenuAction("uppercaseColumn");
                return false;
              }
              break;
          }
        }

        // 9. Ctrl + H (Find/Replace)
        if ((event.ctrlKey || event.metaKey) && event.keyCode === 72) {
          event.preventDefault();
          handleGridMenuAction("findReplaceInColumn");
          return false;
        }
      });

      // ⭐ 셀 선택 변경 시 선택된 셀 합계 계산
      window.AUIGrid.bind(grid, "selectionChange", function (event) {
        try {
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          console.log(
            "📊 [Selection] event:",
            event,
            "selectedItems:",
            selectedItems,
          );

          if (!selectedItems || selectedItems.length === 0) {
            // 선택 해제 시 기본 푸터 복원
            if (footerLayout.length > 0) {
              window.AUIGrid.setFooter(grid, footerLayout);
            }
            return;
          }

          // 선택된 셀의 값 수집 및 컬럼별 합계 계산
          let sum = 0;
          let count = 0;
          let hasNumeric = false;
          const selectedColumns = new Set(); // 선택된 컬럼 추적

          selectedItems.forEach((sel) => {
            // AUIGrid multipleCells 모드: sel 자체가 item이거나 sel.item이 있을 수 있음
            const item = sel.item || sel;
            const dataField = sel.dataField || sel.columnIndex;

            // ⌨️ [키보드 네비게이션] 페이지 이동 + 하이라이트 (첫 번째 선택 아이템만)
            if (sel === selectedItems[0] && item) {
              const rowPageNumber = parseInt(
                item.P || item.pageNumber || item.page,
                10,
              );
              if (
                rowPageNumber &&
                !isNaN(rowPageNumber) &&
                onPageChangeRef.current
              ) {
                // 페이지가 변경된 경우에만 페이지 이동 (불필요한 호출 방지)
                if (activePageRef.current !== rowPageNumber) {
                  onPageChangeRef.current(rowPageNumber);
                  console.log(
                    `⌨️ [Selection→Page] 행 선택 → 페이지 ${rowPageNumber} 이동`,
                  );
                }
                activePageRef.current = rowPageNumber;
              }
              selectedRowRef.current = sel.rowIndex;
              // 🎨 페이지 그룹 하이라이트 리프레시
              try {
                window.AUIGrid.refresh(grid);
              } catch (e) {}
            }

            if (dataField) {
              selectedColumns.add(dataField);
            }

            // dataField가 있으면 해당 필드 값 사용
            let value = null;
            if (dataField && item[dataField] !== undefined) {
              value = item[dataField];
            } else if (sel.value !== undefined) {
              value = sel.value;
            }

            console.log("📊 [Selection] 셀:", { dataField, value, sel });

            if (value !== undefined && value !== null && value !== "") {
              // 콤마 제거 후 숫자 변환
              const cleanValue = String(value).replace(/,/g, "").trim();
              const numValue = parseFloat(cleanValue);
              if (!isNaN(numValue)) {
                sum += numValue;
                count++;
                hasNumeric = true;
              }
            }
          });

          console.log(
            `📊 [Selection Sum] count=${count}, sum=${sum}, hasNumeric=${hasNumeric}, columns:`,
            [...selectedColumns],
          );

          if (hasNumeric && count > 0) {
            // ⭐ 선택된 컬럼에 선택 합계 표시 (기존 컬럼 합계 대체)
            const selectionFooter = [];

            // 기존 footerLayout을 복사하되, 선택된 컬럼만 선택 합계로 대체
            footerLayout.forEach((footer) => {
              if (selectedColumns.has(footer.dataField)) {
                // 선택된 컬럼: 선택 합계로 대체
                selectionFooter.push({
                  dataField: footer.dataField,
                  positionField: footer.positionField,
                  labelText: `[${count}] ${sum.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}`,
                  style:
                    "text-align: right; font-weight: bold; color: #DC2626; font-size: 11px; background-color: #FEF2F2;",
                });
              } else {
                // 미선택 컬럼: 기존 합계 유지
                selectionFooter.push(footer);
              }
            });

            // 선택되었지만 기존 footerLayout에 없는 경우 (비숫자 컬럼 등)
            selectedColumns.forEach((col) => {
              const exists = selectionFooter.some((f) => f.dataField === col);
              if (!exists && col !== "pageNumber" && col !== "idx") {
                selectionFooter.push({
                  dataField: col,
                  positionField: col,
                  labelText: `[${count}] ${sum.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}`,
                  style:
                    "text-align: right; font-weight: bold; color: #DC2626; font-size: 11px; background-color: #FEF2F2;",
                });
              }
            });

            window.AUIGrid.setFooter(grid, selectionFooter);
            // ⭐ 푸터 UI 강제 갱신
            if (window.AUIGrid.updateFooter) {
              window.AUIGrid.updateFooter(grid);
            } else if (window.AUIGrid.refresh) {
              window.AUIGrid.refresh(grid);
            }
            console.log(
              `📊 [Selection Sum] 푸터 업데이트 완료: ${count}개, Σ${sum}`,
            );
          }
        } catch (e) {
          console.warn("📊 [Selection Sum] 오류:", e);
        }
      });

      // ⭐ [Grounding] 셀 클릭 시 PDF 하이라이트 + 📄 페이지 이동
      window.AUIGrid.bind(grid, "cellClick", function (event) {
        const { dataField, rowIndex, item, columnIndex } = event;
        const tableRowIndex =
          item._rowIndex !== undefined ? item._rowIndex - 1 : rowIndex;

        // 📄 [Page Navigation] 클릭한 행의 P(페이지) 값으로 PDF 뷰어 페이지 이동
        const rowPageNumber = parseInt(
          item.P || item.pageNumber || item.page,
          10,
        );
        if (rowPageNumber && !isNaN(rowPageNumber) && onPageChangeRef.current) {
          // 🔧 FIX: 이미 같은 페이지면 불필요한 onPageChange 호출 방지
          if (activePageRef.current !== rowPageNumber) {
            onPageChangeRef.current(rowPageNumber);
            console.log(
              `📄 [Grid→Page] 행 클릭 → 페이지 ${rowPageNumber} 이동`,
            );
          }
        }

        // 🎨 [Page Highlight] ref 업데이트
        activePageRef.current = rowPageNumber || null;
        selectedRowRef.current = rowIndex;
        // 🔧 FIX: refresh를 비동기로 지연 — 동기 refresh가 셀 DOM을 교체하여
        // AUIGrid 더블클릭 편집 감지를 방해하는 문제 수정
        setTimeout(() => {
          try {
            window.AUIGrid.refresh(grid);
          } catch (e) {}
        }, 300);

        // 첫 번째 데이터 열인지 확인 (No., P 제외, 실제 데이터 컬럼)
        // columnIndex: 0=No., 1=P, 2=첫번째데이터필드(Invoice No. 또는 Item)
        const isFirstDataColumn = columnIndex === 2;

        let grounding = null;

        // 🔧 [Step 1] 단일 필드인 경우 먼저 확인 (Invoice No 등 테이블 외부 필드)
        // groundedFields에 직접 키가 있으면 단일 필드
        if (groundedFields[dataField]?.bbox?.coordinates) {
          grounding = groundedFields[dataField];
          console.log(
            `📦 [Grid Grounding] 단일필드 '${dataField}':`,
            grounding?.bbox?.coordinates ? "✅" : "❌",
          );
        }
        // 🔧 [Step 2] 테이블 필드 처리
        else if (isFirstDataColumn) {
          // 첫 번째 테이블 컬럼 (Item No. 등) → 행 전체 하이라이트
          const rowKey = `items[${tableRowIndex}]`;
          grounding = groundedFields[rowKey];
          console.log(
            `📦 [Grid Grounding] Row ${tableRowIndex} 전체 → '${rowKey}':`,
            grounding?.bbox?.coordinates ? "✅" : "❌",
          );

          // 행 레벨 없으면 셀 레벨 시도
          if (!grounding?.bbox?.coordinates) {
            const cellKey = `items[${tableRowIndex}].${dataField}`;
            grounding = groundedFields[cellKey];
            console.log(
              `📦 [Grid Grounding] Row ${tableRowIndex} 폴백 → 셀 '${cellKey}':`,
              grounding?.bbox?.coordinates ? "✅" : "❌",
            );
          }
        } else {
          // 다른 열 클릭 → 셀별 하이라이트
          const cellKey = `items[${tableRowIndex}].${dataField}`;
          grounding = groundedFields[cellKey];

          // 셀별 없으면 행 레벨 폴백
          if (!grounding?.bbox?.coordinates) {
            const rowKey = `items[${tableRowIndex}]`;
            grounding = groundedFields[rowKey];
            console.log(
              `📦 [Grid Grounding] '${dataField}' → 행 폴백 '${rowKey}':`,
              grounding?.bbox?.coordinates ? "✅" : "❌",
            );
          } else {
            console.log(
              `📦 [Grid Grounding] '${dataField}' → 셀 '${cellKey}':`,
              grounding?.bbox?.coordinates ? "✅" : "❌",
            );
          }
        }

        // onFieldClick 호출 — 항상 호출 (bbox 없어도 텍스트 검색 하이라이트용)
        if (onFieldClick) {
          if (grounding?.bbox?.coordinates) {
            onFieldClick({
              fieldKey: dataField,
              fieldValue: item[dataField] || "",
              page: (grounding.bbox.page || 0) + 1, // 0-indexed → 1-indexed
              coordinates: grounding.bbox.coordinates,
              confidence: grounding.confidence || null,
              hasGrounding: true,
            });
          } else {
            // 🔍 [Text Search Fallback] bbox 좌표 없어도 텍스트 값 + 페이지 전달
            // ⚠️ 클릭한 셀 값이 "EA", "1" 등 짧으면 PDF 전체에서 다수 매칭 → 색반전 문제
            // → 항상 가장 긴 텍스트 필드 사용 (Description of Goods 등)으로 고유 라인 식별
            const fallbackPage =
              parseInt(item.P || item.pageNumber || item.page, 10) || null;
            let bestField = dataField,
              bestValue = item[dataField] || "",
              bestLen = bestValue.length;
            try {
              const colInfoList = window.AUIGrid.getColumnInfoList(grid);
              for (const col of colInfoList) {
                const df = col.dataField;
                if (
                  !df ||
                  df === "No." ||
                  df === "P" ||
                  df === "pageNumber" ||
                  df === "#"
                )
                  continue;
                const val = item[df];
                if (val && typeof val === "string" && val.length > bestLen) {
                  bestField = df;
                  bestValue = val;
                  bestLen = val.length;
                }
              }
            } catch (e) {}
            onFieldClick({
              fieldKey: bestField,
              fieldValue: bestValue,
              page: fallbackPage,
              coordinates: null,
              confidence: null,
              hasGrounding: false,
            });
          }
        }
      });

      // (⌨️ Keyboard nav 로직은 위의 keyDown 핸들러에 통합됨)
    };
    // 🔧 FIX: 직접 호출 — useEffect는 DOM 커밋 후 실행되므로 setTimeout 불필요
    // setTimeout(50)은 re-render 폭풍 시 이벤트 루프 차단으로 fire 지연됨 → 그리드 빈 상태 지속
    // 직접 호출하면 첫 useEffect에서 즉시 그리드 생성/업데이트
    tryInitGrid();

    return () => {
      // retry 타이머(retryTimerRef)는 취소하지 않음 — gen 체크로 stale 방지
      if (resizeObserverRef.current) {
        try {
          resizeObserverRef.current.disconnect();
        } catch (e) {}
      }
    };
  }, [
    viewMode,
    pageResults,
    matchedTemplate,
    columnFilter,
    totalPages,
    checkedPages,
    showMismatchOnly,
    allItemsProp,
    gridSafetyTrigger,
  ]); // 🔧 allItemsProp 추가 - 추출 후 items 변경 시 그리드 갱신 보장 + gridSafetyTrigger: safety net 강제 재생성

  // 🔧 컴포넌트 언마운트 시 그리드 정리 (별도 useEffect)
  useEffect(() => {
    return () => {
      if (gridRef.current) {
        try {
          destroyFillHandle("#horizontal_grid");
          window.AUIGrid.destroy(gridRef.current);
        } catch (e) {
          console.warn("그리드 unmount cleanup 실패:", e);
        }
        gridRef.current = null;
      }
      prevGridSignatureRef.current = null;
      // 🔧 FIX: 언마운트 시 pending retry 타이머도 정리
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      // 🔧 FIX: 높이 대기 ResizeObserver 정리
      if (heightObserverForGridRef.current) {
        try {
          heightObserverForGridRef.current.disconnect();
        } catch (e) {}
        heightObserverForGridRef.current = null;
      }
      if (inlineCvGridRef.current) {
        try {
          window.AUIGrid.destroy(inlineCvGridRef.current);
        } catch (e) {}
        inlineCvGridRef.current = null;
      }
    };
  }, []);

  // 🔧 FIX: Grid creation safety net — 모든 조건 충족 후에도 그리드가 생성되지 않은 경우 강제 재생성
  useEffect(() => {
    if (viewMode !== "horizontal" || !pageResults || pageResults.length === 0)
      return;

    const safetyTimer = setTimeout(() => {
      const gridId = "horizontal_grid";
      const gridEl = document.getElementById(gridId);
      const isCreated =
        gridRef.current && window.AUIGrid?.isCreated?.(gridRef.current);

      if (gridEl && gridEl.clientHeight >= 10 && !isCreated) {
        console.warn(
          "🔧 [Grid Safety Net] 그리드 컨테이너 준비됨 but AUIGrid 미생성 — 강제 재생성 트리거",
        );
        prevGridSignatureRef.current = null;
        setGridSafetyTrigger((prev) => prev + 1);
      } else if (gridEl && gridEl.clientHeight < 10) {
        console.warn("⚠️ [Safety Net] 컨테이너 높이 0 — 강제 재시도");
        prevGridSignatureRef.current = null;
        setGridSafetyTrigger((prev) => prev + 1);
      }
    }, 2500);

    return () => clearTimeout(safetyTimer);
  }, [viewMode, pageResults, matchedTemplate]);

  // 🔧 verifiedCells state → ref 동기화
  useEffect(() => {
    verifiedCellsRef.current = verifiedCells;
  }, [verifiedCells]);

  // 🔧 교차검증 데이터 도착 시 자동으로 인라인 그리드 활성화 + 검증 상태 리셋
  useEffect(() => {
    if (crossValidation?.crossItems && crossValidation.crossItems.length > 0) {
      setShowInlineCrossValidation(true);
      setVerifiedCells({});
      verifiedCellsRef.current = {};
    }
  }, [crossValidation]);

  // 🔧 상단 그리드 편집 완료 시 → 하단 CV 그리드 리프레시 (diff 자동 갱신)
  useEffect(() => {
    if (!showInlineCrossValidation || !gridRef.current || !window.AUIGrid)
      return;
    const grid = gridRef.current;
    if (!window.AUIGrid.isCreated(grid)) return;

    const handler = (event) => {
      if (event.oldValue === event.value) return;
      // 상단 편집 후 하단 그리드 리프레시 (filteredPrimary가 바뀌었으므로 labelFunction이 새 값 비교)
      setTimeout(() => {
        if (
          inlineCvGridRef.current &&
          window.AUIGrid.isCreated(inlineCvGridRef.current)
        ) {
          try {
            window.AUIGrid.refresh(inlineCvGridRef.current);
          } catch (e) {}
          console.log(
            "🔄 [InlineCV] 상단 편집 반영 → 하단 diff 갱신:",
            event.dataField,
            event.oldValue,
            "→",
            event.value,
          );
        }
      }, 100);
    };

    window.AUIGrid.bind(grid, "cellEditEnd", handler);
    return () => {
      try {
        window.AUIGrid.unbind(grid, "cellEditEnd", handler);
      } catch (e) {}
    };
  }, [showInlineCrossValidation]);

  // 🔧 인라인 CV 토글 시 메인 그리드 리사이즈 (100% ↔ 50% 전환 시 높이 재계산)
  useEffect(() => {
    if (!gridRef.current || !window.AUIGrid) return;
    const resizeBoth = () => {
      try {
        if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
          window.AUIGrid.resize(gridRef.current);
        }
      } catch (e) {}
      try {
        if (
          inlineCvGridRef.current &&
          window.AUIGrid.isCreated(inlineCvGridRef.current)
        ) {
          window.AUIGrid.resize(inlineCvGridRef.current);
        }
      } catch (e) {}
    };
    // flex 레이아웃 적용 후 여러 타이밍에 resize (브라우저 렌더링 대기)
    const t1 = setTimeout(resizeBoth, 100);
    const t2 = setTimeout(resizeBoth, 300);
    const t3 = setTimeout(resizeBoth, 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [showInlineCrossValidation]);

  // ========================================================================
  // 🔧 인라인 교차검증 그리드 (하단 비교 그리드)
  // ========================================================================
  useEffect(() => {
    const cvGridId = "inline_cv_grid";

    // 조건 불충족 시 기존 그리드 정리
    if (
      !showInlineCrossValidation ||
      !window.AUIGrid ||
      viewMode !== "horizontal" ||
      !crossValidation?.crossItems ||
      crossValidation.crossItems.length === 0
    ) {
      if (inlineCvGridRef.current) {
        try {
          window.AUIGrid.destroy(inlineCvGridRef.current);
        } catch (e) {}
        inlineCvGridRef.current = null;
      }
      return;
    }

    const crossItems = crossValidation.crossItems;

    // 숫자 서식 동치 비교 (순수 숫자 문자열만 비교, "100% COTTON" 같은 텍스트 제외)
    const isNumericEqual = (a, b) => {
      const sa = String(a).replace(/,/g, "").trim();
      const sb = String(b).replace(/,/g, "").trim();
      if (!sa || !sb) return false;
      // 순수 숫자(소수점, 부호 포함)만 허용
      const numRegex = /^-?\d+(\.\d+)?$/;
      if (!numRegex.test(sa) || !numRegex.test(sb)) return false;
      return parseFloat(sa) === parseFloat(sb);
    };

    // 숫자 필드 판별
    const isNumericField = (field) =>
      /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|합계|Unit.*Price|Net.*Weight|Gross.*Weight/i.test(
        field,
      );

    const timer = setTimeout(() => {
      const gridEl = document.getElementById(cvGridId);
      if (!gridEl) {
        console.warn("🔧 [InlineCV] DOM 요소 없음:", cvGridId);
        return;
      }

      // ⭐ 메인 그리드에서 컬럼 레이아웃 + 데이터를 직접 읽어서 동일 구조 보장
      if (!gridRef.current || !window.AUIGrid.isCreated(gridRef.current)) {
        console.warn("🔧 [InlineCV] 메인 그리드 미생성, 재시도 필요");
        return;
      }

      let mainColumns = [];
      let mainData = [];
      try {
        mainColumns = window.AUIGrid.getColumnInfoList(gridRef.current);
        mainData = window.AUIGrid.getGridData(gridRef.current);
      } catch (e) {
        console.warn("🔧 [InlineCV] 메인 그리드 정보 읽기 실패:", e);
        return;
      }

      // 메인 그리드 컬럼에서 필드 목록 추출 (메타 컬럼 제외)
      const skipDataFields = new Set(["_$uid", "_$rid"]);
      const fields = mainColumns
        .map((col) => col.dataField)
        .filter((df) => df && !skipDataFields.has(df));

      console.log("🔧 [InlineCV] 메인 그리드 컬럼 동기화:", fields);

      // checkedPages 필터링 (crossItems에 적용)
      let filteredCross = crossItems;
      if (checkedPages && checkedPages.length > 0) {
        filteredCross = crossItems.filter((item) => {
          const p = Number(item.pageNumber ?? item.P ?? item.p ?? 1);
          return checkedPages.includes(p);
        });
      }

      // 메인 그리드 데이터가 filteredPrimary 역할 (이미 checkedPages 필터링됨)
      const filteredPrimary = mainData;

      // 🔧 필터링 없이 전체 행 표시 (상단 그리드와 행 인덱스 일치 보장)
      let displayCross = filteredCross;

      // 🔧 # 열(idx) 복사: crossItems에 idx가 없으면 mainData에서 복사
      displayCross = displayCross.map((cItem, idx) => {
        const mItem = filteredPrimary[idx];
        const patched = { ...cItem };
        if (!patched.idx && mItem?.idx) patched.idx = mItem.idx;
        if (!patched.pageNumber && mItem?.pageNumber)
          patched.pageNumber = mItem.pageNumber;
        return patched;
      });

      // ⭐ 컬럼 정의: 메인 그리드 컬럼을 복제하되 editable=false, diff labelFunction 추가
      const cvColumns = mainColumns
        .map((col) => {
          const field = col.dataField;
          if (!field || skipDataFields.has(field)) return null;

          const isMetaCol = field === "pageNumber" || field === "idx";
          const isNum = isNumericField(field);

          return {
            dataField: field,
            headerText: col.headerText || fieldNameMap[field] || field,
            width: col.width || getOptimalColumnWidth(field, "shipping") || 200,
            editable: false,
            dataType: col.dataType || "string",
            style: col.style || (isMetaCol ? "center-align" : undefined),
            headerStyle:
              col.headerStyle || (isMetaCol ? "center-align" : undefined),
            renderer: isMetaCol ? undefined : { type: "TemplateRenderer" },
            labelFunction: isMetaCol
              ? undefined
              : (rowIndex, columnIndex, value, headerText, item) => {
                  const cvVal = String(value ?? "");
                  // 🔧 하단 CV 그리드: 순수 텍스트만 표시 (색상 없음 — 참고 전용)
                  // 확인완료 셀만 최소한의 ✓ 표시
                  const cellKey = `${item.pageNumber || ""}_${item.idx || rowIndex}_${field}`;
                  if (verifiedCellsRef.current[cellKey]) {
                    const escVal = cvVal
                      .replace(/&/g, "&amp;")
                      .replace(/</g, "&lt;")
                      .replace(/>/g, "&gt;");
                    return `${escVal} <span style="color:#9CA3AF;font-size:9px;">✓</span>`;
                  }
                  return cvVal;
                },
            styleFunction: isMetaCol
              ? undefined
              : (rowIndex, columnIndex, value, headerText, item) => {
                  return isNum ? "right-align" : "left-align";
                },
            footer: isNum
              ? {
                  operation: "SUM",
                  formatString: "#,##0.##",
                  style: "text-align: right; font-weight: bold; color: #333;",
                }
              : null,
          };
        })
        .filter(Boolean);

      const cvGridProps = {
        headerHeight: 32,
        rowHeight: 28,
        hScrollPolicy: "auto",
        enableFilter: false,
        enableSorting: false,
        showRowNumColumn: true,
        editable: false,
        selectionMode: "singleCell",
        showFooter: true,
        footerPosition: "bottom",
        footerHeight: 26,
        noDataMessage: "2차 모델 데이터 없음",
        enableMovingColumn: true,
        enableColumnResize: true,
        useContextMenu: true,
        contextMenuItems: [
          {
            label: "✓ 확인완료 (상단 정확)",
            callback: (event) => {
              const item = event.item;
              const field = event.dataField;
              if (!item || !field || field === "pageNumber" || field === "idx")
                return;
              const cellKey = `${item.pageNumber || ""}_${item.idx || event.rowIndex}_${field}`;
              setVerifiedCells((prev) => ({ ...prev, [cellKey]: true }));
              setTimeout(() => {
                if (
                  inlineCvGridRef.current &&
                  window.AUIGrid.isCreated(inlineCvGridRef.current)
                ) {
                  try {
                    window.AUIGrid.refresh(inlineCvGridRef.current);
                  } catch (e) {}
                }
              }, 50);
            },
          },
          {
            label: "✕ 확인 취소",
            callback: (event) => {
              const item = event.item;
              const field = event.dataField;
              if (!item || !field) return;
              const cellKey = `${item.pageNumber || ""}_${item.idx || event.rowIndex}_${field}`;
              setVerifiedCells((prev) => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
              });
              setTimeout(() => {
                if (
                  inlineCvGridRef.current &&
                  window.AUIGrid.isCreated(inlineCvGridRef.current)
                ) {
                  try {
                    window.AUIGrid.refresh(inlineCvGridRef.current);
                  } catch (e) {}
                }
              }, 50);
            },
          },
        ],
      };

      try {
        // 기존 그리드 정리
        if (inlineCvGridRef.current) {
          try {
            window.AUIGrid.destroy(inlineCvGridRef.current);
          } catch (e) {}
          inlineCvGridRef.current = null;
        }

        const cvGrid = window.AUIGrid.create(
          "#" + cvGridId,
          cvColumns,
          cvGridProps,
        );
        inlineCvGridRef.current = cvGrid;
        window.AUIGrid.setGridData(cvGrid, displayCross);

        // 열 경계 더블클릭 → 자동 너비 조절
        setupColumnAutoFit(cvGrid, "#" + cvGridId);

        // 푸터 합계
        const footerLayout = [];
        fields.forEach((field) => {
          if (isNumericField(field)) {
            footerLayout.push({
              dataField: field,
              positionField: field,
              operation: "SUM",
              formatString: "#,##0.##",
              style: "text-align: right; font-weight: bold; color: #333;",
            });
          }
        });
        if (footerLayout.length > 0)
          window.AUIGrid.setFooter(cvGrid, footerLayout);

        // 🔧 더블클릭: 하단 값 → 상단 그리드에 반영 (케이스 A: 하단이 맞을 때)
        window.AUIGrid.bind(cvGrid, "cellDoubleClick", (event) => {
          console.log("🔍 [InlineCV 더블클릭] 이벤트 발생:", {
            field: event.dataField,
            rowIndex: event.rowIndex,
            value: event.value,
          });
          const field = event.dataField;
          const item = event.item;
          if (!field || !item || field === "pageNumber" || field === "idx") {
            console.log("🔍 [InlineCV 더블클릭] 스킵: field 필터", {
              field,
              hasItem: !!item,
            });
            return;
          }
          if (!gridRef.current || !window.AUIGrid.isCreated(gridRef.current)) {
            console.log("🔍 [InlineCV 더블클릭] 스킵: 메인 그리드 없음", {
              ref: gridRef.current,
            });
            return;
          }

          const cvVal = String(item[field] ?? "");

          // 메인 그리드에서 현재 값을 실시간으로 읽기
          let origVal = "";
          try {
            const liveItem = window.AUIGrid.getItemByRowIndex(
              gridRef.current,
              event.rowIndex,
            );
            origVal = liveItem ? String(liveItem[field] ?? "") : "";
            console.log("🔍 [InlineCV 더블클릭] 비교:", {
              field,
              origVal,
              cvVal,
              rowIndex: event.rowIndex,
            });
          } catch (e) {
            console.warn("🔍 [InlineCV 더블클릭] getItemByRowIndex 실패:", e);
          }

          // 이미 같으면 무시 (대소문자 무시)
          if (
            origVal === cvVal ||
            origVal.toLowerCase() === cvVal.toLowerCase() ||
            isNumericEqual(origVal, cvVal)
          ) {
            console.log("🔍 [InlineCV 더블클릭] 스킵: 값 동일", {
              origVal,
              cvVal,
            });
            return;
          }

          // 상단 그리드 해당 셀 업데이트
          try {
            // 하단 CV 그리드 가로스크롤 위치 보존
            let cvHPos = 0;
            try {
              cvHPos = window.AUIGrid.getHScrollPosition(cvGrid);
            } catch (e) {}

            window.AUIGrid.setCellValue(
              gridRef.current,
              event.rowIndex,
              field,
              cvVal,
            );

            // 🔧 differences 객체도 업데이트 (labelFunction이 diff.primary를 읽으므로)
            try {
              const mainItem = window.AUIGrid.getItemByRowIndex(
                gridRef.current,
                event.rowIndex,
              );
              if (mainItem?.differences?.[field]) {
                mainItem.differences[field].match = true;
                mainItem.differences[field].primary = cvVal;
                if (mainItem.differences[field].flash !== undefined)
                  mainItem.differences[field].flash = cvVal;
              }
            } catch (e) {}

            // 상단 그리드 리프레시 (labelFunction 재실행)
            try {
              window.AUIGrid.refresh(gridRef.current);
            } catch (e) {}

            // 🔧 하단 셀도 확인완료(녹색)로 자동 마킹
            const cellKey = `${item.pageNumber || ""}_${item.idx || event.rowIndex}_${field}`;
            setVerifiedCells((prev) => ({ ...prev, [cellKey]: true }));

            console.log(
              "✅ [InlineCV 더블클릭] 상단 반영 + 확인완료:",
              field,
              origVal,
              "→",
              cvVal,
            );

            // 하단 그리드도 리프레시 (diff 해소 + 녹색 반영) + 스크롤 위치 복원
            setTimeout(() => {
              if (
                inlineCvGridRef.current &&
                window.AUIGrid.isCreated(inlineCvGridRef.current)
              ) {
                try {
                  window.AUIGrid.refresh(inlineCvGridRef.current);
                  window.AUIGrid.setHScrollPosition(
                    inlineCvGridRef.current,
                    cvHPos,
                  );
                } catch (e) {}
              }
            }, 100);
          } catch (e) {
            console.warn("❌ [InlineCV 더블클릭] 상단 반영 실패:", e);
          }
        });

        // ========================================================================
        // ⭐ 레거시 방식: KeyDown + setSelectionBlock + setFocus (검증된 동기화)
        // ========================================================================
        const setupKeyDownSync = (
          sourceGrid,
          targetGridGetter,
          sourceGridDomId,
        ) => {
          window.AUIGrid.bind(sourceGrid, "keyDown", (event) => {
            const keyCode = event.keyCode;
            // 화살표 키만 처리 (37=←, 38=↑, 39=→, 40=↓)
            if (
              keyCode === 37 ||
              keyCode === 38 ||
              keyCode === 39 ||
              keyCode === 40
            ) {
              const targetGrid = targetGridGetter();
              if (!targetGrid || !window.AUIGrid.isCreated(targetGrid)) return;
              try {
                const nowPos = window.AUIGrid.getSelectedIndex(sourceGrid);
                if (!nowPos || nowPos.length < 2) return;

                const keyDirections = {
                  37: [0, 0, -1, -1], // ←
                  38: [-1, -1, 0, 0], // ↑
                  39: [0, 0, 1, 1], // →
                  40: [1, 1, 0, 0], // ↓
                };

                const direction = keyDirections[keyCode];
                if (direction) {
                  const [rowOff1, rowOff2, colOff1, colOff2] = direction;
                  window.AUIGrid.setSelectionBlock(
                    targetGrid,
                    nowPos[0] + rowOff1,
                    nowPos[0] + rowOff2,
                    nowPos[1] + colOff1,
                    nowPos[1] + colOff2,
                  );
                }
                // 소스 그리드에 포커스 유지
                window.AUIGrid.setFocus(sourceGrid);
              } catch (e) {}
            }
          });
        };

        if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
          setupKeyDownSync(
            gridRef.current,
            () => inlineCvGridRef.current,
            "horizontal_grid",
          );
        }
        setupKeyDownSync(cvGrid, () => gridRef.current, "inline_cv_grid");

        // --- [마우스] cellClick: 클릭한 셀과 같은 위치를 상대 그리드에 선택
        if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
          window.AUIGrid.bind(gridRef.current, "cellClick", (event) => {
            try {
              if (
                !inlineCvGridRef.current ||
                !window.AUIGrid.isCreated(inlineCvGridRef.current)
              )
                return;
              window.AUIGrid.setSelectionBlock(
                inlineCvGridRef.current,
                event.rowIndex,
                event.rowIndex,
                event.columnIndex,
                event.columnIndex,
              );
              window.AUIGrid.setFocus(gridRef.current);
            } catch (e) {}
          });
        }

        window.AUIGrid.bind(cvGrid, "cellClick", (event) => {
          try {
            if (!gridRef.current || !window.AUIGrid.isCreated(gridRef.current))
              return;
            window.AUIGrid.setSelectionBlock(
              gridRef.current,
              event.rowIndex,
              event.rowIndex,
              event.columnIndex,
              event.columnIndex,
            );
            window.AUIGrid.setFocus(cvGrid);
          } catch (e) {}
        });

        // --- [스크롤] 가로 스크롤 동기화 (setHScrollPositionByPx — 레거시 방식)
        if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
          window.AUIGrid.bind(gridRef.current, "hScrollChange", (event) => {
            try {
              if (
                inlineCvGridRef.current &&
                window.AUIGrid.isCreated(inlineCvGridRef.current)
              ) {
                window.AUIGrid.setHScrollPositionByPx(
                  inlineCvGridRef.current,
                  event.position,
                );
              }
            } catch (e) {}
          });
        }

        window.AUIGrid.bind(cvGrid, "hScrollChange", (event) => {
          try {
            if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
              window.AUIGrid.setHScrollPositionByPx(
                gridRef.current,
                event.position,
              );
            }
          } catch (e) {}
        });

        // --- [스크롤] 세로 스크롤 동기화 (setRowPosition — 레거시 방식)
        if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
          window.AUIGrid.bind(gridRef.current, "vScrollChange", (event) => {
            try {
              if (
                inlineCvGridRef.current &&
                window.AUIGrid.isCreated(inlineCvGridRef.current)
              ) {
                window.AUIGrid.setRowPosition(
                  inlineCvGridRef.current,
                  event.position,
                );
              }
            } catch (e) {}
          });
        }
        window.AUIGrid.bind(cvGrid, "vScrollChange", (event) => {
          try {
            if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
              window.AUIGrid.setRowPosition(gridRef.current, event.position);
            }
          } catch (e) {}
        });

        // 리사이즈 + 데이터 기반 컬럼 자동 너비
        setTimeout(() => {
          try {
            window.AUIGrid.resize(cvGrid);
            autoFitAllColumns(cvGrid);
          } catch (e) {}
        }, 200);

        console.log("✅ [InlineCV] 교차검증 그리드 생성 완료:", {
          rows: displayCross.length,
          fields: fields.length,
          matchRate: crossValidation.matchRate,
        });
      } catch (e) {
        console.error("❌ [InlineCV] 그리드 생성 실패:", e);
      }
    }, 150); // 메인 그리드 생성(50ms) 이후 실행

    return () => {
      clearTimeout(timer);
      if (inlineCvGridRef.current) {
        try {
          window.AUIGrid.destroy(inlineCvGridRef.current);
        } catch (e) {}
        inlineCvGridRef.current = null;
      }
    };
  }, [
    showInlineCrossValidation,
    viewMode,
    crossValidation,
    allItemsProp,
    checkedPages,
  ]);

  // AUIGrid 초기화
  useEffect(() => {
    if (!window.AUIGrid || viewMode !== "grid") return;

    const gridId = "current_page_grid";

    // 기존 그리드 제거
    if (gridRef.current) {
      try {
        window.AUIGrid.destroy(gridRef.current);
      } catch (e) {
        console.warn("그리드 제거 실패:", e);
      }
      gridRef.current = null;
    }

    // DOM이 준비될 때까지 대기
    const timer = setTimeout(() => {
      const gridElement = document.getElementById(gridId);
      if (!gridElement) {
        console.error(`그리드 DOM 요소를 찾을 수 없습니다: ${gridId}`);
        return;
      }

      const columnLayout = [
        {
          dataField: "fieldName",
          headerText: "필드명",
          width: "15%",
          editable: false,
          style: "left-align",
        },
        {
          dataField: "flashValue",
          headerText: "Flash",
          width: "28%",
          editable: false,
          style: "left-align",
        },
        {
          dataField: "proValue",
          headerText: "Pro",
          width: "28%",
          editable: false,
          style: "left-align",
        },
        {
          dataField: "currentValue",
          headerText: "최종값",
          width: "29%",
          editable: true,
          editRenderer: {
            type: "InputEditRenderer",
          },
          style: "left-align",
        },
      ];

      // 🔧 컴팩트 그리드 스타일 적용
      const gridProps = {
        headerHeight: 32,
        rowHeight: 28,
        fillColumnSizeMode: true,
        enableFilter: false,
        enableSorting: false,
        enableColumnResize: true,
        showRowNumColumn: true,
        rowNumHeaderText: "No",
        editable: true,
        selectionMode: "singleRow",
        selectionStyle: {
          backgroundColor: "#E5E7EB", // 연한 노란색 (선택된 행)
          color: "#1A1B1E", // 로고 색상 텍스트
        },
      };

      const grid = window.AUIGrid.create(`#${gridId}`, columnLayout, gridProps);
      gridRef.current = grid;

      // 열 경계 더블클릭 → 자동 너비 조절
      setupColumnAutoFit(grid, `#${gridId}`);

      // 데이터 정렬 및 설정
      const sortedData = [...dataRows].sort((a, b) => {
        const indexA = fieldOrder.indexOf(a.key);
        const indexB = fieldOrder.indexOf(b.key);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      window.AUIGrid.setGridData(grid, sortedData);

      // 셀 편집 완료 이벤트
      window.AUIGrid.bind(grid, "cellEditEnd", function (event) {
        setEditedData((prev) => ({
          ...prev,
          [event.item.fieldName]: event.value,
        }));
      });
    }, 100); // 100ms 대기

    return () => {
      clearTimeout(timer);
      if (gridRef.current) {
        try {
          window.AUIGrid.destroy(gridRef.current);
        } catch (e) {
          console.warn("그리드 cleanup 실패:", e);
        }
        gridRef.current = null;
      }
    };
  }, [viewMode, currentPage, dataRows]);

  // ResizeObserver를 사용한 자동 리사이즈
  useEffect(() => {
    if (viewMode === "form" || !window.AUIGrid) {
      return;
    }

    const gridId =
      viewMode === "grid" ? "current_page_grid" : "horizontal_grid";

    // 그리드가 생성될 때까지 대기
    const setupObserver = () => {
      const gridElement = document.getElementById(gridId);

      if (!gridElement || !gridElement.parentElement) {
        console.warn(`⚠️ 그리드 요소를 찾을 수 없음: ${gridId}`);
        return null;
      }

      if (!window.AUIGrid.isCreated(`#${gridId}`)) {
        console.warn(`⚠️ 그리드가 아직 생성되지 않음: ${gridId}`);
        return null;
      }

      // ResizeObserver로 부모 컨테이너 크기 변화 감지
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (window.AUIGrid.isCreated(`#${gridId}`)) {
            // 약간의 지연을 두고 리사이즈 (DOM 업데이트 완료 대기)
            setTimeout(() => {
              try {
                window.AUIGrid.resize(`#${gridId}`);
              } catch (e) {
                // 리사이즈 실패는 조용히 처리
              }
            }, 50);
          }
        }
      });

      // 부모 컨테이너 관찰 시작
      resizeObserver.observe(gridElement.parentElement);

      return resizeObserver;
    };

    // 그리드 생성 후 Observer 설정 (여러 번 시도)
    let observer = null;
    const timer1 = setTimeout(() => {
      observer = setupObserver();
    }, 200);

    const timer2 = setTimeout(() => {
      if (!observer) observer = setupObserver();
    }, 500);

    const timer3 = setTimeout(() => {
      if (!observer) observer = setupObserver();
    }, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [viewMode]);

  // 오른쪽 패널 접기/펼치기에 따른 추가 리사이즈 (ResizeObserver 보조)
  useEffect(() => {
    if (viewMode === "form" || !window.AUIGrid) {
      return;
    }

    const gridId =
      viewMode === "grid" ? "current_page_grid" : "horizontal_grid";

    // CSS transition 완료 후 강제 리사이즈
    const timer = setTimeout(() => {
      if (window.AUIGrid.isCreated(`#${gridId}`)) {
        window.AUIGrid.resize(`#${gridId}`);
        console.log("📐 [Grid] Resize triggered by panel change");

        // 🔧 리사이즈 후 열 하이라이트 복원 (resize가 DOM을 재생성하므로)
        if (
          selectedColumnsRef.current &&
          selectedColumnsRef.current.length > 0 &&
          gridRef.current
        ) {
          const grid = gridRef.current;
          const COLUMN_STYLE_ID = "column-selection-style";
          // 기존 스타일 제거
          const existingStyle = document.getElementById(COLUMN_STYLE_ID);
          if (existingStyle) existingStyle.remove();
          // 헤더 하이라이트 복원
          const gridEl = document.getElementById(gridId);
          if (gridEl) {
            const headerCells = gridEl.querySelectorAll(
              ".aui-grid-header-renderer",
            );
            selectedColumnsRef.current.forEach((colIdx) => {
              if (headerCells[colIdx])
                headerCells[colIdx].classList.add("selected-column-header");
            });
          }
          // 바디 셀 하이라이트 복원
          const rowCount = window.AUIGrid.getRowCount(grid);
          if (rowCount > 0) {
            const nthChildPositions = [];
            selectedColumnsRef.current.forEach((colIdx) => {
              try {
                const cellEl = window.AUIGrid.getCellElementByIndex(
                  grid,
                  0,
                  colIdx,
                );
                if (cellEl?.parentElement) {
                  const siblings = cellEl.parentElement.children;
                  for (let i = 0; i < siblings.length; i++) {
                    if (siblings[i] === cellEl) {
                      nthChildPositions.push(i + 1);
                      break;
                    }
                  }
                }
              } catch (e) {
                /* ignore */
              }
            });
            if (nthChildPositions.length > 0) {
              const style = document.createElement("style");
              style.id = COLUMN_STYLE_ID;
              const selectors = nthChildPositions.map(
                (n) =>
                  `#${gridId} .aui-grid-body-panel .aui-grid-table td:nth-child(${n})`,
              );
              style.textContent = `${selectors.join(",\n")} { background-color: #DBEAFE !important; }`;
              document.head.appendChild(style);
            }
          }
          console.log("📐 [Grid] Column highlights restored after resize");
        }
      }
    }, 350);

    // 🔧 2차 하이라이트 복원 — AUIGrid 가상 렌더링이 나중에 완료될 수 있으므로
    const timer2 = setTimeout(() => {
      if (!window.AUIGrid.isCreated(`#${gridId}`)) return;
      if (
        selectedColumnsRef.current &&
        selectedColumnsRef.current.length > 0 &&
        gridRef.current
      ) {
        const grid = gridRef.current;
        const COLUMN_STYLE_ID = "column-selection-style";
        const gridEl = document.getElementById(gridId);
        if (!gridEl) return;

        // 헤더 하이라이트 재적용
        const headerCells = gridEl.querySelectorAll(
          ".aui-grid-header-renderer",
        );
        selectedColumnsRef.current.forEach((colIdx) => {
          if (headerCells[colIdx])
            headerCells[colIdx].classList.add("selected-column-header");
        });

        // 바디 셀 CSS 재적용
        const rowCount = window.AUIGrid.getRowCount(grid);
        if (rowCount > 0) {
          // 기존 스타일 제거 후 재삽입
          const existingStyle = document.getElementById(COLUMN_STYLE_ID);
          if (existingStyle) existingStyle.remove();

          const nthChildPositions = [];
          selectedColumnsRef.current.forEach((colIdx) => {
            try {
              const cellEl = window.AUIGrid.getCellElementByIndex(
                grid,
                0,
                colIdx,
              );
              if (cellEl?.parentElement) {
                const siblings = cellEl.parentElement.children;
                for (let i = 0; i < siblings.length; i++) {
                  if (siblings[i] === cellEl) {
                    nthChildPositions.push(i + 1);
                    break;
                  }
                }
              }
            } catch (e) {
              /* ignore */
            }
          });
          if (nthChildPositions.length > 0) {
            const style = document.createElement("style");
            style.id = COLUMN_STYLE_ID;
            const selectors = nthChildPositions.map(
              (n) =>
                `#${gridId} .aui-grid-body-panel .aui-grid-table td:nth-child(${n})`,
            );
            style.textContent = `${selectors.join(",\n")} { background-color: #DBEAFE !important; }`;
            document.head.appendChild(style);
          }
        }
        console.log("📐 [Grid] Column highlights restored (2nd pass)");
      }
    }, 650);

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [isRightPanelCollapsed, viewMode, showAIPanel]); // 🤖 showAIPanel 추가

  // 그리드 렌더링
  const renderGrid = () => {
    return (
      <div className="w-full h-full" style={{ minWidth: 0, minHeight: 0 }}>
        <div
          id="current_page_grid"
          style={{
            width: "100%",
            height: "100%",
            minWidth: 0,
          }}
        />
      </div>
    );
  };

  // 가로 그리드 렌더링 (필드명이 헤더, 페이지가 행)
  const renderHorizontalGrid = () => {
    console.log("🔍 [renderHorizontalGrid] 조건 확인:", {
      isPDF,
      pageResultsLength: pageResults?.length, // 📄 pageResults 사용
      willRenderEmpty: !isPDF || !pageResults || pageResults.length === 0,
    });
    console.log(
      "✅ [renderHorizontalGrid] 이 함수가 호출되었습니다! horizontal_grid div가 렌더링됩니다.",
    );

    // 📄 pageResults 기준으로 체크 (체크 상태와 관계없이 그리드 렌더링)
    if (!isPDF || !pageResults || pageResults.length === 0) {
      return (
        <div className="p-2 text-center text-gray-500">
          페이지 결과가 없습니다. (isPDF: {String(isPDF)}, results:{" "}
          {pageResults?.length || 0})
        </div>
      );
    }

    // 교차검증 불일치 통계 계산 (상단 바로가기용 — 백엔드 differences 기반)
    const cvMismatchStats = (() => {
      if (!crossValidation?.crossItems) return null;
      let totalFields = 0,
        mismatchFields = 0,
        resolvedFields = 0;
      const mismatchFieldMap = {};
      const skipK = new Set([
        "pageNumber",
        "P",
        "p",
        "page",
        "differences",
        "metadata",
        "documentType",
        "idx",
        "_rowIndex",
        "_hasDiff",
      ]);
      // 그리드 라이브 데이터에서 differences 수집 (사용자 편집 반영)
      const liveItems = [];
      try {
        if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
          const allRows = window.AUIGrid.getGridData(gridRef.current);
          if (allRows) liveItems.push(...allRows);
        }
      } catch (e) {}
      const pItems = liveItems.length > 0 ? liveItems : allItemsProp || [];
      pItems.forEach((pItem, idx) => {
        const diffs = pItem.differences || {};
        Object.keys(pItem).forEach((k) => {
          if (skipK.has(k) || k.startsWith("_")) return;
          totalFields++;
        });
        Object.entries(diffs).forEach(([k, d]) => {
          if (skipK.has(k) || k.startsWith("_")) return;
          if (d && d.match === false) {
            // 사용자가 셀 값을 수정했으면 resolved
            const currentVal = String(pItem[k] ?? "");
            const primaryVal = String(d.flash ?? "");
            const secondaryVal = String(d.pro ?? "");
            const cellKey = `${pItem.pageNumber || ""}_${pItem.idx || idx}_${k}`;
            if (
              verifiedCells[cellKey] ||
              (currentVal !== primaryVal && currentVal === secondaryVal)
            ) {
              resolvedFields++;
            } else {
              mismatchFields++;
              mismatchFieldMap[k] = (mismatchFieldMap[k] || 0) + 1;
            }
          }
        });
      });
      const verifiedCount = Object.keys(verifiedCells).length;
      const totalMismatch = mismatchFields + resolvedFields;
      const liveMatchRate =
        totalFields > 0
          ? Math.round(((totalFields - mismatchFields) / totalFields) * 100)
          : 100;
      return {
        totalFields,
        mismatchFields,
        resolvedFields,
        mismatchFieldMap,
        matchRate: liveMatchRate,
        verifiedCount,
        totalMismatch,
      };
    })();

    return (
      <div
        className="w-full h-full flex flex-col"
        style={{ minWidth: 0, minHeight: 0 }}
      >
        {/*
          ⚠️ CRITICAL: 아래 3개 요소의 key prop을 절대 제거하지 마세요!
          key="mismatch-nav-bar" / key="main-grid-area" / key="inline-cv-area"
          이유: 조건부 렌더링(mismatch bar, CV grid)이 나타났다 사라지면
          React가 key 없이 fiber 인덱스로 매칭하여 #horizontal_grid div를
          재생성하고 AUIGrid 인스턴스가 분리되어 그리드가 빈 상태로 표시됨.
          (2025.03 버그 수정 — 재현하기 어려운 간헐적 버그)
        */}
        {/* 🔍 불일치 바로가기 바 — 상단 그리드 위 (인라인 CV 열기와 무관하게 표시) */}
        {cvMismatchStats && cvMismatchStats.mismatchFields > 0 && (
          <div
            key="mismatch-nav-bar"
            style={{
              height: 26,
              flexShrink: 0,
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e8eaed",
              display: "flex",
              alignItems: "center",
              gap: 5,
              paddingLeft: 10,
              paddingRight: 10,
              overflowX: "auto",
              scrollbarWidth: "thin",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#5f6368",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              불일치 {cvMismatchStats.mismatchFields}건
            </span>
            <div
              style={{
                width: 1,
                height: 14,
                backgroundColor: "#dadce0",
                flexShrink: 0,
              }}
            />
            {Object.entries(cvMismatchStats.mismatchFieldMap)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 20)
              .map(([field, count]) => (
                <button
                  key={field}
                  onClick={() => {
                    if (!gridRef.current) return;
                    try {
                      const mainData = window.AUIGrid.getGridData(
                        gridRef.current,
                      );
                      const cItems = crossValidation.crossItems;
                      const isNumEq = (a, b) => {
                        const na = parseFloat(String(a).replace(/,/g, ""));
                        const nb = parseFloat(String(b).replace(/,/g, ""));
                        return !isNaN(na) && !isNaN(nb) && na === nb;
                      };
                      for (let i = 0; i < mainData.length; i++) {
                        const pv = String(mainData[i]?.[field] ?? "");
                        const cv = String(cItems[i]?.[field] ?? "");
                        if (
                          pv !== cv &&
                          pv.toLowerCase() !== cv.toLowerCase() &&
                          !isNumEq(pv, cv)
                        ) {
                          const cols = window.AUIGrid.getColumnInfoList(
                            gridRef.current,
                          );
                          const colIdx = cols.findIndex(
                            (c) => c.dataField === field,
                          );
                          window.AUIGrid.setRowPosition(gridRef.current, i);
                          if (colIdx >= 0) {
                            window.AUIGrid.setSelectionByIndex(
                              gridRef.current,
                              i,
                              colIdx,
                              i,
                              colIdx,
                            );
                            try {
                              window.AUIGrid.showColumnByDataField(
                                gridRef.current,
                                field,
                              );
                            } catch (e) {}
                          } else {
                            window.AUIGrid.setSelectionByIndex(
                              gridRef.current,
                              i,
                            );
                          }
                          break;
                        }
                      }
                    } catch (e) {
                      console.warn("바로가기 네비게이션 실패:", e);
                    }
                  }}
                  style={{
                    padding: "1px 8px",
                    borderRadius: 12,
                    fontSize: 10,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: "#fff",
                    border: "1px solid #dadce0",
                    color: "#3c4043",
                    outline: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e8eaed";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#fff";
                  }}
                >
                  {fieldNameMap[field] || field}{" "}
                  <span style={{ fontWeight: 700 }}>{count}</span>
                </button>
              ))}
          </div>
        )}

        {/* 1차 (기준) 그리드 영역 */}
        <div
          key="main-grid-area"
          style={{
            flex: showInlineCrossValidation ? "1 1 50%" : "1 1 100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1, minHeight: 0 }}>
            <div
              id="horizontal_grid"
              style={{
                width: "100%",
                height: "100%",
                minWidth: 0,
              }}
            />
          </div>
        </div>

        {/* 🔧 인라인 교차검증: 검증 그리드 (참고 전용 — 색상 없음) */}
        {showInlineCrossValidation && crossValidation?.crossItems && (
          <React.Fragment key="inline-cv-area">
            <div
              style={{
                flex: "1 1 50%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* 하단 그리드 헤더: 참고 전용 레이블 */}
              <div
                style={{
                  padding: "2px 8px",
                  fontSize: 11,
                  color: "#9AA0A6",
                  borderTop: "1px solid #E8EAED",
                  borderBottom: "1px solid #E8EAED",
                  background: "#FAFAFA",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                <span>2차 모델</span>
                <span style={{ fontSize: 10, color: "#BDC1C6" }}>
                  참고 전용 · 더블클릭으로 상단에 반영
                </span>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <div
                  id="inline_cv_grid"
                  style={{ width: "100%", height: "100%", minWidth: 0 }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    );
  };

  // 🧠 학습 데이터 저장 — 현재 그리드 데이터를 검증 완료 샘플로 저장
  const handleSaveTrainingData = async () => {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? `http://${window.location.hostname}:3002`
        : "");
    setTrainingSaveStatus("saving");

    try {
      // 메인 그리드에서 현재 데이터 가져오기
      if (!gridRef.current || !window.AUIGrid.isCreated(gridRef.current)) {
        throw new Error("그리드가 초기화되지 않았습니다");
      }

      const gridData = window.AUIGrid.getGridData(gridRef.current);
      const columnInfos = window.AUIGrid.getColumnInfoList(gridRef.current);
      const fieldNames = columnInfos
        .map((c) => c.dataField)
        .filter((f) => f && f !== "pageNumber" && f !== "idx");

      // 거래처(템플릿명) 기준 — 템플릿명 = 무역거래처(해외거래처)
      const templateObj = selectedTemplateId
        ? Object.values(templates).find((t) => t.id === selectedTemplateId)
        : matchedTemplate;
      const vendorName = templateObj?.name || documentType || "unknown";

      // CV 데이터 수집
      let cvData = {};
      let cvModel = "";
      let matchRate = null;
      let fieldCorrections = [];

      if (crossValidation) {
        cvData = { items: crossValidation.crossItems || [] };
        cvModel = crossValidation.secondaryModel || "cv-model";
        matchRate = crossValidation.matchRate || null;

        // 수정 내역 추출: 메인 vs CV 불일치 필드
        if (crossValidation.crossItems && gridData.length > 0) {
          for (
            let i = 0;
            i < Math.min(gridData.length, crossValidation.crossItems.length);
            i++
          ) {
            const mainRow = gridData[i];
            const cvRow = crossValidation.crossItems[i];
            for (const field of fieldNames) {
              const mv = String(mainRow[field] ?? "");
              const cv = String(cvRow[field] ?? "");
              if (
                mv &&
                cv &&
                mv !== cv &&
                mv.toLowerCase() !== cv.toLowerCase()
              ) {
                fieldCorrections.push({
                  row: i,
                  field,
                  mainValue: mv,
                  cvValue: cv,
                });
              }
            }
          }
        }
      }

      const payload = {
        vendorName,
        vendorCode: "",
        documentType: documentType || "unknown",
        templateId: selectedTemplateId || "",
        fileName: fileId || "",
        pageCount: totalPages || 1,
        flashData: { items: gridData },
        flashModel: GEMINI_FLASH_MODEL,
        cvData,
        cvModel,
        verifiedData: (() => {
          // 멀티페이지: pageResults 구조로 그룹핑
          const pageGroups = {};
          gridData.forEach((row) => {
            const pn = row.pageNumber || 1;
            if (!pageGroups[pn]) pageGroups[pn] = [];
            pageGroups[pn].push(row);
          });
          const pageNums = Object.keys(pageGroups)
            .map(Number)
            .sort((a, b) => a - b);
          if (pageNums.length > 1) {
            return {
              pageResults: pageNums.map((pn) => ({
                pageNumber: pn,
                items: pageGroups[pn],
              })),
              fieldNames,
            };
          }
          return { items: gridData, fieldNames };
        })(),
        fieldCorrections,
        matchRate,
        fieldNames,
        itemCount: gridData.length,
        userId: "system",
        groupId: "0001",
        uploadedFilePath: uploadedFilePath || "",
      };

      const response = await fetch(`${API_BASE_URL}/api/training-data/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setTrainingSaveStatus("saved");
        console.log(
          `✅ [TrainingData] 학습 데이터 저장 완료: id=${result.id}, vendor=${vendorName}`,
        );
      } else {
        throw new Error(result.error || "저장 실패");
      }
    } catch (err) {
      console.error("❌ [TrainingData] 학습 데이터 저장 실패:", err);
      setTrainingSaveStatus("error");
    }

    // 3초 후 상태 초기화
    setTimeout(() => setTrainingSaveStatus(null), 3000);
  };

  // 🆕 AI 검색으로 보내기 — 그리드 안 description 있는 모든 행을 sessionStorage queue 에 push 후
  // AI HS 분류 메뉴 (?audit=onto-detail&hs=4202) 로 navigate. LeftAiPanel 이 mount 시 자동 load + 다중검색 탭 활성화.
  const handleSendToAiSearch = () => {
    if (!gridRef.current || !window.AUIGrid) {
      alert("그리드가 준비되지 않았습니다.");
      return;
    }
    const grid = gridRef.current;
    const rows = window.AUIGrid.getGridData(grid) || [];
    const sampleKeys = rows[0] ? Object.keys(rows[0]) : [];
    const norm = (k) =>
      String(k)
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    const descKey = sampleKeys.find((k) =>
      [
        "description",
        "desc",
        "품명",
        "goodsdescription",
        "descriptionofgoods",
        "item",
        "artigo",
      ].includes(norm(k)),
    );
    const compKey = sampleKeys.find((k) =>
      ["composition", "재질", "material"].includes(norm(k)),
    );
    const hsKey = sampleKeys.find((k) => norm(k) === "hscode");
    if (!descKey) {
      alert("Description 컬럼이 없습니다.");
      return;
    }
    const items = rows
      .map((r) => ({
        description: String(r[descKey] || "").trim(),
        composition: compKey ? String(r[compKey] || "").trim() : "",
        hscode: hsKey ? String(r[hsKey] || "").trim() : "",
        AIHSCode: String(r.AIHSCode || "").trim(),
      }))
      .filter((it) => it.description);
    if (items.length === 0) {
      alert("전송할 행이 없습니다. Description 이 있는 행만 전송됩니다.");
      return;
    }
    sessionStorage.setItem(
      "hsClassifierQueue",
      JSON.stringify({
        items,
        sourceFileId: fileId,
        timestamp: Date.now(),
      }),
    );
    // AI HS 분류 메뉴로 navigate (SPA pushState — LeftAiPanel 이 popstate listener 로 queue consume)
    window.history.pushState({}, "", "/?audit=onto-detail&hs=4202");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  // 🆕 AI HS 자동분류 (5-layer evidence) — grid 안 모든 row 중 HSCode 빈 cell 만
  // grid 가 source-of-truth: window.AUIGrid.getGridData / updateRow 직접 사용
  const handleAutoHsClassify = async () => {
    if (isHsClassifying) return;
    if (!gridRef.current || !window.AUIGrid) {
      alert("그리드가 준비되지 않았습니다.");
      return;
    }
    const grid = gridRef.current;
    const rows = window.AUIGrid.getGridData(grid) || [];
    // HSCode field key 식별 — case insensitive
    const sampleKeys = rows[0] ? Object.keys(rows[0]) : [];
    const norm = (k) =>
      String(k)
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    const hsKey = sampleKeys.find((k) => norm(k) === "hscode");
    if (!hsKey) {
      alert(
        "HSCode 컬럼이 없습니다. (현재 템플릿에 HSCode field 가 있어야 합니다.)",
      );
      return;
    }
    const descKey = sampleKeys.find((k) =>
      [
        "description",
        "desc",
        "품명",
        "goodsdescription",
        "descriptionofgoods",
        "item",
        "artigo",
      ].includes(norm(k)),
    );
    if (!descKey) {
      alert(
        "Description 컬럼이 없습니다. (분류할 키워드 source 가 필요합니다.)",
      );
      return;
    }
    const compKey = sampleKeys.find((k) =>
      ["composition", "재질", "material"].includes(norm(k)),
    );

    // AIHSCode 는 별도 column — HSCode (수출국 참고값) 와 무관하게 description 있는 모든 행 분류.
    // 이미 AIHSCode 가 채워진 (사용자 확정 or 이전 분류) 행은 보존.
    const validRows = rows
      .map((r, i) => ({
        rowIndex: i,
        description: String(r[descKey] || "").trim(),
        composition: compKey ? String(r[compKey] || "").trim() : "",
        existingAi: String(r.AIHSCode || "").trim(),
      }))
      .filter((r) => r.description && !r.existingAi);
    if (validRows.length === 0) {
      alert(
        "분류할 행이 없습니다. (Description 비어있거나 AI HSCode 이미 채워짐.)",
      );
      return;
    }
    setIsHsClassifying(true);
    setHsClassifyStats(null);
    try {
      const items = validRows.map((r, i) => ({
        index: i,
        description: r.description,
        composition: r.composition,
      }));
      const API_BASE =
        process.env.REACT_APP_API_URL ||
        (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
          ? `http://${window.location.hostname}:3002`
          : "");
      // loginHS bulk 엔진(EvidenceClassify, Spring) — 판례·마스터·임베딩·L2검증으로 행별 분류.
      // batch-flash(단순 Gemini 1콜)에서 전환. review_needed(보류) 플래그 포함, 유효 HSK 보장.
      const res = await fetch(`${API_BASE}/api/hs-classify/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      (data.results || []).forEach((r) => {
        const v = validRows[r.index];
        if (!v || !r.ok || !r.hsk_10digit) return;
        const suggestion = {
          hsk: r.hsk_10digit,
          hs6: r.hs_6digit,
          hs4: r.hs_4digit,
          chapter: r.chapter,
          confidence: r.confidence,
          popum_ko: r.popum_ko,
          basic_rate: r.basic_rate,
          source: r.source || GEMINI_FLASH_MODEL,
          keyword: r.keyword,
        };
        // Flash 1차 = 모든 confidence 의 결과를 AI HSCode 에 즉시 채움 (사용자가 직접 검토).
        // HIGH/MEDIUM/LOW 색상은 AI dot 으로 표현. user_confirmed 는 modal 확정 시.
        const patch = {
          _aiSuggestion: suggestion,
          AIHSCode: r.hsk_10digit,
          _provenance: "ai_suggested",
        };
        try {
          window.AUIGrid.updateRow(grid, patch, v.rowIndex);
        } catch (e) {
          /* ignore single row update fail */
        }
      });
      setHsClassifyStats({
        total: data.total,
        success: data.success,
        confidence_distribution: data.confidence_distribution,
      });
      // 부모로 변경 통지 — onSaveVehicle 또는 다른 callback 없는 경우 silent (grid 가 source)
    } catch (e) {
      console.error("[autoHsClassify]", e);
      alert(`HS 자동분류 실패: ${e.message}`);
    } finally {
      setIsHsClassifying(false);
    }
  };

  // AI dot mousedown 우회 — AUIGrid cellClick 가 templateRenderer 안에서 미발화하는 케이스 대비
  // labelFunction 안 span 의 data-ai-dot attribute 로 row index 도출
  useEffect(() => {
    const handler = (e) => {
      const dot = e.target?.closest?.("[data-ai-dot]");
      if (!dot) return;
      const rowIdx = parseInt(dot.getAttribute("data-ai-dot"), 10);
      if (isNaN(rowIdx) || !gridRef.current || !window.AUIGrid) return;
      const rows = window.AUIGrid.getGridData(gridRef.current) || [];
      const item = rows[rowIdx];
      if (!item) return;
      const norm = (k) =>
        String(k)
          .toLowerCase()
          .replace(/[\s_-]+/g, "");
      const readVal = (cands) => {
        for (const k of Object.keys(item)) {
          if (cands.includes(norm(k)) && item[k]) return String(item[k]);
        }
        return "";
      };
      setHsEvidenceModal({
        rowIndex: rowIdx,
        description: readVal([
          "description",
          "desc",
          "품명",
          "goodsdescription",
          "descriptionofgoods",
          "item",
          "artigo",
        ]),
        composition: readVal(["composition", "재질", "material"]),
      });
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Evidence modal 확정 — AIHSCode 만 갱신 (HSCode 수출국 참고값은 보존)
  const handleHsEvidenceConfirm = (rowIndex, hsk, detail) => {
    if (!gridRef.current || !window.AUIGrid) return;
    const grid = gridRef.current;
    const rows = window.AUIGrid.getGridData(grid) || [];
    if (rowIndex < 0 || rowIndex >= rows.length) return;
    const patch = { AIHSCode: hsk, _provenance: "user_confirmed" };
    if (detail?.decided) {
      patch._aiSuggestion = {
        ...(rows[rowIndex]._aiSuggestion || {}),
        confirmed_hsk: hsk,
        decided: detail.decided,
      };
    }
    try {
      window.AUIGrid.updateRow(grid, patch, rowIndex);
    } catch (e) {
      console.warn("[handleHsEvidenceConfirm] updateRow fail:", e);
    }
  };

  // 🧪 VLM 파인튜닝 데이터 저장 — 전체 페이지 이미지 + 추출 결과를 저장
  const handleSaveVLMData = async () => {
    const API_BASE =
      process.env.REACT_APP_API_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? `http://${window.location.hostname}:3002`
        : "");
    setVlmSaveStatus("saving");

    try {
      // 그리드 데이터 수집 (pageResults 구조로 그룹핑)
      let extractionResult = {};

      if (gridRef.current && window.AUIGrid.isCreated(gridRef.current)) {
        const gridData = window.AUIGrid.getGridData(gridRef.current);
        if (gridData && gridData.length > 0) {
          const skipKeys = new Set([
            "idx",
            "differences",
            "metadata",
            "_rowIndex",
            "_hasDiff",
          ]);
          const cleanRows = gridData.map((row) => {
            const clean = {};
            Object.entries(row).forEach(([k, v]) => {
              if (!skipKeys.has(k) && !k.startsWith("_")) clean[k] = v;
            });
            return clean;
          });

          // pageNumber별로 그룹핑하여 pageResults 구조 생성
          const pageGroups = {};
          cleanRows.forEach((row) => {
            const pn = row.pageNumber || 1;
            if (!pageGroups[pn]) pageGroups[pn] = [];
            const { pageNumber, ...rest } = row;
            pageGroups[pn].push(rest);
          });

          const pageNums = Object.keys(pageGroups)
            .map(Number)
            .sort((a, b) => a - b);
          if (pageNums.length > 1) {
            // 멀티페이지: pageResults 구조
            extractionResult = {
              pageResults: pageNums.map((pn) => ({
                pageNumber: pn,
                items: pageGroups[pn],
              })),
            };
          } else if (cleanRows.length === 1) {
            // 단일 행: 플랫 객체
            const { pageNumber, ...rest } = cleanRows[0];
            extractionResult = rest;
          } else {
            // 단일 페이지 다중 행
            extractionResult = {
              items: cleanRows.map(({ pageNumber, ...rest }) => rest),
            };
          }
        }
      }

      if (Object.keys(extractionResult).length === 0) {
        throw new Error("추출 결과가 비어있습니다");
      }

      // 템플릿 정보
      const templateObj = selectedTemplateId
        ? Object.values(templates).find((t) => t.id === selectedTemplateId)
        : matchedTemplate;
      const vendorName = templateObj?.name || documentType || "unknown";

      // 필드명 수집
      const columnInfos = window.AUIGrid.getColumnInfoList(gridRef.current);
      const fieldNames = columnInfos
        .map((c) => c.dataField)
        .filter((f) => f && f !== "pageNumber" && f !== "idx");

      const payload = {
        pdfPath: uploadedFilePath || "",
        pageNumber: selectedPageNumber || 1,
        totalPages: totalPages || 1,
        extractionResult,
        templateName: vendorName,
        templateId: selectedTemplateId || "",
        prompt: "",
        fieldNames,
        documentType: documentType || "",
        fileName: fileId || "",
      };

      // 🔧 pdfPath가 없으면 pdfFile에서 base64 이미지 추출 (temp 파일 삭제 대비)
      if (!uploadedFilePath && pdfFile) {
        try {
          console.log("📸 [VLM] pdfPath 없음 → PDF에서 직접 이미지 추출...");
          const arrayBuffer = await pdfFile.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const pagesToRender = Math.min(
            pdf.numPages,
            totalPages || pdf.numPages,
          );
          const imagesBase64 = [];

          for (let pg = 1; pg <= pagesToRender; pg++) {
            const page = await pdf.getPage(pg);
            const viewport = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext("2d");
            await page.render({ canvasContext: ctx, viewport }).promise;
            const dataUrl = canvas.toDataURL("image/png");
            imagesBase64.push(dataUrl.replace(/^data:image\/png;base64,/, ""));
          }

          if (imagesBase64.length > 0) {
            payload.imagesBase64 = imagesBase64;
            console.log(
              `📸 [VLM] ${imagesBase64.length}페이지 이미지 추출 완료`,
            );
          }
        } catch (imgErr) {
          console.warn("⚠️ [VLM] PDF 이미지 추출 실패:", imgErr.message);
        }
      }

      const response = await fetch(`${API_BASE}/api/vlm-finetune/save-sample`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setVlmSaveStatus("saved");
        console.log(
          `✅ [VLM] 샘플 저장 완료: ${result.sampleId} (이미지: ${result.imageSaved}, 필드: ${result.fieldCount})`,
        );
      } else {
        throw new Error(result.error || "저장 실패");
      }
    } catch (err) {
      console.error("❌ [VLM] 저장 실패:", err);
      setVlmSaveStatus("error");
    }

    setTimeout(() => setVlmSaveStatus(null), 3000);
  };

  // 엑셀 다운로드 - 🔧 AUIGrid 동적 열 지원 추가
  const handleExcelDownload = () => {
    const fileName = `${documentType || "data"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    console.log("🔍 [Excel] 다운로드 시작, viewMode:", viewMode);

    // 🔧 AUIGrid 데이터 우선 사용 (동적 열 포함!)
    if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
      try {
        const gridData = window.AUIGrid.getGridData("#horizontal_grid");
        const columnLayout = window.AUIGrid.getColumnLayout("#horizontal_grid");

        if (
          gridData &&
          gridData.length > 0 &&
          columnLayout &&
          columnLayout.length > 0
        ) {
          console.log(
            "📊 [Excel] AUIGrid 직접 내보내기:",
            gridData.length,
            "행,",
            columnLayout.length,
            "열",
          );

          // 열 순서에 맞게 데이터 재구성 (숫자는 number 타입으로 변환)
          const exportData = gridData.map((row) => {
            const exportRow = {};
            columnLayout.forEach((col) => {
              if (col.dataField) {
                const headerText = col.headerText || col.dataField;
                let value = row[col.dataField] ?? "";

                // 🔧 숫자 변환: 콤마 제거 후 숫자인지 확인
                if (typeof value === "string" && value.trim() !== "") {
                  const cleanValue = value.replace(/,/g, "").trim();
                  // 숫자 패턴: 정수 또는 소수
                  if (/^-?\d+(\.\d+)?$/.test(cleanValue)) {
                    const numValue = parseFloat(cleanValue);
                    if (!isNaN(numValue)) {
                      value = numValue; // 숫자 타입으로 변환
                    }
                  }
                }

                exportRow[headerText] = value;
              }
            });
            return exportRow;
          });

          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(exportData);

          // 열 너비 설정
          const colWidths = columnLayout.map((col) => ({
            wch: col.width ? Math.floor(col.width / 7) : 15,
          }));
          ws["!cols"] = colWidths;

          XLSX.utils.book_append_sheet(wb, ws, "Items");
          XLSX.writeFile(wb, fileName);

          console.log("✅ [Excel] AUIGrid 데이터 내보내기 완료");
          return;
        }
      } catch (err) {
        console.warn(
          "⚠️ [Excel] AUIGrid 직접 내보내기 실패, 템플릿 방식으로 폴백:",
          err,
        );
      }
    }

    // 📋 폴백: 템플릿 기반 내보내기 (기존 로직)
    // 선택된 템플릿 정보 가져오기
    const selectedTemplate = selectedTemplateId
      ? templates[selectedTemplateId]
      : null;
    const singleFields =
      selectedTemplate?.singleFields || selectedTemplate?.fields || [];
    const tableFields = selectedTemplate?.tableFields || [];
    const computedFields = selectedTemplate?.computedFields || [];

    console.log("📋 [Excel] 템플릿 기반 폴백:", {
      templateId: selectedTemplateId,
      templateName: selectedTemplate?.name,
      singleFieldsCount: singleFields.length,
      tableFieldsCount: tableFields.length,
      computedFieldsCount: computedFields.length,
    });

    // 합성 필드 계산 함수 (대소문자 무시 매칭)
    const evaluateComputedField = (formula, sourceData) => {
      if (!formula) return "";
      let result = formula;

      // formula에서 {placeholder} 형태를 모두 찾음
      const placeholders = formula.match(/\{([^}]+)\}/g) || [];

      placeholders.forEach((placeholder) => {
        const keyInFormula = placeholder.slice(1, -1); // {key} -> key

        // sourceData에서 대소문자 무시하고 키 찾기
        const matchingKey = Object.keys(sourceData).find(
          (k) => k.toLowerCase() === keyInFormula.toLowerCase(),
        );

        if (matchingKey && sourceData[matchingKey]) {
          result = result.replace(placeholder, sourceData[matchingKey]);
        } else {
          result = result.replace(placeholder, ""); // 매칭 안되면 빈 문자열
        }
      });

      return result.trim();
    };

    try {
      const wb = XLSX.utils.book_new();

      // pageResults 또는 현재 페이지 데이터로 vehicles 배열 생성
      const vehicles = [];
      if (pageResults && pageResults.length > 0) {
        pageResults.forEach((pr, idx) => {
          const extractedData = pr.extractedData || pr;
          vehicles.push({
            fileName: documentType || "Document",
            pageNumber: pr.pageNumber || idx + 1,
            current: { ...extractedData },
          });
        });
      } else if (editedData && Object.keys(editedData).length > 0) {
        vehicles.push({
          fileName: documentType || "Document",
          pageNumber: currentPage,
          current: { ...editedData },
        });
      }

      if (vehicles.length === 0) {
        alert("내보낼 데이터가 없습니다.");
        return;
      }

      // 🔧 테이블 필드가 있으면 펼치기 로직 적용
      if (tableFields.length > 0) {
        // 각 테이블별로 별도 시트 생성
        tableFields.forEach((table, tableIndex) => {
          const flattenedData = [];

          vehicles.forEach((v) => {
            // 단일 필드 데이터 수집 (파일명, 페이지)
            const baseSingleData = {
              파일명: v.fileName,
              페이지: v.pageNumber,
            };

            // 최상위 단일 필드 추가
            singleFields.forEach((field) => {
              const fieldKey = typeof field === "string" ? field : field.key;
              const displayName =
                typeof field === "string"
                  ? field
                  : field.displayName || field.key;
              baseSingleData[displayName] = v.current[fieldKey] || "-";
            });

            // 테이블 데이터 가져오기 (🔧 FIX: items 폴백 추가)
            let tableData = v.current[table.tableName] || [];
            if (tableData.length === 0 && v.current.items) {
              tableData = v.current.items;
            }

            if (Array.isArray(tableData) && tableData.length > 0) {
              // 테이블 행마다 단일 필드 반복
              tableData.forEach((row, rowIdx) => {
                const flatRow = { ...baseSingleData, 행번호: rowIdx + 1 };

                // 🔧 합성 필드는 각 ROW 데이터로 계산
                computedFields.forEach((cf) => {
                  const displayName = cf.displayName || cf.key;
                  flatRow[displayName] = evaluateComputedField(cf.formula, row);
                });

                // 테이블 컬럼 추가
                table.columns.forEach((col) => {
                  const displayName = col.displayName || col.key;
                  if (col.type === "computed" && col.formula) {
                    // 🔧 테이블 합성 필드 계산
                    let computed = col.formula;
                    const pattern = /\{([^}]+)\}/g;
                    let hasReplacement = false;

                    computed = computed.replace(pattern, (_, key) => {
                      hasReplacement = true;
                      const valKey = Object.keys(row).find(
                        (k) => k.toLowerCase() === key.toLowerCase(),
                      );
                      let val = valKey ? row[valKey] : "";
                      if (
                        typeof val === "string" &&
                        /^-?[\d,]+(\.\d+)?$/.test(val)
                      ) {
                        return val.replace(/,/g, "");
                      }
                      return val || 0;
                    });

                    if (hasReplacement && /^[\d+\-*/.() \s]+$/.test(computed)) {
                      try {
                        // eslint-disable-next-line no-eval
                        flatRow[displayName] = String(eval(computed));
                      } catch (e) {
                        flatRow[displayName] = computed;
                      }
                    } else {
                      // 문자열 결합 (재치환)
                      let stringComputed = col.formula;
                      stringComputed = stringComputed.replace(
                        pattern,
                        (_, key) => {
                          const valKey = Object.keys(row).find(
                            (k) => k.toLowerCase() === key.toLowerCase(),
                          );
                          return valKey ? row[valKey] : "";
                        },
                      );
                      flatRow[displayName] = stringComputed;
                    }
                  } else {
                    flatRow[displayName] = row[col.key] || "-";
                  }
                });
                flattenedData.push(flatRow);
              });
            } else {
              // 테이블 데이터 없으면 단일 필드만 출력
              const flatRow = { ...baseSingleData, 행번호: "-" };
              computedFields.forEach((cf) => {
                const displayName = cf.displayName || cf.key;
                flatRow[displayName] = "-";
              });
              table.columns.forEach((col) => {
                const displayName = col.displayName || col.key;
                flatRow[displayName] = "-";
              });
              flattenedData.push(flatRow);
            }
          });

          const ws = XLSX.utils.json_to_sheet(flattenedData);
          const sheetName = table.tableName.substring(0, 31); // Excel 시트 이름 제한
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });
      } else {
        // 기존 로직: 테이블 필드 없음 (단일 필드만)
        const data = vehicles.map((v) => {
          const baseData = {
            파일명: v.fileName,
            페이지: v.pageNumber,
          };

          // 합성 필드 추가
          computedFields.forEach((cf) => {
            const displayName = cf.displayName || cf.key;
            baseData[displayName] = evaluateComputedField(
              cf.formula,
              v.current,
            );
          });

          if (singleFields.length > 0) {
            singleFields.forEach((field) => {
              const fieldKey = typeof field === "string" ? field : field.key;
              const displayName =
                typeof field === "string"
                  ? field
                  : field.displayName || field.key;
              baseData[displayName] = v.current[fieldKey] || "-";
            });
          } else {
            // 템플릿이 없으면 모든 필드 출력
            Object.entries(v.current).forEach(([key, value]) => {
              if (
                ![
                  "documentType",
                  "pageResults",
                  "metadata",
                  "items",
                  "pageNumber",
                  "P",
                  "page",
                ].includes(key)
              ) {
                baseData[key] = value || "-";
              }
            });
          }
          return baseData;
        });

        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, "추출결과");
      }

      XLSX.writeFile(wb, fileName);
      console.log("✅ 엑셀 다운로드 완료:", fileName);
    } catch (e) {
      console.error("❌ 엑셀 다운로드 실패:", e);
      alert("엑셀 다운로드 실패: " + e.message);
    }
  };

  // 📦 JSON 다운로드 — json.txt 표준 인보이스 스키마로 best-effort 매핑
  //   awb / invoice / currency / shipper·buyer·consignee / tradeterm /
  //   shipping_cost / total_*_weight / commodities[]
  //   소스에 없는 항목(name/addr 분리, 운임 세부)은 빈값/null 로 둠
  const handleJsonDownload = () => {
    const fileName = `${documentType || "data"}_${new Date().toISOString().slice(0, 10)}.json`;
    console.log("🔍 [JSON] 다운로드 시작, viewMode:", viewMode);

    // 키 정규화: 소문자 + 구분자(공백/_/-/./괄호/슬래시) 제거
    const norm = (k) =>
      String(k || "")
        .toLowerCase()
        .replace(/[\s_\-()/.]/g, "");

    // 소스 객체에서 alias 목록에 맞는 첫 값을 반환 (정규화 매칭)
    const pick = (src, aliases) => {
      if (!src) return undefined;
      const normAliases = aliases.map(norm);
      for (const key of Object.keys(src)) {
        if (normAliases.includes(norm(key))) {
          const v = src[key];
          if (v !== undefined && v !== null && String(v).trim() !== "")
            return v;
        }
      }
      return undefined;
    };

    // 숫자 변환 (콤마 제거). 변환 불가 시 null
    const toNum = (v) => {
      if (v === undefined || v === null || String(v).trim() === "") return null;
      const n = parseFloat(String(v).replace(/,/g, "").trim());
      return isNaN(n) ? null : n;
    };
    // 문자열 변환. 없으면 "" (json.txt 패턴이 "string" 기본값이므로)
    const toStr = (v) =>
      v === undefined || v === null ? "" : String(v).trim();

    // ── 헤더(단일) 필드 소스: editedData ──
    const h = editedData || {};

    // ── commodities 소스: AUIGrid 우선 (동적 열 포함), 폴백 items/pageResults ──
    let rows = [];
    if (window.AUIGrid && window.AUIGrid.isCreated("#horizontal_grid")) {
      try {
        rows = window.AUIGrid.getGridData("#horizontal_grid") || [];
      } catch (err) {
        console.warn("⚠️ [JSON] AUIGrid 읽기 실패, 폴백:", err);
      }
    }
    if (rows.length === 0) {
      rows =
        (Array.isArray(h.items) && h.items) ||
        (Array.isArray(h.commodities) && h.commodities) ||
        [];
    }

    const commodities = rows.map((row, idx) => ({
      seq: toNum(pick(row, ["seq", "no", "sno", "rownum"])) ?? idx + 1,
      description: toStr(
        pick(row, [
          "description",
          "goods",
          "commodity",
          "item",
          "itemdescription",
          "goodsdescription",
          "productname",
          "product",
          "품명",
          "상품명",
        ]),
      ),
      tariff: toStr(
        pick(row, [
          "hscode",
          "tariff",
          "htscode",
          "hsk",
          "hskcode",
          "commoditycode",
          "세번",
        ]),
      ),
      origin: toStr(
        pick(row, [
          "coo",
          "origin",
          "countryoforigin",
          "madein",
          "co",
          "country",
          "원산지",
        ]),
      ),
      qty: toNum(
        pick(row, [
          "quantity",
          "qty",
          "pcs",
          "pieces",
          "units",
          "count",
          "수량",
        ]),
      ),
      unit_of_measure: toStr(
        pick(row, ["unittype", "unit", "uom", "unitofmeasure", "단위"]),
      ),
      unit_price: toNum(
        pick(row, ["unitprice", "price", "rate", "unitcost", "단가"]),
      ),
      value: toNum(
        pick(row, [
          "amount",
          "value",
          "linetotal",
          "lineamount",
          "extendedamount",
          "total",
          "금액",
        ]),
      ),
      net_weight: toNum(pick(row, ["netweight", "nw", "순중량"])),
      PO: toStr(
        pick(row, [
          "po",
          "pono",
          "purchaseorder",
          "ponumber",
          "orderno",
          "ordernumber",
        ]),
      ),
      Part: toStr(
        pick(row, [
          "partno",
          "part",
          "partnumber",
          "itemno",
          "modelno",
          "sku",
          "styleno",
          "productcode",
          "articleno",
        ]),
      ),
    }));

    const payload = {
      awb: toStr(
        pick(h, [
          "blnumber",
          "awb",
          "blno",
          "billoflading",
          "mawb",
          "hawb",
          "waybill",
        ]),
      ),
      invoice: {
        num: toStr(
          pick(h, ["invoicenumber", "invoiceno", "invno", "docno", "invoice"]),
        ),
        value: toNum(
          pick(h, [
            "totalamount",
            "totalvalue",
            "invoicevalue",
            "totalprice",
            "amount",
          ]),
        ),
      },
      currency: toStr(pick(h, ["currency", "curr"])),
      shipper: {
        name: toStr(
          pick(h, [
            "sellername",
            "shipper",
            "seller",
            "consignor",
            "exporter",
            "supplier",
          ]),
        ),
        addr: toStr(
          pick(h, ["selleraddress", "shipperaddress", "shipperaddr"]),
        ),
      },
      buyer: {
        name: toStr(pick(h, ["buyername", "buyer", "importer"])),
        addr: toStr(pick(h, ["buyeraddress", "buyeraddr", "billto"])),
      },
      consignee: {
        name: toStr(pick(h, ["consigneename", "consignee", "shipto"])),
        addr: toStr(pick(h, ["consigneeaddress", "consigneeaddr"])),
      },
      tradeterm: toStr(
        pick(h, [
          "incoterms",
          "tradeterms",
          "tradeterm",
          "deliveryterms",
          "paymentterms",
        ]),
      ),
      shipping_cost: {
        insurance: toNum(
          pick(h, ["insurance", "insuranceamount", "insurancecost"]),
        ),
        freight: toNum(
          pick(h, ["freight", "freightamount", "freightcost", "freightcharge"]),
        ),
        handling: toNum(pick(h, ["handling", "handlingcharge", "handlingfee"])),
        service_charge: toNum(pick(h, ["servicecharge", "servicefee"])),
      },
      total_net_weight: toNum(
        pick(h, ["netweight", "totalnetweight", "nw", "totalnw"]),
      ),
      total_gross_weight: toNum(
        pick(h, ["grossweight", "totalgrossweight", "gw", "totalgw"]),
      ),
      commodities,
    };

    try {
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log("✅ JSON 다운로드 완료:", fileName, payload);
    } catch (e) {
      console.error("❌ JSON 다운로드 실패:", e);
      alert("JSON 다운로드 실패: " + e.message);
    }
  };

  // 차량명 매핑 확인 및 검증 진행
  const proceedWithCarNameMapping = async (
    selectedMasterData,
    similarity,
    matchType,
  ) => {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? `http://${window.location.hostname}:3002`
        : "");
    const finalData = { ...editedData };

    // 그리드 모드인 경우 그리드에서 데이터 가져오기
    if (viewMode === "grid" && gridRef.current) {
      const gridData = window.AUIGrid.getGridData(gridRef.current);
      gridData.forEach((row) => {
        finalData[row.fieldName] = row.currentValue;
      });
    }

    // ⭐ 차량명을 선택한 기초자료의 영문명으로 변경
    if (selectedMasterData && selectedMasterData.car_name_en) {
      finalData.model = selectedMasterData.car_name_en.toUpperCase();

      // 매핑 저장 (학습)
      try {
        await fetch(`${API_BASE_URL}/api/vehicle-master/save-mapping`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ocrRecognizedName: carNameMappingData.ocrRecognizedName,
            masterId: selectedMasterData.master_id,
            carNameKr: selectedMasterData.car_name_kr,
            carNameEn: selectedMasterData.car_name_en,
            similarityScore: similarity,
            matchType: matchType,
            userId: "system",
          }),
        });
      } catch (err) {
        console.error("❌ 차량명 매핑 저장 실패:", err);
      }
    }

    // 실제 검증 진행
    await performValidation(finalData);
  };

  // 실제 검증 수행
  const performValidation = async (finalData) => {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? `http://${window.location.hostname}:3002`
        : "");

    // ⭐ 1. 주소 검증 가중치 저장
    if (finalData.address) {
      const ocrAddress =
        pageData.flashData?.address ||
        pageData.proData?.address ||
        finalData.address;
      const verifiedAddress = finalData.address;

      if (ocrAddress !== verifiedAddress && ocrAddress) {
        try {
          await fetch(`${API_BASE_URL}/api/address/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ocrRecognized: ocrAddress,
              verifiedAddress: verifiedAddress,
              userId: "system",
            }),
          });
        } catch (err) {
          console.error("❌ 주소 검증 가중치 저장 실패:", err);
        }
      }
    }

    finalData.documentType = pageData.documentType;
    finalData.pageNumber = currentPage;
    finalData.metadata = {
      ...finalData.metadata,
      matchRate: matchRate,
      userEdited: true,
      editedAt: new Date().toISOString(),
      flashData: pageData.flashData,
      proData: pageData.proData,
    };

    // 검증 완료 처리
    if (fileId && onValidatePage) {
      await onValidatePage(fileId, currentPage, finalData);
    }

    // 팝업 닫기
    setShowCarNameMappingModal(false);
    setCarNameMappingData(null);
    setSimilarMatches([]);
  };

  // 검증 버튼 클릭 핸들러 (차량명 매핑 팝업 표시)
  const handleValidateClick = async () => {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? `http://${window.location.hostname}:3002`
        : "");
    const currentData = { ...editedData };

    // 그리드 모드인 경우 그리드에서 데이터 가져오기
    if (viewMode === "grid" && gridRef.current) {
      const gridData = window.AUIGrid.getGridData(gridRef.current);
      gridData.forEach((row) => {
        currentData[row.fieldName] = row.currentValue;
      });
    }

    // 차량명이 있으면 매핑 팝업 표시
    if (currentData.model) {
      const ocrCarName = currentData.model;

      // 기초자료 조회
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(ocrCarName)}`,
        );

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            // 정확한 매칭 또는 학습된 매핑이 있는 경우
            // 단, 영문명이 있는 경우에만 masterData로 설정 (저장된 데이터만 표시)
            const masterData = result.data;
            const hasEnglishName =
              (masterData.BASE_CAR_NM_ENG &&
                masterData.BASE_CAR_NM_ENG.trim() !== "") ||
              (masterData.car_name_en && masterData.car_name_en.trim() !== "");

            setCarNameMappingData({
              ocrRecognizedName: ocrCarName,
              currentData: currentData,
              masterData: hasEnglishName ? masterData : null, // 영문명이 없으면 null로 설정
              isExactMatch: !result.isSimilar,
            });
            setShowCarNameMappingModal(true);
          } else {
            // 유사도 검색
            setSearchingSimilarity(true);
            const similarResponse = await fetch(
              `${API_BASE_URL}/api/vehicle-master/search-similar/${encodeURIComponent(ocrCarName)}?threshold=0.6`,
            );

            if (similarResponse.ok) {
              const similarResult = await similarResponse.json();
              if (similarResult.success && similarResult.results.length > 0) {
                setCarNameMappingData({
                  ocrRecognizedName: ocrCarName,
                  currentData: currentData,
                  masterData: null,
                  isExactMatch: false,
                });
                setSimilarMatches(similarResult.results);
                setShowCarNameMappingModal(true);
              } else {
                // 매핑 없음 - 직접 입력 모드
                setCarNameMappingData({
                  ocrRecognizedName: ocrCarName,
                  currentData: currentData,
                  masterData: null,
                  isExactMatch: false,
                });
                setShowCarNameMappingModal(true);
              }
            } else {
              // 검색 실패 - 직접 입력 모드
              setCarNameMappingData({
                ocrRecognizedName: ocrCarName,
                currentData: currentData,
                masterData: null,
                isExactMatch: false,
              });
              setShowCarNameMappingModal(true);
            }
            setSearchingSimilarity(false);
          }
        } else {
          // 검색 실패 - 직접 입력 모드
          setCarNameMappingData({
            ocrRecognizedName: ocrCarName,
            currentData: currentData,
            masterData: null,
            isExactMatch: false,
          });
          setShowCarNameMappingModal(true);
        }
      } catch (err) {
        console.error("❌ 차량 기초자료 조회 실패:", err);
        // 오류 발생 시 직접 입력 모드
        setCarNameMappingData({
          ocrRecognizedName: ocrCarName,
          currentData: currentData,
          masterData: null,
          isExactMatch: false,
        });
        setShowCarNameMappingModal(true);
      }
    } else {
      // 차량명이 없으면 바로 검증 진행
      await performValidation(currentData);
    }
  };

  // 검증 완료 (차량등록은 3.차량등록매핑 탭에서 수행) - 레거시 호환
  const handleConfirm = async () => {
    await handleValidateClick();
  };

  // 기초자료 저장 후 모달 갱신
  useEffect(() => {
    const handleVehicleMasterSaved = (event) => {
      if (showCarNameMappingModal && carNameMappingData) {
        const savedData = event.detail;
        // 저장된 차량명이 OCR 인식 차량명과 일치하는 경우에만 모달 갱신
        if (
          savedData.BASE_CAR_NM &&
          savedData.BASE_CAR_NM.trim() ===
            carNameMappingData.ocrRecognizedName.trim()
        ) {
          // 저장된 데이터로 masterData 업데이트
          setCarNameMappingData((prev) => ({
            ...prev,
            masterData: savedData, // 저장된 데이터로 갱신
          }));
        }
      }
    };

    window.addEventListener("vehicleMasterSaved", handleVehicleMasterSaved);
    return () => {
      window.removeEventListener(
        "vehicleMasterSaved",
        handleVehicleMasterSaved,
      );
    };
  }, [showCarNameMappingModal, carNameMappingData]);

  // 데이터가 없으면 처리 대기 중 상태 표시 (우선순위 1)
  // 🔧 단, selectedTemplateId가 있고 matchedTemplate이 있으면 템플릿 필드 미리보기 표시
  const hasData =
    pageData.flashData ||
    pageData.proData ||
    pageData.extractedData ||
    (pageResults && pageResults.length > 0);
  const hasTemplatePreview = selectedTemplateId && matchedTemplate;

  if (!hasData && !hasTemplatePreview) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200">
          <h2 className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <div className="animate-pulse inline-block text-xs">⏳</div>
            문서 처리 실행 대기 중
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">📄</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              파일 업로드 완료
            </h3>
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              <span className="font-medium text-gray-700">일괄 처리</span>{" "}
              버튼을 클릭하면
              <br />
              실시간으로 처리 결과가 여기에 표시됩니다.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>
                  <span className="font-medium">분류</span> →{" "}
                  <span className="font-medium">추출</span> →{" "}
                  <span className="font-medium">검증</span>
                </p>
                <p className="text-gray-400 text-[11px]">
                  각 단계가 실시간으로 업데이트됩니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔧 템플릿 미리보기 모드: 데이터는 없지만 템플릿이 선택된 경우
  if (!hasData && hasTemplatePreview) {
    const singleFields =
      matchedTemplate.singleFields || matchedTemplate.fields || [];
    const tableFields = matchedTemplate.tableFields || [];
    const computedFields = matchedTemplate.computedFields || [];

    // 🚀 모든 필드를 outputOrder 기준으로 통합
    const allPreviewFields = [];

    // 단일 필드
    singleFields.forEach((f, idx) => {
      const field = typeof f === "string" ? { key: f } : f;
      const order = field.outputOrder ?? idx + 1;
      if (order > 0) {
        allPreviewFields.push({
          key: field.key,
          displayName: field.displayName || field.key,
          type: field.type || "string",
          order: order,
          fieldType: "single",
        });
      }
    });

    // 테이블 필드 컬럼
    tableFields.forEach((table) => {
      (table.columns || []).forEach((col, colIdx) => {
        const order = col.outputOrder ?? 100 + colIdx + 1;
        allPreviewFields.push({
          key: col.key,
          displayName: col.displayName || col.key,
          type: col.type || "string",
          order: order,
          fieldType: "table",
          tableName: table.tableName,
        });
      });
    });

    // 합성 필드
    computedFields.forEach((cf, idx) => {
      const order = cf.outputOrder ?? cf.order ?? 200 + idx + 1;
      if (order > 0) {
        allPreviewFields.push({
          key: cf.key,
          displayName: cf.displayName || cf.key,
          type: "computed",
          order: order,
          fieldType: "computed",
          formula: cf.formula,
        });
      }
    });

    // outputOrder 기준 정렬
    allPreviewFields.sort((a, b) => a.order - b.order);

    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* 헤더 - AI Studio 스타일 */}
        <div className="flex-shrink-0 px-3 py-1 border-b border-gray-200">
          <h2 className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
            <span className="text-gray-400">📋</span>
            {matchedTemplate.name}
            <span className="text-gray-300 ml-1">
              ({allPreviewFields.length}개 필드)
            </span>
          </h2>
        </div>
        <div className="flex-1 overflow-auto">
          {/* 통합 필드 미리보기 - 슬림 테이블 형식 */}
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100 bg-gray-50 sticky top-0">
                <th className="py-0.5 px-2 text-left w-6 font-normal">NO.</th>
                <th className="py-0.5 px-2 text-left font-normal">필드명</th>
                <th className="py-0.5 px-2 text-left w-12 font-normal">타입</th>
                <th className="py-0.5 px-2 text-left font-normal">값</th>
              </tr>
            </thead>
            <tbody>
              {allPreviewFields.map((field, idx) => (
                <tr
                  key={idx}
                  className={`border-b border-gray-50 hover:bg-gray-50/50 ${
                    field.fieldType === "computed"
                      ? "bg-gray-50/30"
                      : field.fieldType === "table"
                        ? "bg-blue-50/30"
                        : ""
                  }`}
                >
                  <td className="py-0.5 px-2 text-gray-300">{idx + 1}</td>
                  <td className="py-0.5 px-2 text-gray-700">
                    {field.displayName}
                    {field.fieldType === "computed" && (
                      <span className="ml-1 text-gray-500 text-[9px]">
                        합성
                      </span>
                    )}
                    {field.fieldType === "table" && (
                      <span className="ml-1 text-blue-400 text-[9px]">
                        {field.tableName}
                      </span>
                    )}
                  </td>
                  <td className="py-0.5 px-2 text-gray-400">{field.type}</td>
                  <td className="py-0.5 px-2">
                    <span className="text-gray-300 italic">실행 후 표시</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 체크된 페이지가 하나도 없을 때만 안내 메시지 표시
  // 🔧 변경: 현재 페이지 해제 여부가 아닌, 체크된 페이지가 있는지 확인
  const hasCheckedPages = checkedPages && checkedPages.length > 0;
  console.log(
    "🚨 [RENDER GATE] hasCheckedPages:",
    hasCheckedPages,
    "checkedPages:",
    checkedPages,
    "viewMode:",
    viewMode,
  );
  if (!hasCheckedPages) {
    console.log("🚨 [RENDER GATE] EARLY RETURN — checkedPages 비어있음!");
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-xl">📄</span>
          </div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            페이지가 선택되지 않음
          </h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">
            왼쪽 페이지 목록에서 체크박스를 선택하면
            <br />
            해당 페이지의 결과를 확인할 수 있습니다.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <p className="text-xs text-gray-600">
              💡 <span className="font-medium">전체 선택</span>을 클릭하면 모든
              페이지를 확인할 수 있습니다.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex-1 min-h-0 flex flex-row overflow-hidden">
      {/* 🔧 그리드 영역 - AI 패널이 열리면 자동으로 축소 */}
      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{ minWidth: 0 }}
      >
        {/* 🆕 PaddleOCR 1차 추출 결과 비교 배너 */}
        {ocrEnabled && paddleOcrResult && (
          <div className="px-2 py-1.5 border-b border-emerald-200 bg-emerald-50 text-[12px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-emerald-700">
                🟢 OCR (PaddleOCR)
              </span>
              <span className="text-emerald-600">
                doc_type=<b>{paddleOcrResult.doc_type}</b>
              </span>
              <span className="text-emerald-600">
                {paddleOcrResult.ocr_ms
                  ? `${Math.round(paddleOcrResult.ocr_ms)}ms`
                  : "?"}
              </span>
              <span className="text-emerald-600">
                conf=<b>{paddleOcrResult.overall_confidence ?? "?"}</b>
              </span>
              {paddleOcrResult.classifier?.matched_keyword && (
                <span className="text-emerald-500 text-[11px]">
                  ({paddleOcrResult.classifier.matched_keyword})
                </span>
              )}
            </div>
            {paddleOcrResult.fields &&
              (() => {
                // 🆕 자체검증: 패턴/규칙 위반시 의심 표시
                const validate = (key, val) => {
                  if (val === null || val === undefined || val === "")
                    return "empty";
                  const s = String(val).trim();
                  switch (key) {
                    case "plate_no":
                      // 한국 번호판: [지역]?\d{2,3}[가-힣]\s?\d{4}
                      return /^(\D{2,3}\s?)?\d{2,3}[가-힣]\s?\d{4}$/.test(s)
                        ? "ok"
                        : "invalid";
                    case "vin":
                      if (s.length !== 17) return "invalid";
                      if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(s)) return "invalid";
                      if (!/\d/.test(s) || (s.match(/[A-Z]/g) || []).length < 3)
                        return "invalid";
                      return "ok";
                    case "year": {
                      const y = parseInt(s, 10);
                      return Number.isFinite(y) && y >= 1990 && y <= 2030
                        ? "ok"
                        : "invalid";
                    }
                    case "owner_id_no":
                      // 주민번호 13자리 또는 YYYY-MM-DD 또는 마스킹
                      return /^(\d{6}-?\d{7}|\d{4}-\d{2}-\d{2}|\d{6}\*{1,7}|\d{13})$/.test(
                        s.replace(/\s/g, ""),
                      )
                        ? "ok"
                        : "suspect";
                    case "mileage_km": {
                      const n = parseInt(s.replace(/[^\d]/g, ""), 10);
                      return Number.isFinite(n) && n >= 0 && n <= 9999999
                        ? "ok"
                        : "invalid";
                    }
                    case "canceled_date":
                    case "registration_date":
                    case "issue_date":
                      return /^\d{4}[-./]\d{1,2}[-./]\d{1,2}$|^\d{8}$/.test(
                        s.replace(/\s/g, ""),
                      )
                        ? "ok"
                        : "suspect";
                    case "vehicle_weight_kg":
                    case "displacement_cc":
                    case "length_mm":
                    case "width_mm":
                    case "height_mm": {
                      const n = parseInt(s.replace(/[^\d]/g, ""), 10);
                      return Number.isFinite(n) && n > 0 ? "ok" : "invalid";
                    }
                    case "car_name":
                      return s.length >= 2 ? "ok" : "suspect";
                    case "owner_name":
                      return (s.length >= 2 &&
                        !/[a-z]/i.test(s.split("").slice(0, 3).join(""))) ||
                        /[A-Z]/.test(s)
                        ? "ok"
                        : "suspect";
                    case "owner_address":
                      return s.length >= 5 ? "ok" : "suspect";
                    default:
                      return "ok";
                  }
                };
                const colorClass = (status) => {
                  switch (status) {
                    case "ok":
                      return "font-medium text-gray-900";
                    case "empty":
                      return "text-red-400 italic";
                    case "invalid":
                      return "text-red-600 font-semibold bg-red-50 px-1 rounded";
                    case "suspect":
                      return "text-amber-600 font-medium";
                    default:
                      return "";
                  }
                };
                return (
                  <div className="mt-1 grid grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-gray-700">
                    {[
                      ["plate_no", "자동차등록번호"],
                      ["vin", "차대번호"],
                      ["car_name", "차량명"],
                      ["year", "모델연도"],
                      ["owner_name", "성명"],
                      ["owner_id_no", "생년월일"],
                      ["mileage_km", "주행거리"],
                      ["vehicle_weight_kg", "총중량"],
                      ["displacement_cc", "배기량"],
                      ["length_mm", "가로"],
                      ["width_mm", "세로"],
                      ["height_mm", "높이"],
                      ["owner_address", "사용본거지"],
                      ["canceled_date", "말소등록일"],
                    ].map(([k, label]) => {
                      const v = paddleOcrResult.fields[k];
                      // 차량명은 영문만 추출 후 표시
                      let display = v;
                      if (k === "car_name" && v) {
                        const m = String(v).match(/[(](.+?)[)]/);
                        if (m && /^[A-Z][A-Z0-9 .]{1,}$/i.test(m[1].trim())) {
                          display = m[1].trim().toUpperCase();
                        } else {
                          const en = String(v).match(/[A-Z][A-Z0-9 .]{1,}/i);
                          if (en) display = en[0].trim().toUpperCase();
                        }
                      }
                      const status = validate(k, display);
                      const isEmpty = status === "empty";
                      return (
                        <div
                          key={k}
                          className="truncate"
                          title={`${label} (${k}): ${display ?? "(none)"} [${status}]`}
                        >
                          <span className="text-gray-500">{label}:</span>{" "}
                          <span className={colorClass(status)}>
                            {isEmpty ? "(none)" : String(display)}
                            {status === "invalid" && " ⚠"}
                            {status === "suspect" && " ?"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            {paddleOcrResult.vllm_used &&
              paddleOcrResult.vllm_result?.fields && (
                <div className="mt-1.5 pt-1.5 border-t border-emerald-200">
                  <span className="font-semibold text-purple-700">
                    🟣 vLLM 검증
                  </span>{" "}
                  <span className="text-purple-600 text-[11px]">
                    ({paddleOcrResult.vllm_result.elapsed_ms}ms,{" "}
                    {paddleOcrResult.vllm_result.model})
                  </span>
                  <div className="mt-1 grid grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-gray-700">
                    {Object.entries(paddleOcrResult.vllm_result.fields)
                      .filter(
                        ([k, v]) => v !== null && v !== undefined && v !== "",
                      )
                      .slice(0, 12)
                      .map(([k, v]) => {
                        const ocrVal = paddleOcrResult.fields?.[k];
                        const same = String(ocrVal ?? "") === String(v ?? "");
                        return (
                          <div
                            key={k}
                            className="truncate"
                            title={`${k}: ${v}`}
                          >
                            <span className="text-gray-500">{k}:</span>{" "}
                            <span
                              className={`font-medium ${same ? "" : "text-red-600"}`}
                            >
                              {String(v)}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
          </div>
        )}
        {/* 🆕 Donut 1차 모델 추출 결과 비교 배너 (PaddleOCR과 별개, 성능 비교용) */}
        {donutEnabled && donutResult && (
          <div className="px-2 py-1.5 border-b border-purple-200 bg-purple-50 text-[12px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-purple-700">
                🟣 OCR (Donut)
              </span>
              <span className="text-purple-600">
                doc_type=<b>{donutResult.doc_type}</b>
              </span>
              <span className="text-purple-600">
                {donutResult.elapsed_ms
                  ? `${Math.round(donutResult.elapsed_ms)}ms`
                  : "?"}
              </span>
              <span className="text-purple-500 text-[11px]">
                ({donutResult.model || "donut-v1"})
              </span>
              {donutResult.fields?._parse_error && (
                <span className="text-red-600 text-[11px]">JSON 파싱 실패</span>
              )}
            </div>
            {donutResult.fields && !donutResult.fields._parse_error && (
              <div className="mt-1 grid grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-gray-700">
                {[
                  ["plate_no", "자동차등록번호"],
                  ["vin", "차대번호"],
                  ["car_name", "차량명"],
                  ["year", "모델연도"],
                  ["owner_name", "성명"],
                  ["owner_id_no", "생년월일"],
                  ["mileage_km", "주행거리"],
                  ["vehicle_weight_kg", "총중량"],
                  ["displacement_cc", "배기량"],
                  ["length_cm", "가로"],
                  ["width_cm", "세로"],
                  ["height_cm", "높이"],
                  ["owner_address", "사용본거지"],
                  ["canceled_date", "말소등록일"],
                  ["registration_date", "최초등록일"],
                ].map(([k, label]) => {
                  const v = donutResult.fields[k];
                  const paddleV = paddleOcrResult?.fields?.[k];
                  const isEmpty = v === null || v === undefined || v === "";
                  // PaddleOCR 결과와 동일 여부 (둘 다 있을 때만 비교)
                  const isDifferent =
                    !isEmpty &&
                    paddleV != null &&
                    String(v) !== String(paddleV);
                  return (
                    <div
                      key={k}
                      className="truncate"
                      title={`${label} (${k}): ${v ?? "(none)"}${paddleV != null ? ` | paddle: ${paddleV}` : ""}`}
                    >
                      <span className="text-gray-500">{label}:</span>{" "}
                      <span
                        className={`${isEmpty ? "text-red-400 italic" : isDifferent ? "text-amber-600 font-semibold" : "font-medium text-gray-900"}`}
                      >
                        {isEmpty ? "(none)" : String(v)}
                        {isDifferent && " Δ"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* 통합 컨트롤 바 (탭 버튼 + 필터 + 뷰모드) */}
        <div className="flex items-center gap-1 px-1 py-1 border-b border-gray-200 bg-gray-50 flex-shrink-0 text-xs">
          {/* 탭 버튼 */}
          {/* 탭 버튼은 RightPanelTabs에서 관리 (항상 표시) */}
          {false && onTabChange && (
            <>
              <button
                onClick={() => onTabChange("fields")}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                  activeTab === "fields"
                    ? "text-blue-600 bg-blue-50"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Settings className="w-3.5 h-3.5" />
                템플릿
                {hasTabChanges && (
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
                )}
              </button>
              <button
                onClick={() => onTabChange("results")}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                  activeTab === "results"
                    ? "text-blue-600 bg-blue-50"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Table2 className="w-3.5 h-3.5" />
                추출결과
              </button>
              <div className="w-px h-3.5 bg-gray-300" />
            </>
          )}
          {/* P1 검증 상태 + 전체/단일/테이블 필터 제거 — 공간 절약, columnFilter='all' 기본값 유지 */}
          <div className="flex-1" />
          {/* 🔧 교차 검증 비교 버튼 */}
          {console.error("🔧 [교차비교 버튼]", {
            hasCrossValidation: !!crossValidation,
            hasItems: !!crossValidation?.crossItems,
            count: crossValidation?.crossItems?.length,
          })}
          {crossValidation && crossValidation.crossItems && (
            <>
              {/* 인라인 교차검증 토글 */}
              <button
                onClick={() => {
                  setShowInlineCrossValidation((prev) => !prev);
                  if (showInlineCrossValidation) setInlineCvColumnFilter(null);
                }}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition-colors ${
                  showInlineCrossValidation
                    ? "text-white bg-[#444746] hover:bg-[#3c4043] shadow-sm"
                    : "text-[#444746] bg-[#f1f3f4] hover:bg-[#e8eaed] border border-[#dadce0]"
                }`}
                title="하단에 검증 비교 그리드 표시/숨김"
              >
                <GitCompare className="w-3.5 h-3.5" />
                {crossValidation.verificationMode === "sequential"
                  ? "🔍"
                  : "⚡"}{" "}
                검증 {cellLevelMatchRate ?? crossValidation.matchRate}%
              </button>
              {/* 프롬프트 품질 리포트 배지 */}
              {crossValidation.promptQualityReport &&
                crossValidation.promptQualityReport.status !== "pass" && (
                  <button
                    onClick={() => {
                      const report = crossValidation.promptQualityReport;
                      const issueText = report.issues
                        .map(
                          (i) =>
                            `[${i.severity.toUpperCase()}] ${i.field}: ${i.message}`,
                        )
                        .join("\n\n");
                      alert(
                        `프롬프트 품질 리포트 (${report.status})\n점수: ${report.score}\n\n${report.summary}\n\n--- 상세 이슈 ---\n${issueText}`,
                      );
                    }}
                    className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition-colors ${
                      crossValidation.promptQualityReport.status === "critical"
                        ? "text-white bg-red-500 hover:bg-red-600"
                        : crossValidation.promptQualityReport.status === "warn"
                          ? "text-white bg-amber-500 hover:bg-amber-600"
                          : "text-[#5f6368] bg-blue-100 hover:bg-blue-200"
                    }`}
                    title={crossValidation.promptQualityReport.summary}
                  >
                    {crossValidation.promptQualityReport.status === "critical"
                      ? "!!"
                      : "!"}
                    프롬프트 {crossValidation.promptQualityReport.issues.length}
                    건
                  </button>
                )}
              {/* 팝업 교차비교 (기존) */}
              <button
                onClick={() => setShowCrossValidationModal(true)}
                className="flex items-center gap-1 px-1.5 py-1 text-xs font-medium rounded transition-colors text-[#5f6368] hover:text-[#1f1f1f] hover:bg-[#f1f3f4]"
                title="교차 검증 결과를 풀스크린 팝업으로 비교 (PDF 뷰어 포함)"
              >
                ↗
              </button>
            </>
          )}
          {/* 뷰 모드 토글 */}
          <button
            onClick={() => setViewMode("form")}
            className={`p-0.5 rounded transition ${viewMode === "form" ? "bg-gray-900 text-white" : "text-gray-400 hover:bg-gray-200"}`}
            title="폼 모드"
          >
            <List className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setViewMode("horizontal")}
            className={`p-0.5 rounded transition ${viewMode === "horizontal" ? "bg-gray-900 text-white" : "text-gray-400 hover:bg-gray-200"}`}
            title="가로 그리드"
          >
            <Table className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleExcelDownload}
            className="p-0.5 rounded transition text-[#5f6368] hover:bg-[#f1f3f4]"
            title="엑셀 다운로드"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleJsonDownload}
            className="p-0.5 rounded transition text-[#5f6368] hover:bg-[#f1f3f4]"
            title="JSON 다운로드 (표준 인보이스 스키마)"
          >
            <FileJson className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-3.5 bg-gray-300" />
          <div className="w-px h-3.5 bg-gray-300" />
          {/* 모노톤 통일 — Google AI Studio 풍 (white bg / 회색 border / 텍스트만 강조) */}
          {(() => {
            const btnBase =
              "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors border border-[#dadce0] bg-white text-[#3c4043] hover:bg-[#f1f3f4]";
            const btnDisabled = "opacity-50 cursor-wait";
            return (
              <>
                {/* AI 검색으로 보내기 — sessionStorage queue + AI HS 분류 메뉴 navigate */}
                <button
                  onClick={handleSendToAiSearch}
                  className={btnBase}
                  title="그리드의 description 있는 모든 행을 AI HS 분류 메뉴 (다중검색 탭) 로 전달 — 행 클릭으로 검색/온톨로지/evidence 검증"
                >
                  <Search className="w-3.5 h-3.5" />
                  AI 검색으로 보내기
                </button>
                {/* AI HS 자동분류 (5-layer evidence, 빈 HSCode cell 만) */}
                <button
                  onClick={handleAutoHsClassify}
                  disabled={isHsClassifying}
                  className={`${btnBase} ${isHsClassifying ? btnDisabled : ""}`}
                  title="빈 HSCode 행만 5-layer (선례/본문/의미/Gemini) 분류"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {isHsClassifying ? "HS분류중…" : "HS자동분류"}
                </button>
                {hsClassifyStats && (
                  <span
                    className="text-[10px] text-[#5f6368]"
                    title={`high ${hsClassifyStats.confidence_distribution?.high || 0} / mid ${hsClassifyStats.confidence_distribution?.medium || 0} / low ${hsClassifyStats.confidence_distribution?.low || 0}`}
                  >
                    {hsClassifyStats.success}/{hsClassifyStats.total} (H
                    {hsClassifyStats.confidence_distribution?.high || 0}/M
                    {hsClassifyStats.confidence_distribution?.medium || 0}/L
                    {hsClassifyStats.confidence_distribution?.low || 0})
                  </span>
                )}
                {/* VLM 파인튜닝 데이터 저장 — 개발용. loginHS 일반 사용자에게 숨김 */}
                {!IS_LOGINHS_PRODUCT && (
                  <>
                    <button
                      onClick={handleSaveVLMData}
                      disabled={vlmSaveStatus === "saving"}
                      className={`${btnBase} ${vlmSaveStatus === "saving" ? btnDisabled : ""}`}
                      title="현재 페이지 이미지+추출결과를 VLM 파인튜닝 샘플로 저장"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {vlmSaveStatus === "saving"
                        ? "VLM저장중…"
                        : vlmSaveStatus === "saved"
                          ? "VLM저장완료"
                          : vlmSaveStatus === "error"
                            ? "VLM저장실패"
                            : "VLM학습저장"}
                    </button>
                    <button
                      onClick={() => setShowVLMFineTuning(true)}
                      className={btnBase}
                      title="VLM 파인튜닝 데이터 관리 / JSONL 생성 / 원격 서버"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      파인튜닝
                    </button>
                  </>
                )}
              </>
            );
          })()}
          {!IS_LOGINHS_PRODUCT && <HarnessWorkflowGuide className="ml-1" />}
        </div>

        {/* 결과 표시 영역 */}
        {console.log("🎯 [Render] viewMode:", viewMode)}
        <div className="flex-1 overflow-hidden">
          {viewMode === "form" ? (
            <div className="h-full overflow-auto">{renderForm()}</div>
          ) : viewMode === "grid" ? (
            renderGrid()
          ) : (
            renderHorizontalGrid()
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-gray-50">
          {/* 검증 버튼 영역 - 🔧 TODO: 추후 삭제 예정, 현재 hidden */}
          <div className="hidden flex gap-2">
            {/* 현재 페이지 검증 */}
            <button
              onClick={handleValidateClick}
              disabled={validatedPages[currentPage]}
              className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                validatedPages[currentPage]
                  ? "bg-gray-300 text-gray-600 cursor-default"
                  : "bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800"
              }`}
              title={
                validatedPages[currentPage]
                  ? "이미 검증 완료됨"
                  : "현재 페이지 검증"
              }
            >
              {validatedPages[currentPage] ? "✓ 검증완료" : `현재 페이지 검증`}
            </button>

            {/* 전체 검증 */}
            {totalPages > 1 && (
              <button
                onClick={() => {
                  if (fileId && onValidateAllPages) {
                    // ⭐ editedData를 전달하여 수정된 데이터로 검증
                    onValidateAllPages(fileId, totalPages, editedData);
                  }
                }}
                className="flex-1 px-4 py-2 rounded text-sm font-medium transition-colors bg-gray-500 text-white hover:bg-gray-600 active:bg-gray-700"
                title={`전체 ${totalPages}개 페이지 일괄 검증`}
              >
                전체 검증
              </button>
            )}
          </div>

          {/* 검증 완료 안내 - 회색계열 */}
          {validatedPages[currentPage] && (
            <div className="mt-2 space-y-2">
              <div className="text-xs text-gray-600 bg-gray-100 px-3 py-2 rounded border border-gray-300 flex items-center gap-2">
                <span className="text-base">✓</span>
                <span>
                  페이지 {currentPage} 검증 완료 - 3.차량등록매핑 탭에서 차량을
                  등록하세요
                </span>
              </div>

              {/* ⭐ 기초자료에서 가져온 필드 표시 */}
              {pageData.extractedData?.metadata?.filledFromMaster &&
                pageData.extractedData.metadata.filledFromMaster.length > 0 && (
                  <div className="text-xs bg-blue-50 px-3 py-2 rounded border border-blue-200 flex items-start gap-2">
                    <span className="text-blue-600 font-semibold">📋</span>
                    <div className="flex-1">
                      <span className="text-blue-700 font-semibold">
                        기초자료에서 자동으로 가져온 필드:
                      </span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {pageData.extractedData.metadata.filledFromMaster.map(
                          (field, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                            >
                              {field}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {/* ⭐ 기초자료에 있는 차량이지만 현재 값으로 업데이트할 수 있는 옵션 */}
              {pageData.extractedData?.metadata?.masterDataFound && (
                <div className="text-xs bg-green-50 px-3 py-2 rounded border border-green-200 flex items-start gap-2">
                  <span className="text-green-600 font-semibold">✓</span>
                  <div className="flex-1">
                    <span className="text-green-700 font-semibold">
                      차량명이 기초자료에 있습니다. 현재 값으로 기초자료를
                      업데이트하시겠습니까?
                    </span>
                    <div className="mt-1">
                      <button
                        onClick={() => {
                          const currentData = pageData.extractedData;
                          const masterData = currentData.metadata?.masterData;
                          setEditingMasterId(masterData?.BASE_CAR_NO || null);
                          setNewVehicleData({
                            BASE_CAR_NM:
                              masterData?.BASE_CAR_NM ||
                              currentData.model ||
                              "",
                            BASE_CAR_NM_ENG:
                              currentData.model ||
                              masterData?.BASE_CAR_NM_ENG ||
                              "",
                            BASE_CAR_MAKER:
                              currentData.brand ||
                              masterData?.BASE_CAR_MAKER ||
                              "",
                            BASE_CAR_YEAR:
                              currentData.modelYear ||
                              masterData?.BASE_CAR_YEAR ||
                              "",
                            BASE_CAR_G_PUM: masterData?.BASE_CAR_G_PUM || "",
                            BASE_CAR_PUM: masterData?.BASE_CAR_PUM || "",
                            BASE_CAR_CC:
                              currentData.displacement ||
                              masterData?.BASE_CAR_CC ||
                              "",
                            BASE_CAR_WEIGHT:
                              currentData.totalWeight ||
                              masterData?.BASE_CAR_WEIGHT ||
                              "",
                            BASE_CAR_CBM:
                              currentData.cbm || masterData?.BASE_CAR_CBM || "",
                          });
                          setShowAddVehicleModal(true);
                        }}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700"
                      >
                        현재 값으로 기초자료 업데이트
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ⭐ 차량명이 기초자료에 없을 경우 추가 옵션 */}
              {pageData.extractedData?.metadata?.masterDataNotFound && (
                <div className="text-xs bg-gray-50 px-3 py-2 rounded border border-gray-200 flex items-start gap-2">
                  <span className="text-gray-600 font-semibold">⚠️</span>
                  <div className="flex-1">
                    <span className="text-yellow-700 font-semibold">
                      차량명 "{pageData.extractedData.metadata.carName}"이
                      기초자료에 없습니다.
                    </span>
                    <div className="mt-1">
                      <button
                        onClick={() => {
                          setNewVehicleData({
                            car_name_kr:
                              pageData.extractedData.metadata.carName || "",
                            car_name_en: "",
                            brand: pageData.extractedData.brand || "",
                            model_year: pageData.extractedData.modelYear || "",
                            trade_item_name: "",
                            item_name: "",
                            displacement:
                              pageData.extractedData.displacement || "",
                            weight: pageData.extractedData.totalWeight || "",
                            cbm: pageData.extractedData.cbm || "",
                          });
                          setShowAddVehicleModal(true);
                        }}
                        className="px-2 py-1 bg-yellow-600 text-white rounded text-xs font-medium hover:bg-yellow-700"
                      >
                        기초자료에 추가
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 차량명 매핑 팝업 */}
        {showCarNameMappingModal && carNameMappingData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-[52vw] h-[80vh] flex flex-col">
              {/* 헤더 */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3
                  className="text-lg font-semibold"
                  style={{ color: "#0A1037" }}
                >
                  차량명 매핑 확인
                </h3>
                <div className="flex items-center gap-2">
                  {/* 확인/취소 버튼 */}
                  {carNameMappingData.masterData &&
                    carNameMappingData.isExactMatch && (
                      <button
                        onClick={() =>
                          proceedWithCarNameMapping(
                            carNameMappingData.masterData,
                            1.0,
                            "exact",
                          )
                        }
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                      >
                        확인 (영문명:{" "}
                        {carNameMappingData.masterData.BASE_CAR_NM_ENG ||
                          carNameMappingData.masterData.car_name_en?.toUpperCase() ||
                          ""}
                        )
                      </button>
                    )}
                  {similarMatches.length > 0 &&
                    !carNameMappingData.masterData && (
                      <button
                        onClick={() => {
                          if (similarMatches[0]) {
                            proceedWithCarNameMapping(
                              similarMatches[0].data,
                              similarMatches[0].similarity,
                              "similar",
                            );
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                      >
                        확인 (영문명:{" "}
                        {similarMatches[0]?.data?.BASE_CAR_NM_ENG ||
                          similarMatches[0]?.data?.car_name_en?.toUpperCase() ||
                          ""}
                        )
                      </button>
                    )}
                  {!carNameMappingData.masterData &&
                    similarMatches.length === 0 &&
                    carNameMappingData.manualCarNameEn && (
                      <button
                        onClick={() => {
                          if (carNameMappingData.manualCarNameEn) {
                            const finalData = {
                              ...carNameMappingData.currentData,
                            };
                            finalData.model =
                              carNameMappingData.manualCarNameEn;
                            performValidation(finalData);
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                      >
                        확인 (영문명: {carNameMappingData.manualCarNameEn})
                      </button>
                    )}
                  <button
                    onClick={() => {
                      setShowCarNameMappingModal(false);
                      setCarNameMappingData(null);
                      setSimilarMatches([]);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => {
                      setShowCarNameMappingModal(false);
                      setCarNameMappingData(null);
                      setSimilarMatches([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* 상단 정보 영역 */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  {/* OCR 인식 차량명 */}
                  <div>
                    <div className="text-sm text-gray-600 mb-2">
                      OCR 인식 차량명:
                    </div>
                    <div className="text-lg font-semibold text-gray-900 bg-white px-4 py-2 rounded border border-gray-200">
                      {carNameMappingData.ocrRecognizedName}
                    </div>
                  </div>

                  {/* 기초자료 매핑 영문차량명 */}
                  <div>
                    <div className="text-sm text-gray-600 mb-2">
                      기초자료 매핑 영문차량명:
                    </div>
                    <div className="text-lg font-semibold text-gray-900 bg-white px-4 py-2 rounded border border-gray-200">
                      {/* 기초자료에 영문명이 있고, 저장된 데이터인 경우에만 표시 */}
                      {(carNameMappingData.masterData?.BASE_CAR_NM_ENG &&
                        carNameMappingData.masterData.BASE_CAR_NM_ENG.trim() !==
                          "") ||
                      (carNameMappingData.masterData?.car_name_en &&
                        carNameMappingData.masterData.car_name_en.trim() !==
                          "") ||
                      (similarMatches[0]?.data?.BASE_CAR_NM_ENG &&
                        similarMatches[0].data.BASE_CAR_NM_ENG.trim() !== "") ||
                      (similarMatches[0]?.data?.car_name_en &&
                        similarMatches[0].data.car_name_en.trim() !== "")
                        ? carNameMappingData.masterData?.BASE_CAR_NM_ENG ||
                          carNameMappingData.masterData?.car_name_en ||
                          similarMatches[0]?.data?.BASE_CAR_NM_ENG ||
                          similarMatches[0]?.data?.car_name_en
                        : ""}
                    </div>
                  </div>
                </div>
              </div>

              {/* 하단 그리드 영역 */}
              <div className="flex-1 overflow-hidden px-0 py-0 min-h-0">
                <VehicleMasterManagement
                  API_BASE_URL={
                    process.env.REACT_APP_API_URL ||
                    (window.location.hostname === "localhost" ||
                    window.location.hostname === "127.0.0.1"
                      ? `http://${window.location.hostname}:3002`
                      : "")
                  }
                  ocrRecognizedName={carNameMappingData.ocrRecognizedName}
                  currentData={carNameMappingData.currentData}
                />
              </div>
            </div>
          </div>
        )}

        {/* 기초설정 모달 */}
        {showMasterSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-[52vw] h-[80vh] flex flex-col">
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: "#0A1037" }}
                  >
                    기초설정
                  </h3>
                  <span className="text-sm text-gray-600">
                    차량 기초자료관리(그룹코드:{" "}
                    {localStorage.getItem("groupCode") || "0001"})
                  </span>
                  <button
                    onClick={() => {
                      // VehicleMasterManagement 컴포넌트의 갱신 기능을 호출
                      window.dispatchEvent(
                        new CustomEvent("refreshVehicleMaster"),
                      );
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
                    title="새로고침"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>갱신</span>
                  </button>
                </div>
                <button
                  onClick={() => setShowMasterSettingsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden min-h-0">
                <VehicleMasterManagement
                  API_BASE_URL={
                    process.env.REACT_APP_API_URL ||
                    (window.location.hostname === "localhost" ||
                    window.location.hostname === "127.0.0.1"
                      ? `http://${window.location.hostname}:3002`
                      : "")
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* 차량 추가 모달 */}
        {showAddVehicleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3
                  className="text-lg font-semibold"
                  style={{ color: "#0A1037" }}
                >
                  {editingMasterId
                    ? "차량 기초자료 업데이트"
                    : "차량 기초자료 추가"}
                </h3>
                <button
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    setEditingMasterId(null);
                    setNewVehicleData({
                      car_name_kr: "",
                      car_name_en: "",
                      brand: "",
                      model_year: "",
                      trade_item_name: "",
                      item_name: "",
                      displacement: "",
                      weight: "",
                      cbm: "",
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    차량명(한글) *
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.car_name_kr}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        car_name_kr: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 쏘나타"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    차량명(영문) *
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.car_name_en}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        car_name_en: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: SONATA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    브랜드
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.brand}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        brand: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 현대"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    연식
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.model_year}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        model_year: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 2011"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    배기량
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.displacement}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        displacement: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 1998"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    중량
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.weight}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        weight: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 1740"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CBM
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.cbm}
                    onChange={(e) =>
                      setNewVehicleData({
                        ...newVehicleData,
                        cbm: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 13"
                  />
                </div>
              </div>
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    setEditingMasterId(null);
                    setNewVehicleData({
                      car_name_kr: "",
                      car_name_en: "",
                      brand: "",
                      model_year: "",
                      trade_item_name: "",
                      item_name: "",
                      displacement: "",
                      weight: "",
                      cbm: "",
                    });
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    const API_BASE_URL =
                      process.env.REACT_APP_API_URL ||
                      (window.location.hostname === "localhost" ||
                      window.location.hostname === "127.0.0.1"
                        ? `http://${window.location.hostname}:3002`
                        : "");
                    try {
                      let response;
                      if (editingMasterId) {
                        // 기존 데이터 업데이트
                        response = await fetch(
                          `${API_BASE_URL}/api/vehicle-master/${editingMasterId}`,
                          {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(newVehicleData),
                          },
                        );
                      } else {
                        // 새 데이터 추가
                        response = await fetch(
                          `${API_BASE_URL}/api/vehicle-master/add`,
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(newVehicleData),
                          },
                        );
                      }

                      if (response.ok) {
                        const result = await response.json();
                        if (result.success) {
                          alert(
                            editingMasterId
                              ? "차량 기초자료가 업데이트되었습니다."
                              : "차량이 기초자료에 추가되었습니다.",
                          );
                          setShowAddVehicleModal(false);
                          setEditingMasterId(null);
                          setNewVehicleData({
                            car_name_kr: "",
                            car_name_en: "",
                            brand: "",
                            model_year: "",
                            trade_item_name: "",
                            item_name: "",
                            displacement: "",
                            weight: "",
                            cbm: "",
                          });
                        } else {
                          alert(
                            (editingMasterId ? "차량 업데이트" : "차량 추가") +
                              "에 실패했습니다: " +
                              (result.message || "알 수 없는 오류"),
                          );
                        }
                      } else {
                        alert(
                          (editingMasterId ? "차량 업데이트" : "차량 추가") +
                            "에 실패했습니다.",
                        );
                      }
                    } catch (error) {
                      console.error("차량 처리 실패:", error);
                      alert(
                        (editingMasterId ? "차량 업데이트" : "차량 추가") +
                          " 중 오류가 발생했습니다.",
                      );
                    }
                  }}
                  disabled={
                    !newVehicleData.car_name_kr || !newVehicleData.car_name_en
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {editingMasterId ? "업데이트" : "추가"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* 🔧 그리드 영역 닫힘 */}

      {/* 🤖 AI 인라인 사이드바 — Claude-in-Excel 스타일 */}
      {showAIPanel && (
        <div
          className="flex-shrink-0 h-full bg-white border-l border-gray-200 flex flex-col shadow-lg"
          style={{ width: 360, minWidth: 320, maxWidth: 420 }}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-gray-900 text-sm">
                AI 어시스턴트
              </span>
            </div>
            <button
              onClick={() => {
                setShowAIPanel(false);
                setAiResult(null);
                setAiError(null);
                setAiMessages([]);
                window.dispatchEvent(
                  new CustomEvent("aiPanelStateChange", {
                    detail: { open: false },
                  }),
                );
              }}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* 대화 영역 (스크롤) */}
          <div
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
            style={{ minHeight: 0 }}
          >
            {/* 대화가 없을 때 안내 + 예시 힌트 */}
            {aiMessages.length === 0 && !aiLoading && !aiError && (
              <div className="space-y-3">
                <div className="text-center py-3">
                  <Sparkles className="w-7 h-7 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs text-gray-500">
                    자연어로 데이터를 변환하세요
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Enter로 전송 · Shift+Enter로 줄바꿈
                  </p>
                </div>

                {/* ⚡ 빠른 작업 칩 — 카테고리별 */}
                {[
                  {
                    label: "🚢 무역 · 관세",
                    actions: [
                      {
                        text: "인코텀즈 추출",
                        prompt:
                          "이 열에서 인코텀즈(FOB, CIF, FCA 등)만 추출해줘",
                      },
                      {
                        text: "HS코드 10자리 생성",
                        prompt:
                          "품명을 기반으로 한국 관세율표 기준 HS코드 10자리를 추가열에 생성해줘",
                      },
                      {
                        text: "HS코드 검증",
                        prompt:
                          "현재 HS코드가 품명과 맞는지 검토하고 수정이 필요하면 올바른 코드를 제안해줘",
                      },
                      {
                        text: "원산지 추출",
                        prompt: "이 열에서 국가명/원산지 정보만 추출해줘",
                      },
                    ],
                  },
                  {
                    label: "🔧 데이터 정리",
                    actions: [
                      {
                        text: "대소문자 변환",
                        prompt: "모두 대문자로 변환해줘",
                      },
                      {
                        text: "날짜 형식 통일",
                        prompt: "날짜를 YYYY-MM-DD 형식으로 통일해줘",
                      },
                      {
                        text: "열 분리",
                        prompt: ", 기준으로 2개 열로 분리해줘",
                      },
                      {
                        text: "빈 셀 채우기",
                        prompt: "빈 셀을 이전 행의 값으로 채워줘",
                      },
                    ],
                  },
                  {
                    label: "🗑️ 필터 · 삭제",
                    actions: [
                      {
                        text: "조건부 행 삭제",
                        prompt: "빈 행을 모두 삭제해줘",
                      },
                      { text: "중복 제거", prompt: "중복된 행을 제거해줘" },
                      {
                        text: "특정 값만 유지",
                        prompt: "FCA인 행만 남기고 나머지 삭제해줘",
                      },
                    ],
                  },
                ].map((category, ci) => (
                  <div key={ci} className="space-y-1">
                    <div className="text-[10px] text-gray-400 font-medium px-1">
                      {category.label}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {category.actions.map((action, ai) => (
                        <button
                          key={ai}
                          onClick={() => setAiPrompt(action.prompt)}
                          className="text-[11px] text-gray-600 hover:bg-blue-50 hover:text-blue-700 px-2 py-1 rounded-md transition-colors border border-gray-200 hover:border-blue-300 bg-white"
                          title={action.prompt}
                        >
                          {action.text}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 대화 메시지 목록 */}
            {aiMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "user" ? (
                  /* 사용자 메시지 */
                  <div className="max-w-[85%] px-3 py-2 rounded-xl rounded-tr-sm bg-blue-50 text-gray-800 text-xs whitespace-pre-wrap shadow-sm border border-blue-200">
                    {msg.content}
                    {/* 🖼️ 첨부 이미지 표시 */}
                    {msg.images && msg.images.length > 0 && (
                      <div className="mt-1.5 flex gap-1 flex-wrap">
                        {msg.images.map((src, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={src}
                            alt={`첨부 ${imgIdx + 1}`}
                            className="w-16 h-16 object-cover rounded border border-blue-200 cursor-pointer hover:opacity-80 transition"
                            onClick={() => window.open(src, "_blank")}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : msg.role === "assistant" ? (
                  /* AI 응답 메시지 */
                  <div className="max-w-[95%] space-y-2">
                    <div className="px-3 py-2.5 rounded-xl rounded-tl-sm bg-gray-100 text-gray-800 text-xs shadow-sm">
                      <div className="whitespace-pre-wrap">{msg.content}</div>

                      {/* Diff 미리보기 */}
                      {msg.previews && msg.previews.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="text-[10px] text-gray-500 mb-1 font-medium">
                            변경 미리보기:
                          </div>
                          <div className="space-y-0.5">
                            {msg.previews.map((p, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-1 text-[10px]"
                              >
                                <span className="text-gray-400 w-5 text-right flex-shrink-0">
                                  #{p.rowIndex}
                                </span>
                                <span
                                  className="text-red-500 line-through truncate max-w-[100px]"
                                  title={p.oldVal}
                                >
                                  {p.oldVal || "(빈)"}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span
                                  className="text-blue-600 font-medium truncate max-w-[100px]"
                                  title={p.newVal}
                                >
                                  {p.newVal || "(빈)"}
                                </span>
                              </div>
                            ))}
                            {msg.changedCount > 5 && (
                              <div className="text-[10px] text-gray-400">
                                ...외 {msg.changedCount - 5}개 변경
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 적용/취소 버튼 (pendingTransform이 이 메시지의 것일 때만) */}
                    {msg.transformedData && aiResult?.msgIdx === idx && (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => {
                            // 🔧 AI 결과 적용
                            const grid = gridRef.current || "#horizontal_grid";
                            if (
                              !window.AUIGrid?.isCreated(grid) ||
                              !msg.transformedData
                            )
                              return;

                            const transformedData = msg.transformedData;
                            const currentData =
                              window.AUIGrid.getGridData(grid);

                            // 📸 스냅샷
                            const snap = {
                              type: "full_snapshot",
                              label: "AI 열 변환",
                              beforeData: currentData.map((r) => ({ ...r })),
                              beforeLayout: window.AUIGrid.getColumnLayout(
                                grid,
                              ).map((c) => ({ ...c })),
                            };

                            // 병합
                            const transformMap = new Map();
                            transformedData.forEach((row) => {
                              if (row._rowIndex !== undefined)
                                transformMap.set(row._rowIndex, row);
                            });

                            const hasFilteredRows =
                              transformedData.length < currentData.length &&
                              transformedData.every(
                                (item) => item._rowIndex !== undefined,
                              );

                            let newData;
                            if (hasFilteredRows) {
                              const keptIndices = new Set(
                                transformedData.map((item) => item._rowIndex),
                              );
                              newData = currentData
                                .filter((_, idx2) => keptIndices.has(idx2))
                                .map((row, newIdx) => {
                                  const originalIdx = [...keptIndices].sort(
                                    (a, b) => a - b,
                                  )[newIdx];
                                  const transformed =
                                    transformMap.get(originalIdx);
                                  if (transformed) {
                                    const merged = { ...row };
                                    Object.keys(transformed).forEach((key) => {
                                      if (key !== "_rowIndex")
                                        merged[key] = transformed[key];
                                    });
                                    return merged;
                                  }
                                  return row;
                                });
                            } else {
                              newData = currentData.map((row, idx2) => {
                                const transformed = transformMap.get(idx2);
                                if (transformed) {
                                  const merged = { ...row };
                                  Object.keys(transformed).forEach((key) => {
                                    if (key !== "_rowIndex")
                                      merged[key] = transformed[key];
                                  });
                                  return merged;
                                }
                                return { ...row };
                              });
                            }

                            // 새 열 감지
                            const existingColumns =
                              window.AUIGrid.getColumnLayout(grid).map(
                                (c) => c.dataField,
                              );
                            const allAiKeys = new Set();
                            transformedData.forEach((row) => {
                              Object.keys(row).forEach((k) => {
                                if (k !== "_rowIndex") allAiKeys.add(k);
                              });
                            });
                            const missingColumns = [...allAiKeys].filter(
                              (k) => !existingColumns.includes(k),
                            );
                            if (missingColumns.length > 0) {
                              const firstExistingKey = [...allAiKeys].find(
                                (k) => existingColumns.includes(k),
                              );
                              let insertIdx = firstExistingKey
                                ? existingColumns.indexOf(firstExistingKey) + 1
                                : existingColumns.length;
                              missingColumns.forEach((colName, i) => {
                                try {
                                  window.AUIGrid.addColumn(
                                    grid,
                                    {
                                      dataField: colName,
                                      headerText: colName,
                                      width: 150,
                                      editable: true,
                                      style: "aui-left",
                                    },
                                    insertIdx + i,
                                  );
                                } catch (e) {
                                  /* */
                                }
                              });
                            }

                            window.AUIGrid.setGridData(grid, newData);
                            setUndoStack((prev) => [
                              ...prev,
                              { id: Date.now(), ...snap },
                            ]);
                            showUndoToast();

                            // autoAlign
                            try {
                              const layout =
                                window.AUIGrid.getColumnLayout(grid);
                              const data = window.AUIGrid.getGridData(grid);
                              if (layout && data && data.length > 0) {
                                const numRegex = /^-?[\d,]+\.?\d*$/;
                                const skipFields = new Set([
                                  "No",
                                  "P",
                                  "rowNum",
                                  "pageNumber",
                                ]);
                                let cssRules = "";
                                layout.forEach((col, colIdx2) => {
                                  if (
                                    !col.dataField ||
                                    skipFields.has(col.dataField)
                                  )
                                    return;
                                  let numCount = 0,
                                    totalCount = 0;
                                  for (
                                    let i = 0;
                                    i < Math.min(data.length, 20);
                                    i++
                                  ) {
                                    const val = String(
                                      data[i]?.[col.dataField] || "",
                                    ).trim();
                                    if (val === "") continue;
                                    totalCount++;
                                    if (numRegex.test(val.replace(/\s/g, "")))
                                      numCount++;
                                  }
                                  if (totalCount > 0)
                                    cssRules += `.aui-grid-renderer-base[data-column-index="${colIdx2}"] { text-align: ${numCount / totalCount >= 0.6 ? "right" : "left"} !important; }\n`;
                                });
                                if (cssRules) {
                                  let styleEl =
                                    document.getElementById("auto-align-style");
                                  if (!styleEl) {
                                    styleEl = document.createElement("style");
                                    styleEl.id = "auto-align-style";
                                    document.head.appendChild(styleEl);
                                  }
                                  styleEl.textContent = cssRules;
                                }
                              }
                            } catch (e) {
                              /* */
                            }

                            // 메시지 업데이트 (적용 완료 표시)
                            setAiMessages((prev) =>
                              prev.map((m, i) =>
                                i === idx ? { ...m, applied: true } : m,
                              ),
                            );
                            setAiResult(null);
                            console.log("✅ [AI] 변환 적용 완료");
                          }}
                          className="flex-1 px-2.5 py-1.5 bg-blue-600 text-white text-[11px] font-medium rounded-lg hover:bg-blue-700 transition shadow-sm flex items-center justify-center gap-1"
                        >
                          <Check className="w-3 h-3" /> 적용
                        </button>
                        <button
                          onClick={() => {
                            setAiResult(null);
                          }}
                          className="px-2.5 py-1.5 bg-gray-100 text-gray-600 text-[11px] font-medium rounded-lg hover:bg-gray-200 transition border border-gray-200"
                        >
                          건너뛰기
                        </button>
                      </div>
                    )}

                    {/* 적용 완료 표시 */}
                    {msg.applied && (
                      <div className="flex items-center gap-1 text-[10px] text-green-600 px-1">
                        <Check className="w-3 h-3" /> 적용됨
                        <button
                          onClick={() => {
                            // Undo 실행
                            if (undoStack.length > 0) {
                              const lastSnap = undoStack[undoStack.length - 1];
                              const grid =
                                gridRef.current || "#horizontal_grid";
                              if (
                                window.AUIGrid?.isCreated(grid) &&
                                lastSnap.beforeData
                              ) {
                                // 🔧 먼저 추가된 열 제거 (beforeLayout에 없는 열을 역순으로 제거)
                                if (lastSnap.beforeLayout) {
                                  const beforeFields = new Set(
                                    lastSnap.beforeLayout.map(
                                      (c) => c.dataField,
                                    ),
                                  );
                                  const currentLayout =
                                    window.AUIGrid.getColumnLayout(grid);
                                  // 역순으로 제거해야 인덱스가 밀리지 않음
                                  for (
                                    let i = currentLayout.length - 1;
                                    i >= 0;
                                    i--
                                  ) {
                                    if (
                                      currentLayout[i].dataField &&
                                      !beforeFields.has(
                                        currentLayout[i].dataField,
                                      )
                                    ) {
                                      try {
                                        window.AUIGrid.removeColumn(grid, i);
                                        console.log(
                                          "🔄 [Undo] Removed column:",
                                          currentLayout[i].dataField,
                                          "at index",
                                          i,
                                        );
                                      } catch (e) {
                                        console.warn(
                                          "⚠️ [Undo] removeColumn failed:",
                                          e,
                                        );
                                      }
                                    }
                                  }
                                }
                                window.AUIGrid.setGridData(
                                  grid,
                                  lastSnap.beforeData,
                                );
                                setUndoStack((prev) => prev.slice(0, -1));
                                setAiMessages((prev) =>
                                  prev.map((m, i) =>
                                    i === idx
                                      ? { ...m, applied: false, undone: true }
                                      : m,
                                  ),
                                );
                                console.log("✅ [Undo] 복원 완료");
                              }
                            }
                          }}
                          className="ml-1 text-[10px] text-orange-600 hover:text-orange-800 underline cursor-pointer"
                        >
                          되돌리기
                        </button>
                      </div>
                    )}

                    {/* 되돌리기 완료 표시 */}
                    {msg.undone && (
                      <div className="flex items-center gap-1 text-[10px] text-orange-600 px-1">
                        <Undo2 className="w-3 h-3" /> 되돌림
                      </div>
                    )}
                  </div>
                ) : (
                  /* 시스템 메시지 */
                  <div className="text-[10px] text-gray-400 text-center w-full">
                    {msg.content}
                  </div>
                )}
              </div>
            ))}

            {/* AI 에러 */}
            {aiError && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                ❌ {aiError}
              </div>
            )}

            {/* 로딩 */}
            {aiLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span className="text-xs">AI가 분석 중...</span>
                <button
                  onClick={() => {
                    if (aiAbortRef.current) {
                      aiAbortRef.current.abort();
                    }
                    setAiLoading(false);
                    setAiError(null);
                  }}
                  className="ml-auto text-[10px] text-red-500 hover:text-red-700 underline"
                >
                  취소
                </button>
              </div>
            )}

            <div ref={aiChatEndRef} />
          </div>

          {/* 하단 입력 영역 — Claude-in-Excel 스타일 */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50/80">
            {/* 선택된 열 배지 (프롬프트 바로 위) */}
            <div className="px-3 pt-2 pb-1">
              <div className="flex flex-wrap items-center gap-1">
                {aiTargetColumns.length > 0 ? (
                  <>
                    <span className="text-[10px] text-gray-400">
                      Column {aiTargetColumns.length} selected
                    </span>
                    {aiTargetColumns.map((c) => (
                      <span
                        key={c.field}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium"
                      >
                        {c.headerText}
                        <button
                          onClick={() =>
                            setAiTargetColumns((prev) =>
                              prev.filter((p) => p.field !== c.field),
                            )
                          }
                          className="ml-0.5 text-blue-400 hover:text-blue-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="text-[10px] text-gray-400">
                    전체 데이터 대상
                  </span>
                )}
              </div>
            </div>

            {/* 프롬프트 입력 (textarea) */}
            <div className="px-3 pb-2.5">
              {/* 🖼️ 첨부 이미지 미리보기 */}
              {aiAttachedImages.length > 0 && (
                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {aiAttachedImages.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.dataUrl}
                        alt={img.name}
                        className="w-14 h-14 object-cover rounded border border-gray-200 shadow-sm"
                      />
                      <button
                        onClick={() =>
                          setAiAttachedImages((prev) =>
                            prev.filter((i) => i.id !== img.id),
                          )
                        }
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[9px] shadow"
                      >
                        ✕
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[7px] text-center rounded-b truncate px-0.5">
                        {img.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* 🖼️ 현재 이미지 첨부 버튼 */}
              {currentFilePreview && aiAttachedImages.length === 0 && (
                <button
                  onClick={async () => {
                    try {
                      // 현재 뷰어 이미지 또는 PDF 페이지 이미지를 base64로 변환
                      let imageUrl = null;
                      // PDF: 현재 페이지의 pngImage/correctedImageUrl 사용
                      if (pageResults && pageResults.length > 0) {
                        const currentPageResult =
                          pageResults.find(
                            (p) => p.pageNumber === selectedPageNumber,
                          ) || pageResults[0];
                        imageUrl =
                          currentPageResult?.pngImage ||
                          currentPageResult?.correctedImageUrl;
                      }
                      // 폴백: 파일 미리보기 URL
                      if (!imageUrl) imageUrl = currentFilePreview;
                      if (!imageUrl) return;

                      const response = await fetch(imageUrl);
                      const blob = await response.blob();
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        setAiAttachedImages([
                          {
                            id: Date.now(),
                            dataUrl: e.target.result,
                            name: `현재문서_p${selectedPageNumber || 1}.png`,
                          },
                        ]);
                      };
                      reader.readAsDataURL(blob);
                    } catch (err) {
                      console.error("🖼️ 현재 이미지 첨부 실패:", err);
                    }
                  }}
                  className="mb-2 w-full text-[11px] text-blue-600 hover:bg-blue-50 px-2 py-1.5 rounded-lg transition-colors border border-blue-200 border-dashed flex items-center justify-center gap-1.5"
                >
                  <span>📷</span> 현재 보고 있는 이미지 첨부
                </button>
              )}
              <div className="relative flex items-end border border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition shadow-sm">
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (
                        (aiPrompt.trim() || aiAttachedImages.length > 0) &&
                        !aiLoading
                      ) {
                        // 폼 제출 트리거
                        e.target
                          .closest(".relative")
                          ?.querySelector("button[data-submit]")
                          ?.click();
                      }
                    }
                    // Shift+Enter는 기본 동작 (줄바꿈)
                  }}
                  onPaste={(e) => {
                    // 🖼️ Ctrl+V 클립보드 이미지 붙여넣기
                    const items = e.clipboardData?.items;
                    if (!items) return;
                    const imageItems = Array.from(items).filter((item) =>
                      item.type.startsWith("image/"),
                    );
                    if (imageItems.length === 0) return;
                    e.preventDefault();
                    imageItems.forEach((item) => {
                      const file = item.getAsFile();
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setAiAttachedImages((prev) => [
                          ...prev,
                          {
                            id: Date.now() + Math.random(),
                            dataUrl: ev.target.result,
                            name: "screenshot.png",
                          },
                        ]);
                      };
                      reader.readAsDataURL(file);
                    });
                  }}
                  onInput={(e) => {
                    // 자동 높이 조절 (min 2줄, max ~5줄)
                    e.target.style.height = "auto";
                    const lineH = 18;
                    const minH = lineH * 2 + 12; // 2줄 + padding
                    const maxH = lineH * 5 + 12; // 5줄 + padding
                    e.target.style.height =
                      Math.max(minH, Math.min(maxH, e.target.scrollHeight)) +
                      "px";
                  }}
                  placeholder={
                    aiAttachedImages.length > 0
                      ? "이미지에 대해 질문하세요..."
                      : "변환 명령을 입력하세요... (Ctrl+V: 스크린샷)"
                  }
                  className="flex-1 px-3 py-2 text-xs resize-none bg-transparent focus:outline-none"
                  style={{
                    minHeight: "48px",
                    maxHeight: "102px",
                    lineHeight: "18px",
                  }}
                  disabled={aiLoading}
                  rows={2}
                  autoFocus
                />
                <button
                  data-submit="true"
                  onClick={async () => {
                    const hasImages = aiAttachedImages.length > 0;
                    if ((!aiPrompt.trim() && !hasImages) || aiLoading) return;

                    const promptText =
                      aiPrompt.trim() ||
                      (hasImages ? "이미지를 분석해주세요" : "");
                    // 사용자 메시지 추가 (이미지 포함)
                    setAiMessages((prev) => [
                      ...prev,
                      {
                        role: "user",
                        content: promptText,
                        images: hasImages
                          ? aiAttachedImages.map((img) => img.dataUrl)
                          : undefined,
                        timestamp: Date.now(),
                      },
                    ]);
                    setAiPrompt("");

                    // 🎯 액션 커맨드 감지 — 기존 기능 직접 실행
                    const lowerPrompt = promptText.toLowerCase();
                    const isExcelCmd =
                      /엑셀|excel|xlsx|다운로드|download|내보내기|export/.test(
                        lowerPrompt,
                      );
                    const isUndoCmd =
                      /되돌리기|되돌려|undo|취소|원복|rollback/.test(
                        lowerPrompt,
                      ) && !/변환|변경|삭제|분리/.test(lowerPrompt);

                    if (isExcelCmd) {
                      // ✅ 엑셀 다운로드 직접 실행
                      setTimeout(() => {
                        try {
                          handleExcelDownload();
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content:
                                "✅ 엑셀 파일 다운로드를 실행했습니다! 브라우저 다운로드 폴더를 확인해주세요.",
                              timestamp: Date.now(),
                            },
                          ]);
                        } catch (e) {
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content: `❌ 엑셀 다운로드 실패: ${e.message}`,
                              isError: true,
                              timestamp: Date.now(),
                            },
                          ]);
                        }
                        setTimeout(
                          () =>
                            aiChatEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                            }),
                          100,
                        );
                      }, 300);
                      return;
                    }

                    if (isUndoCmd) {
                      // ✅ 되돌리기 직접 실행
                      setTimeout(() => {
                        if (undoStack.length > 0) {
                          performUndo();
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content: "✅ 마지막 작업을 되돌렸습니다.",
                              timestamp: Date.now(),
                            },
                          ]);
                        } else {
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content: "⚠️ 되돌릴 작업이 없습니다.",
                              timestamp: Date.now(),
                            },
                          ]);
                        }
                        setTimeout(
                          () =>
                            aiChatEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                            }),
                          100,
                        );
                      }, 300);
                      return;
                    }

                    // 🗑️ 열 삭제 커맨드 감지
                    const isDeleteColCmd =
                      /열\s*삭제|열\s*제거|컬럼\s*삭제|column\s*delete|remove\s*column|drop\s*column|현재\s*열\s*삭제/.test(
                        lowerPrompt,
                      );
                    if (isDeleteColCmd) {
                      setTimeout(() => {
                        const grid = gridRef.current || "#horizontal_grid";
                        if (!window.AUIGrid?.isCreated(grid)) {
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content: "❌ 그리드가 없습니다.",
                              isError: true,
                              timestamp: Date.now(),
                            },
                          ]);
                          return;
                        }

                        // 삭제 대상 열 결정 (AI 사이드바에 선택된 열 또는 selectedColumnsRef)
                        let targetCols = aiTargetColumns;
                        if (
                          targetCols.length === 0 &&
                          selectedColumnsRef.current?.length > 0
                        ) {
                          const layout = window.AUIGrid.getColumnLayout(grid);
                          targetCols = selectedColumnsRef.current
                            .map((idx) => ({
                              field: layout[idx]?.dataField,
                              headerText:
                                layout[idx]?.headerText ||
                                layout[idx]?.dataField,
                            }))
                            .filter((c) => c.field);
                        }

                        if (targetCols.length === 0) {
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content:
                                "⚠️ 삭제할 열을 선택해주세요. (헤더 클릭으로 열 선택 후 다시 시도)",
                              timestamp: Date.now(),
                            },
                          ]);
                          setTimeout(
                            () =>
                              aiChatEndRef.current?.scrollIntoView({
                                behavior: "smooth",
                              }),
                            100,
                          );
                          return;
                        }

                        try {
                          // 📸 스냅샷 저장 (되돌리기 지원)
                          const currentData = window.AUIGrid.getGridData(grid);
                          const currentLayout =
                            window.AUIGrid.getColumnLayout(grid);
                          const snap = {
                            id: Date.now(),
                            type: "full_snapshot",
                            label: `열 삭제: ${targetCols.map((c) => c.headerText).join(", ")}`,
                            beforeData: currentData.map((r) => ({ ...r })),
                            beforeLayout: currentLayout.map((c) => ({ ...c })),
                          };

                          // 열 삭제 (역순으로)
                          const layout = window.AUIGrid.getColumnLayout(grid);
                          const deletedNames = [];
                          for (let i = layout.length - 1; i >= 0; i--) {
                            if (
                              targetCols.some(
                                (tc) => tc.field === layout[i].dataField,
                              )
                            ) {
                              window.AUIGrid.removeColumn(grid, i);
                              deletedNames.push(
                                layout[i].headerText || layout[i].dataField,
                              );
                            }
                          }

                          setUndoStack((prev) => [...prev, snap]);
                          showUndoToast();

                          // 선택 상태 초기화
                          selectedColumnsRef.current = [];
                          setAiTargetColumns([]);

                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content: `✅ 열 삭제 완료: ${deletedNames.join(", ")}\n\n되돌리려면 "되돌리기"를 입력하세요.`,
                              timestamp: Date.now(),
                            },
                          ]);
                          console.log("🗑️ [AI Action] 열 삭제:", deletedNames);
                        } catch (e) {
                          setAiMessages((prev) => [
                            ...prev,
                            {
                              role: "assistant",
                              content: `❌ 열 삭제 실패: ${e.message}`,
                              isError: true,
                              timestamp: Date.now(),
                            },
                          ]);
                        }
                        setTimeout(
                          () =>
                            aiChatEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                            }),
                          100,
                        );
                      }, 300);
                      return;
                    }

                    // 🤖 일반 AI 변환 요청
                    setAiLoading(true);
                    setAiResult(null);
                    setAiError(null);

                    // 자동 스크롤
                    setTimeout(
                      () =>
                        aiChatEndRef.current?.scrollIntoView({
                          behavior: "smooth",
                        }),
                      100,
                    );

                    const abortController = new AbortController();
                    aiAbortRef.current = abortController;

                    try {
                      const API_BASE_URL =
                        process.env.REACT_APP_API_URL ||
                        (window.location.hostname === "localhost" ||
                        window.location.hostname === "127.0.0.1"
                          ? `http://${window.location.hostname}:3002`
                          : "");
                      const grid = gridRef.current || "#horizontal_grid";
                      const gridData = window.AUIGrid.getGridData(grid);

                      let cols = aiTargetColumns;
                      if (cols.length === 0) {
                        const layout = window.AUIGrid.getColumnLayout(grid);
                        cols = layout
                          .filter(
                            (c) =>
                              c.dataField &&
                              c.dataField !== "__checkbox" &&
                              c.dataField !== "__rownum",
                          )
                          .map((c) => ({
                            field: c.dataField,
                            headerText: c.headerText || c.dataField,
                          }));
                      }

                      const columns = cols.map((c) => c.field);
                      const data = gridData.map((row, i) => {
                        const obj = { _rowIndex: i };
                        cols.forEach((c) => {
                          obj[c.field] = row[c.field] || "";
                        });
                        return obj;
                      });

                      // 🖼️ 이미지 첨부 처리
                      const imageDataUrls = aiAttachedImages.map(
                        (img) => img.dataUrl,
                      );
                      const selectedModel = normalizeGeminiGenerationModel(
                        localStorage.getItem("aiAssistantModel"),
                        GEMINI_FLASH_MODEL,
                      );
                      setAiAttachedImages([]); // 전송 후 초기화

                      const res = await fetch(
                        `${API_BASE_URL}/api/ai/transform-data`,
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            prompt: promptText,
                            columns,
                            data,
                            model: selectedModel,
                            images:
                              imageDataUrls.length > 0
                                ? imageDataUrls
                                : undefined,
                            context: {
                              totalRows: data.length,
                              columnCount: columns.length,
                              hasImages: imageDataUrls.length > 0,
                            },
                          }),
                          signal: abortController.signal,
                        },
                      );
                      const result = await res.json();

                      if (result.success) {
                        // Diff 미리보기 생성
                        const firstCol = cols[0];
                        let previews = [];
                        let changedCount = 0;
                        if (firstCol && result.transformedData) {
                          for (const row of result.transformedData) {
                            if (row._rowIndex === undefined) continue;
                            const oldRow = gridData[row._rowIndex];
                            if (!oldRow) continue;
                            const oldVal = String(oldRow[firstCol.field] || "");
                            const newVal = String(row[firstCol.field] || "");
                            if (oldVal !== newVal) {
                              changedCount++;
                              if (previews.length < 5)
                                previews.push({
                                  rowIndex: row._rowIndex,
                                  oldVal,
                                  newVal,
                                });
                            }
                          }
                        }

                        const msgIdx = aiMessages.length + 1; // +1 because user msg was just added
                        setAiMessages((prev) => [
                          ...prev,
                          {
                            role: "assistant",
                            content:
                              result.explanation ||
                              `✅ 변환 완료! ${changedCount}개 셀 변경`,
                            transformedData: result.transformedData,
                            previews,
                            changedCount,
                            timestamp: Date.now(),
                          },
                        ]);
                        setAiResult({
                          transformedData: result.transformedData,
                          msgIdx,
                        });
                      } else {
                        setAiError(result.error || "변환 실패");
                        setAiMessages((prev) => [
                          ...prev,
                          {
                            role: "assistant",
                            content: `⚠️ ${result.error || "변환 실패"}`,
                            isError: true,
                            timestamp: Date.now(),
                          },
                        ]);
                      }
                    } catch (err) {
                      if (err.name === "AbortError") {
                        setAiMessages((prev) => [
                          ...prev,
                          {
                            role: "system",
                            content: "요청이 취소되었습니다.",
                            timestamp: Date.now(),
                          },
                        ]);
                      } else {
                        setAiError(err.message);
                        setAiMessages((prev) => [
                          ...prev,
                          {
                            role: "assistant",
                            content: `❌ ${err.message}`,
                            isError: true,
                            timestamp: Date.now(),
                          },
                        ]);
                      }
                    } finally {
                      setAiLoading(false);
                      aiAbortRef.current = null;
                      setTimeout(
                        () =>
                          aiChatEndRef.current?.scrollIntoView({
                            behavior: "smooth",
                          }),
                        200,
                      );
                    }
                  }}
                  disabled={!aiPrompt.trim() || aiLoading}
                  className="flex-shrink-0 p-2 mr-1 mb-1 text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-1 px-0.5">
                <span className="text-[9px] text-gray-400">
                  ⏎ 전송 · ⇧⏎ 줄바꿈
                </span>
                <span className="text-[9px] text-gray-400">
                  ⊘ Ask before edits
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔄 Undo 토스트 (3초 자동 숨김) */}
      {undoToastVisible && undoStack.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            background: "#1e293b",
            color: "#fff",
            padding: "10px 20px",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            fontSize: "13px",
            fontWeight: 500,
            animation: "fadeInUp 0.2s ease-out",
            overflow: "hidden",
          }}
        >
          <Undo2 className="w-4 h-4" style={{ color: "#60a5fa" }} />
          <span>작업이 실행되었습니다</span>
          <button
            onClick={performUndo}
            style={{
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              padding: "4px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            되돌리기
          </button>
          <span style={{ color: "#94a3b8", fontSize: "11px" }}>Ctrl+Z</span>
          {/* 프로그레스 바 */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "2px",
              background: "#3b82f6",
              animation: "undoProgress 5s linear forwards",
            }}
          />
          <style>{`
            @keyframes fadeInUp { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
            @keyframes undoProgress { from { width: 100%; } to { width: 0%; } }
          `}</style>
        </div>
      )}

      {/* 🔍 찾기/바꾸기 패널 (Google Sheets 스타일 플로팅) */}
      {showFindReplace && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowFindReplace(false)}
          />
          <div
            className="fixed z-50 bg-white rounded-lg select-none"
            style={{
              top: "60px",
              right: "40px",
              width: "380px",
              boxShadow:
                "0 4px 24px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.12)",
              border: "1px solid #dadce0",
            }}
          >
            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-800">
                  찾기/바꾸기
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-gray-400 mr-1">
                  {frTargetCols.map((c) => c.headerText).join(", ")}
                </span>
                <button
                  onClick={() => setShowFindReplace(false)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* 본문 */}
            <div className="px-4 py-3 space-y-3">
              {/* 찾기 입력 */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-10 flex-shrink-0">
                  찾기
                </label>
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={frSearch}
                    onChange={(e) => {
                      setFrSearch(e.target.value);
                      // 실시간 매치 수 표시
                      const searchVal = e.target.value;
                      if (!searchVal) {
                        setFrMatchCount(null);
                        return;
                      }
                      const grid = gridRef.current || "#horizontal_grid";
                      if (!window.AUIGrid?.isCreated(grid)) return;
                      const data = window.AUIGrid.getGridData(grid);
                      let count = 0;
                      frTargetCols.forEach((col) => {
                        data.forEach((row) => {
                          const val = String(row[col.field] || "");
                          const searchStr = frMatchCase
                            ? searchVal
                            : searchVal.toLowerCase();
                          const cellStr = frMatchCase ? val : val.toLowerCase();
                          if (cellStr.includes(searchStr)) count++;
                        });
                      });
                      setFrMatchCount(count);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        // Enter = 전체 바꾸기 실행
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-14"
                    placeholder="찾을 텍스트..."
                    autoFocus
                  />
                  {frMatchCount !== null && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {frMatchCount}건
                    </span>
                  )}
                </div>
              </div>

              {/* 바꾸기 입력 */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-10 flex-shrink-0">
                  바꾸기
                </label>
                <input
                  type="text"
                  value={frReplace}
                  onChange={(e) => setFrReplace(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="바꿀 텍스트... (비우면 삭제)"
                />
              </div>

              {/* 옵션 */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={frMatchCase}
                    onChange={(e) => {
                      setFrMatchCase(e.target.checked);
                      // 대소문자 변경 시 매치 수 재계산
                      if (frSearch) {
                        const grid = gridRef.current || "#horizontal_grid";
                        if (!window.AUIGrid?.isCreated(grid)) return;
                        const data = window.AUIGrid.getGridData(grid);
                        let count = 0;
                        const mc = e.target.checked;
                        frTargetCols.forEach((col) => {
                          data.forEach((row) => {
                            const val = String(row[col.field] || "");
                            const searchStr = mc
                              ? frSearch
                              : frSearch.toLowerCase();
                            const cellStr = mc ? val : val.toLowerCase();
                            if (cellStr.includes(searchStr)) count++;
                          });
                        });
                        setFrMatchCount(count);
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-600">대소문자 구분</span>
                </label>
              </div>

              {/* 버튼 */}
              <div className="flex gap-2 pt-1">
                <button
                  disabled={!frSearch}
                  onClick={() => {
                    if (!frSearch) return;
                    const grid = gridRef.current || "#horizontal_grid";
                    if (!window.AUIGrid?.isCreated(grid)) return;
                    const data = window.AUIGrid.getGridData(grid);
                    const beforeVals = [];
                    const changeLog = [];
                    let replaceCount = 0;

                    frTargetCols.forEach((col) => {
                      data.forEach((row, ri) => {
                        const original = String(row[col.field] || "");
                        let replaced;
                        if (frMatchCase) {
                          if (!original.includes(frSearch)) return;
                          replaced = original.split(frSearch).join(frReplace);
                        } else {
                          const regex = new RegExp(
                            frSearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                            "gi",
                          );
                          if (!regex.test(original)) return;
                          replaced = original.replace(
                            new RegExp(
                              frSearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                              "gi",
                            ),
                            frReplace,
                          );
                        }
                        if (original !== replaced) {
                          beforeVals.push({
                            rowIndex: ri,
                            field: col.field,
                            value: original,
                          });
                          changeLog.push({
                            row: ri + 1,
                            col: col.headerText,
                            before: original,
                            after: replaced,
                          });
                          window.AUIGrid.updateRow(
                            grid,
                            { [col.field]: replaced },
                            ri,
                          );
                          replaceCount++;
                        }
                      });
                    });

                    if (replaceCount > 0) {
                      setUndoStack((prev) => [
                        ...prev,
                        {
                          id: Date.now(),
                          type: "cell_update",
                          beforeValues: beforeVals,
                        },
                      ]);
                      showUndoToast();
                      setFrMatchCount(0);
                      console.log(
                        `✅ [FindReplace] ${replaceCount}개 셀 바꿈: "${frSearch}" → "${frReplace}"`,
                      );
                      alert(
                        `✅ ${replaceCount}개 셀에서 바꾸기 완료!\n\n` +
                          `"${frSearch}" → "${frReplace || "(삭제)"}"\n\n` +
                          `대상 열: ${frTargetCols.map((c) => c.headerText).join(", ")}\n\n` +
                          `Ctrl+Z로 되돌릴 수 있습니다.`,
                      );
                    } else {
                      alert(`"${frSearch}"를 찾을 수 없습니다.`);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                >
                  <Replace className="w-3.5 h-3.5" />
                  전체 바꾸기
                </button>
                <button
                  onClick={() => setShowFindReplace(false)}
                  className="px-3 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-200 transition border border-gray-200"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 📝 텍스트 편집기 모달 (EditPlus 스타일) */}
      {showTextEditor && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setShowTextEditor(false)}
          />
          <div
            className="fixed z-50 bg-white rounded-xl flex flex-col"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "560px",
              maxHeight: "80vh",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* 헤더 */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-bold text-gray-800">
                  텍스트로 편집
                </span>
                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                  {textEditorTarget.headerText}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400">
                  {textEditorContent.split("\n").length}줄
                  {textEditorContent.split("\n").length !==
                    textEditorTarget.rowCount && (
                    <span className="text-orange-500 ml-1">
                      (원본 {textEditorTarget.rowCount}행)
                    </span>
                  )}
                </span>
                <button
                  onClick={() => setShowTextEditor(false)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* 안내 */}
            <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-[11px] text-blue-700 flex items-center gap-2">
              <span>💡</span>
              <span>
                한 줄 = 한 셀 · 자유롭게 편집 · <b>Ctrl+H</b>로 브라우저
                찾기/바꾸기 사용 가능
              </span>
            </div>

            {/* 에디터 */}
            <div
              className="flex-1 overflow-hidden px-5 py-3"
              style={{ minHeight: "300px" }}
            >
              <div className="relative h-full">
                {/* 줄 번호 */}
                <div
                  className="absolute left-0 top-0 bottom-0 overflow-hidden text-right pr-2 select-none pointer-events-none"
                  style={{
                    width: "40px",
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: "13px",
                    lineHeight: "20px",
                    paddingTop: "8px",
                    color: "#94a3b8",
                    borderRight: "1px solid #e2e8f0",
                  }}
                >
                  {textEditorContent.split("\n").map((_, i) => (
                    <div key={i} style={{ height: "20px" }}>
                      {i + 1}
                    </div>
                  ))}
                </div>
                <textarea
                  value={textEditorContent}
                  onChange={(e) => setTextEditorContent(e.target.value)}
                  className="w-full h-full resize-none focus:outline-none focus:ring-0 border border-gray-200 rounded-md"
                  style={{
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: "13px",
                    lineHeight: "20px",
                    paddingLeft: "50px",
                    paddingTop: "8px",
                    paddingRight: "12px",
                    minHeight: "300px",
                    tabSize: 4,
                    whiteSpace: "pre",
                  }}
                  spellCheck={false}
                  wrap="off"
                />
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <span className="text-[11px] text-gray-400">
                {textEditorContent.split("\n").length !==
                textEditorTarget.rowCount
                  ? `⚠️ 줄 수(${textEditorContent.split("\n").length})가 원본 행 수(${textEditorTarget.rowCount})와 다릅니다. 줄이 부족하면 빈 값, 초과하면 무시됩니다.`
                  : `✓ ${textEditorTarget.rowCount}행 일치`}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowTextEditor(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition border border-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={() => {
                    const grid = gridRef.current || "#horizontal_grid";
                    if (!window.AUIGrid?.isCreated(grid)) return;
                    const data = window.AUIGrid.getGridData(grid);
                    const field = textEditorTarget.field;
                    const newLines = textEditorContent.split("\n");

                    // 📸 스냅샷 (되돌리기)
                    const beforeVals = [];
                    let changeCount = 0;

                    data.forEach((row, ri) => {
                      const original = String(row[field] || "");
                      const newVal = ri < newLines.length ? newLines[ri] : "";
                      if (original !== newVal) {
                        beforeVals.push({
                          rowIndex: ri,
                          field,
                          value: original,
                        });
                        window.AUIGrid.updateRow(grid, { [field]: newVal }, ri);
                        changeCount++;
                      }
                    });

                    if (changeCount > 0) {
                      setUndoStack((prev) => [
                        ...prev,
                        {
                          id: Date.now(),
                          type: "cell_update",
                          beforeValues: beforeVals,
                        },
                      ]);
                      showUndoToast();
                      console.log(`✅ [TextEditor] ${changeCount}개 셀 수정됨`);
                    }

                    setShowTextEditor(false);
                    if (changeCount > 0) {
                      alert(
                        `✅ ${changeCount}개 셀이 수정되었습니다.\n\nCtrl+Z로 되돌릴 수 있습니다.`,
                      );
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition shadow-sm flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" />
                  적용
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 📊 격자 편집기 (열 분리) */}
      {showGridEditor && (
        <GridTextEditor
          columnName={gridEditorData.columnName}
          columnField={gridEditorData.columnField}
          values={gridEditorData.values}
          onClose={() => setShowGridEditor(false)}
          onApply={(splitColumns) => {
            const grid = gridRef.current || "#horizontal_grid";
            if (!window.AUIGrid?.isCreated(grid)) return;
            const data = window.AUIGrid.getGridData(grid);
            const layout = window.AUIGrid.getColumnLayout(grid);

            // 📸 스냅샷 (되돌리기): 원래 열 값 저장
            const beforeVals = [];
            let changeCount = 0;
            const originalField = gridEditorData.columnField;

            // 첫 번째 열(원본)은 원래 열에 덮어쓰기
            if (splitColumns.length > 0) {
              splitColumns[0].values.forEach((newVal, ri) => {
                if (ri >= data.length) return;
                const original = String(data[ri][originalField] || "");
                if (original !== newVal) {
                  beforeVals.push({
                    rowIndex: ri,
                    field: originalField,
                    value: original,
                  });
                  window.AUIGrid.updateRow(
                    grid,
                    { [originalField]: newVal },
                    ri,
                  );
                  changeCount++;
                }
              });
            }

            // 나머지 열은 새 열로 추가 (기존 분리 열 정리 후)
            {
              const currentLayout = window.AUIGrid.getColumnLayout(grid);
              const origColIdx = currentLayout.findIndex(
                (c) => c.dataField === originalField,
              );

              // ⭐ 기존 _split_ 열 정리: 이전에 적용된 분리 열들을 모두 제거
              const existingSplitCols = [];
              currentLayout.forEach((col, idx) => {
                if (
                  col.dataField &&
                  col.dataField.startsWith(`${originalField}_split_`)
                ) {
                  existingSplitCols.push(idx);
                }
              });
              // 역순으로 제거 (인덱스 시프트 방지)
              for (let i = existingSplitCols.length - 1; i >= 0; i--) {
                try {
                  window.AUIGrid.removeColumn(grid, existingSplitCols[i]);
                } catch (e) {
                  console.warn("⚠️ [GridEditor] removeColumn failed:", e);
                }
              }
              if (existingSplitCols.length > 0) {
                console.log(
                  `🧹 [GridEditor] 기존 분리 열 ${existingSplitCols.length}개 제거 완료`,
                );
              }

              // 새 분리 열 추가 (splitColumns[1] 이후)
              if (splitColumns.length > 1) {
                // 정리 후 레이아웃 다시 가져오기
                const updatedLayout = window.AUIGrid.getColumnLayout(grid);
                const updatedOrigIdx = updatedLayout.findIndex(
                  (c) => c.dataField === originalField,
                );

                for (let si = 1; si < splitColumns.length; si++) {
                  const newField = `${originalField}_split_${si}`;
                  const newHeader = `${gridEditorData.columnName} (${splitColumns[si].header || si + 1})`;

                  window.AUIGrid.addColumn(
                    grid,
                    {
                      dataField: newField,
                      headerText: newHeader,
                      width: 150,
                      editable: true,
                    },
                    updatedOrigIdx + si,
                  );

                  splitColumns[si].values.forEach((newVal, ri) => {
                    if (ri >= data.length) return;
                    window.AUIGrid.updateRow(grid, { [newField]: newVal }, ri);
                    changeCount++;
                  });
                }
              }
            }

            if (beforeVals.length > 0) {
              setUndoStack((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  type: "cell_update",
                  beforeValues: beforeVals,
                },
              ]);
              showUndoToast();
            }

            setShowGridEditor(false);
            console.log(
              `✅ [GridEditor] ${splitColumns.length}열 분리 완료, ${changeCount}개 셀 수정`,
            );
            alert(
              `✅ ${splitColumns.length}개 열로 분리 완료!\n${changeCount}개 셀이 수정되었습니다.`,
            );
          }}
        />
      )}

      {/* 🆕 헤더 컨텍스트 메뉴 오버레이 (Google Sheets 스타일) */}
      {headerMenuVisible && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setHeaderMenuVisible(false)}
          />
          <div
            className="fixed z-50 bg-white rounded-lg py-1.5 select-none"
            style={{
              left: headerMenuPosition.x,
              top: headerMenuPosition.y,
              minWidth: "220px",
              boxShadow:
                "0 2px 6px 2px rgba(60,64,67,0.15), 0 1px 2px 0 rgba(60,64,67,0.3)",
              border: "1px solid #dadce0",
            }}
          >
            <div
              className="px-4 py-1.5 text-[11px] text-gray-500 border-b border-gray-200 flex items-center gap-2"
              style={{ fontWeight: 500 }}
            >
              열: {headerMenuTarget.headerText}
            </div>
            {[
              {
                label: "선택 열 병합... (Alt+M)",
                icon: <Link className="w-4 h-4" />,
                callback: "mergeColumns",
                help: "선택한 열들을 하나로 합칩니다\n예: [A열]+[B열] → [A, B]",
              },
              {
                label: "열 분리... (Alt+S)",
                icon: <Scissors className="w-4 h-4" />,
                callback: "splitColumn",
                help: '1열을 구분자로 여러 열로 나눕니다\n예: "A, B, C" → [A] [B] [C]',
              },
              {
                label: "AI로 열 변환... (Alt+A)",
                icon: <Sparkles className="w-4 h-4" />,
                callback: "mergeColumnsPrompt",
                help: 'AI에게 자연어로 변환 지시\n예: "날짜 형식을 YYYY-MM-DD로 변환"',
              },
              { type: "divider" },
              {
                label: "Trim (Alt+T)",
                icon: <Eraser className="w-4 h-4" />,
                callback: "trimColumn",
                help: '앞뒤 공백 + 내부 연속 공백 정리\n예: "  hello   world  " → "hello world"',
              },
              {
                label: "UPPERCASE (Alt+U)",
                icon: <CaseSensitive className="w-4 h-4" />,
                callback: "uppercaseColumn",
                help: '영문 소문자를 대문자로 변환\n예: "Cleaning Compound" → "CLEANING COMPOUND"',
              },
              {
                label: "숫자만 남기기",
                icon: <Hash className="w-4 h-4" />,
                callback: "keepNumbersOnly",
                help: '숫자·소수점만 유지, 나머지 제거\n예: "2.40 GR" → "2.40"',
              },
              {
                label: "문자만 남기기",
                icon: <Type className="w-4 h-4" />,
                callback: "keepTextOnly",
                help: '한글·영문만 유지, 숫자·특수문자 제거\n예: "750/ooo ROSE GOLD" → "ooo ROSE GOLD"',
              },
              {
                label: "특수문자 제거",
                icon: <Ban className="w-4 h-4" />,
                callback: "removeSpecialChars",
                help: '문자·숫자·공백만 유지\n예: "$1,234.56" → "1234.56"',
              },
              {
                label: "찾기/바꾸기 (Ctrl+H)",
                icon: <Search className="w-4 h-4" />,
                callback: "findReplaceInColumn",
                help: '선택한 열에서 텍스트 찾기 & 바꾸기\n예: "GR" → "" (단위 제거)',
              },

              {
                label: "열 분리 (격자)",
                icon: <Grid3x3 className="w-4 h-4" />,
                callback: "gridViewEditor",
                help: "쉼표·탭·공백 등으로 열을 분리\n사각형 드래그 선택 → 복사/다운로드\n예: 'A,B,C' → [A] [B] [C]",
              },
              { type: "divider" },
              {
                label: "정렬...",
                icon: <ArrowUpDown className="w-4 h-4" />,
                callback: "sortColumn",
                help: "열 기준 오름/내림차순 정렬\n예: [3,1,2] → [1,2,3] 또는 [3,2,1]",
              },
              {
                label: "빈 행 삭제",
                icon: <Trash2 className="w-4 h-4" />,
                callback: "deleteEmptyRows",
                help: "모든 셀이 비어있는 행을 삭제\n예: 27행 중 빈 행 3개 → 24행",
              },
              { type: "divider" },
              {
                label: "열 복사",
                icon: <Copy className="w-4 h-4" />,
                callback: "copyColumn",
              },
              {
                label: "열 추가 (Ctrl +)",
                icon: <Plus className="w-4 h-4" />,
                callback: "addColumn",
              },
              {
                label: "열 삭제 (Ctrl -)",
                icon: <X className="w-4 h-4" />,
                callback: "deleteColumn",
              },
              { type: "divider" },
              {
                label: "수량×단가 → 금액",
                icon: <Calculator className="w-4 h-4" />,
                callback: "addAmountColumn",
                help: "수량×단가 계산 열 추가\n예: qty=5, uprice=100 → amount=500",
              },
              {
                label: "금액÷수량 → 단가",
                icon: <Calculator className="w-4 h-4" />,
                callback: "addUnitPriceColumn",
                help: "금액÷수량 계산 열 추가\n예: amount=500, qty=5 → uprice=100",
              },
            ].map((item, idx) =>
              item.type === "divider" ? (
                <div key={idx} className="border-t border-gray-200 my-1" />
              ) : (
                <button
                  key={idx}
                  className="w-full px-4 text-left text-[13px] hover:bg-gray-100 transition-colors flex items-center gap-3 text-gray-700 group"
                  style={{ padding: "7px 16px", fontWeight: 400 }}
                  onClick={() => {
                    setHeaderMenuVisible(false);
                    handleGridMenuAction(item.callback, headerMenuTarget);
                  }}
                >
                  <span className="text-gray-500 flex-shrink-0">
                    {item.icon}
                  </span>
                  <span className="flex-1">{item.label}</span>
                  {item.help && (
                    <span
                      title={item.help}
                      className="w-4 h-4 rounded-full text-gray-400 text-[10px] flex items-center justify-center cursor-help hover:bg-gray-200 hover:text-gray-600 transition-colors flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ?
                    </span>
                  )}
                </button>
              ),
            )}
          </div>
        </>
      )}

      {/* 셀 컨텍스트 메뉴 (Google Sheets 스타일) */}
      {gridMenuVisible && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setGridMenuVisible(false)}
          />
          <div
            className="fixed z-50 bg-white rounded-lg py-1.5 select-none"
            style={{
              left: gridMenuPosition.x,
              top: gridMenuPosition.y,
              minWidth: "220px",
              boxShadow:
                "0 2px 6px 2px rgba(60,64,67,0.15), 0 1px 2px 0 rgba(60,64,67,0.3)",
              border: "1px solid #dadce0",
            }}
          >
            {/* 헤더 */}
            <div
              className="px-4 py-1.5 text-[11px] text-gray-500 border-b border-gray-200 flex items-center justify-between"
              style={{ fontWeight: 500 }}
            >
              <span>행 {gridMenuTarget.rowRange}</span>
            </div>

            {/* 행 작업 */}
            <div
              className="px-4 pt-2 pb-1 text-[11px] text-gray-400 select-none"
              style={{ fontWeight: 500 }}
            >
              행 작업
            </div>
            {[
              {
                label: `위에 행 ${gridMenuTarget.rowCount}개 삽입 (Ctrl +)`,
                icon: <Plus className="w-4 h-4" />,
                callback: "insertRow",
              },
              {
                label: "아래에 행 추가 (Ctrl +)",
                icon: <ArrowUp className="w-4 h-4 rotate-180" />,
                callback: "addRow",
              },
              {
                label: `${gridMenuTarget.rowCount > 1 ? `${gridMenuTarget.rowCount}개 행 삭제` : `${gridMenuTarget.rowRange}행 삭제`} (Ctrl -)`,
                icon: <Trash2 className="w-4 h-4" />,
                callback: "deleteSelectedRows",
              },
            ].map((item, idx) => (
              <button
                key={idx}
                className="w-full text-left text-[13px] flex items-center gap-3 hover:bg-gray-100 text-gray-700 transition-colors group"
                style={{ padding: "7px 16px", fontWeight: 400 }}
                onClick={() => {
                  setGridMenuVisible(false);
                  handleGridMenuAction(item.callback, gridMenuTarget);
                }}
              >
                <span className="text-gray-500 flex-shrink-0">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
              </button>
            ))}

            <div className="border-t border-gray-200 my-1" />

            {/* 편집 */}
            <div
              className="px-4 pt-2 pb-1 text-[11px] text-gray-400 select-none"
              style={{ fontWeight: 500 }}
            >
              편집
            </div>
            {[
              {
                label: "셀 내용 지우기 (Delete)",
                icon: <Eraser className="w-4 h-4" />,
                callback: "clearCell",
              },
              {
                label: "위로 병합",
                icon: <ArrowUp className="w-4 h-4" />,
                callback: "mergeUp",
              },
              {
                label: "왼쪽으로 병합",
                icon: <ArrowLeft className="w-4 h-4" />,
                callback: "mergeLeft",
              },
              {
                label: "삭제 옵션...",
                icon: <Trash2 className="w-4 h-4" />,
                callback: "showDeleteDialog",
              },
            ].map((item, idx) => (
              <button
                key={idx}
                className="w-full text-left text-[13px] flex items-center gap-3 hover:bg-gray-100 text-gray-700 transition-colors group"
                style={{ padding: "7px 16px", fontWeight: 400 }}
                onClick={() => {
                  setGridMenuVisible(false);
                  handleGridMenuAction(item.callback, gridMenuTarget);
                }}
              >
                <span className="text-gray-500 flex-shrink-0">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
      {/* 🔧 교차 검증 비교 모달 */}
      <CrossValidationModal
        isOpen={showCrossValidationModal}
        onClose={() => setShowCrossValidationModal(false)}
        primaryItems={allItemsProp}
        crossItems={crossValidation?.crossItems || []}
        crossValidation={crossValidation}
        pdfFile={pdfFile}
      />
      {/* 🧠 학습 데이터 조회 모달 */}
      <TrainingDataViewer
        isOpen={showTrainingViewer}
        onClose={() => setShowTrainingViewer(false)}
        apiBaseUrl={
          process.env.REACT_APP_API_URL ||
          (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
            ? `http://${window.location.hostname}:3002`
            : "")
        }
      />
      {/* 🧪 VLM 파인튜닝 관리 패널 */}
      {showVLMFineTuning && (
        <VLMFineTuningPanel onClose={() => setShowVLMFineTuning(false)} />
      )}

      {/* 🆕 AI HS 분류 evidence modal — AI dot 셀 클릭 또는 _aiSuggestion 있는 HSCode 클릭 시 진입 */}
      <InvoiceLineEvidence
        open={!!hsEvidenceModal}
        onClose={() => setHsEvidenceModal(null)}
        rowIndex={hsEvidenceModal?.rowIndex}
        description={hsEvidenceModal?.description}
        composition={hsEvidenceModal?.composition}
        onConfirm={handleHsEvidenceConfirm}
      />
    </div>
  );
}
